<template>
  <div class="flex-child">
    <!-- Initial Loading Spinner -->
    <div v-if="isInitialLoading" class="initial-loading-overlay">
      <div class="loading-content">
        <div class="loading-spinner"></div>
        <p class="loading-message">Loading Knowledge Inventory...</p>
      </div>
    </div>

    <div v-else class="body-container">
      <header>
        <h2>Knowledge Inventory</h2>
        <div class="header-actions">
            <button 
              @click="openConfigModal" 
              class="config-btn"
              title="AI Configuration">
              <i class="fas fa-cog"></i>
            </button>
            <div class="genesys-status">
              <span class="status-indicator" :class="genesysStatus.connected ? 'connected' : 'disconnected'">
                {{ genesysStatus.connected ? '🟢 Genesys Connected' : '🔴 Genesys Disconnected' }}
              </span>
            </div>
        </div>
      </header>

      <!-- AI Configuration Modal -->
      <dialog class="dialog-ai-config" :open="showConfigModal">
        <div class="dialog-backdrop" @click="closeConfigModal"></div>
        <div class="dialog-body">
          <button type="button" class="close" @click="closeConfigModal">
            <span>&times;</span>
          </button>
          <form @submit.prevent="saveAIConfiguration">
            <h4 class="dialog-header">AI Configuration</h4>

            <div class="dialog-grid-container">
              <label for="bedrock-api-key">Bedrock API Key</label>
              <div class="form-field">
                <BaseInput
                  id="bedrock-api-key"
                  v-model="aiConfig.bedrock_api_key"
                  placeholder=""
                  type="password"
                  required />
              </div>

              <label for="bedrock-model">Bedrock Model</label>
              <div class="form-field">
                <select
                  id="bedrock-model"
                  v-model="aiConfig.bedrock_model"
                  class="form-control">
                  <option value="openai.gpt-oss-120b-1:0">OpenAI GPT OSS 120B</option>
                  <option value="meta.llama3-1-405b-instruct-v1:0">Llama 3.1 405B Instruct</option>
                </select>
              </div>

              <label for="alert-email">Alert Email</label>
              <div class="form-field">
                <BaseInput
                  id="alert-email"
                  v-model="aiConfig.alert_email"
                  placeholder="email@example.com"
                  type="email" />
                <small class="form-help-text">Email address to receive alerts when monitored webpages are updated</small>
              </div>

              <label for="from-email">From Email</label>
              <div class="form-field">
                <BaseInput
                  id="from-email"
                  v-model="aiConfig.from_email"
                  placeholder="noreply@example.com"
                  type="email" />
                <small class="form-help-text">Email address to use as sender for Genesys agentless email alerts</small>
              </div>

              <label for="genesys-client-id">Genesys Client ID</label>
              <div class="form-field">
                <BaseInput
                  id="genesys-client-id"
                  v-model="aiConfig.genesys_client_id"
                  placeholder=""
                  type="text"
                  required />
              </div>

              <label for="genesys-client-secret">Genesys Client Secret</label>
              <div class="form-field">
                <BaseInput
                  id="genesys-client-secret"
                  v-model="aiConfig.genesys_client_secret"
                  placeholder=""
                  type="password"
                  required />
              </div>

              <label for="genesys-environment">Genesys Environment</label>
              <div class="form-field">
                <select
                  id="genesys-environment"
                  v-model="aiConfig.genesys_environment"
                  class="form-control">
                  <option value="usw2.pure.cloud">usw2.pure.cloud</option>
                  <option value="mypurecloud.com">mypurecloud.com</option>
                  <option value="usw2.pure.cloud">usw2.pure.cloud</option>
                  <option value="use2.us-gov-pure.cloud">use2.us-gov-pure.cloud</option>
                  <option value="cac1.pure.cloud">cac1.pure.cloud</option>
                  <option value="mypurecloud.ie">mypurecloud.ie</option>
                  <option value="euw2.pure.cloud">euw2.pure.cloud</option>
                  <option value="mypurecloud.de">mypurecloud.de</option>
                  <option value="aps1.pure.cloud">aps1.pure.cloud</option>
                  <option value="mypurecloud.jp">mypurecloud.jp</option>
                  <option value="apne2.pure.cloud">apne2.pure.cloud</option>
                  <option value="mypurecloud.com.au">mypurecloud.com.au</option>
                  <option value="sae1.pure.cloud">sae1.pure.cloud</option>
                </select>
              </div>

              <label for="upload-directory">File Upload Directory</label>
              <div class="form-field">
                <BaseInput
                  id="upload-directory"
                  v-model="aiConfig.upload_directory"
                  placeholder="/uploaded-files"
                  type="text"
                  required />
                <small class="form-help-text">Directory path where uploaded files will be stored on scraperGCCX backend</small>
              </div>
            </div>

            <div class="config-status" v-if="configStatus">
              <div :class="['status-message', configStatus.type]">
                {{ configStatus.message }}
              </div>
            </div>

            <div class="button-group" style="margin-top: 20px">
              <ButtonSubmit 
                label="Save Configuration"
                :disabled="savingConfig" />
                <ButtonSubmit 
                label="Test Connection"
                :disabled="testingConfig"
                @click="testConfiguration" />
            
              <ButtonCancel
                label="Cancel"
                type="button"
                @click="closeConfigModal" />
            </div>
          </form>
        </div>
      </dialog>

      <div id="subheader" v-if="processedData" class="member-info subheader-aligned">
        <div class="info-item">
          <span class="info-label">Source Type:</span>
          <span class="info-value">{{ getSourceTypeLabel() }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Output Format:</span>
          <span class="info-value">Genesys KB JSON</span>
        </div>
        <div class="info-item" v-if="processedData?.json?.documents">
          <span class="info-label">Total Articles:</span>
          <span class="info-value">{{ processedData.json.documents.length }}</span>
        </div>
        <div class="info-item" v-if="getContentLength()">
          <span class="info-label">Content Length:</span>
          <span class="info-value">{{ getContentLength() }} chars</span>
        </div>
        <div class="info-item" v-if="approvedArticles.length > 0">
          <span class="info-label">Approved:</span>
          <span class="info-value approved-count">{{ approvedArticles.length }}</span>
        </div>
        <div class="info-item" v-if="selectedKnowledgeBase">
          <span class="info-label">Target KB:</span>
          <span class="info-value">{{ selectedKnowledgeBase.name }}</span>
        </div>
        <div class="info-item" v-if="processedData?.import_result">
          <span class="info-label">Status:</span>
          <span class="info-value import-success">✅ Imported</span>
        </div>
      </div>

      <!-- Always Visible Progress Log Sidebar -->
      <!-- Progress Log Sidebar -->
      <div class="progress-log-sidebar" v-if="progressLog.length > 0">
        <div class="log-sidebar-header">
          <h5>
            <i class="fas fa-list-alt"></i>
            Progress Log
          </h5>
          <button @click="clearProgressLog" class="clear-log-btn" title="Clear Log">
            <i class="fas fa-trash"></i>
          </button>
        </div>
        <div class="log-container sidebar-log">
          <div 
            v-for="(log, index) in progressLog.slice(-10)" 
            :key="index" 
            class="log-entry"
            :class="getLogEntryClass(log)">
            <span class="log-timestamp">{{ formatTimestamp(log.timestamp) }}</span>
            <span class="log-message">{{ log.message }}</span>
          </div>
        </div>
      </div>

      <!-- Articles are now viewed via inline "Articles" button -->

      <div id="centerdiv" :class="{ 'article-editor-mode': showArticleEditor, 'with-log-sidebar': progressLog.length > 0 || processedData?.json?.documents?.length > 0 }">
        <!-- Left Column - Configuration OR Article Basic Info Editor -->
        <div class="containerdiv" 
             :class="{ 'editor-left-column': showArticleEditor }"
             id="containerdiv1">
          
          <!-- Original configuration content when NOT editing -->
          <div v-if="!showArticleEditor">
            <!-- Knowledge Inventory Section -->
            <div class="source-section knowledge-inventory-section">
              
              
              <!-- Add Sites Section -->
              <div class="add-sites-section">
                <div class="section-header">
                  <h3>
                    <i class="fas fa-plus-circle"></i>
                    Add New Source
                  </h3>
                  <p class="section-description">Add websites to monitor for content changes and document generation</p>
                </div>

                <!-- Source Tabs -->
                <div class="source-tabs">
                  <button 
                    @click="activeSource = 'url'"
                    :class="['tab-button', { active: activeSource === 'url' }]">
                    <i class="fas fa-link"></i>
                    Website URL
                  </button>
                  <button 
                    @click="activeSource = 'file'"
                    :class="['tab-button', { active: activeSource === 'file' }]">
                    <i class="fas fa-file-upload"></i>
                    File
                  </button>
                </div>

                <!-- URL Input Tab -->
                <div v-if="activeSource === 'url'" class="input-tab-content">
                  <div class="input-header">
                    <h4>Enter Website URL</h4>
                    <p>Add a website URL to automatically crawl and monitor</p>
                  </div>
                  
                  <div class="url-input-container">
                    <div class="input-container">
                      <BaseInput
                        type="url"
                        v-model="knowledgeUrl"
                        placeholder="https://example.com"
                        class="url-input-field" />
                    </div>
                    <button 
                      @click="addToMonitored" 
                      class="add-site-btn"
                      :disabled="!knowledgeUrl || isScanning">
                      <i :class="isScanning ? 'fas fa-spinner fa-spin' : 'fas fa-plus'"></i>
                      {{ isScanning ? 'Adding Site...' : 'Add Site' }}
                    </button>
                  </div>
                </div>

                <!-- File Upload Tab -->
                <div v-if="activeSource === 'file'" class="input-tab-content">
                  <div class="input-header">
                    <h4>Upload Files</h4>
                    <p>Upload documents to monitor for changes</p>
                  </div>
                  
                  <div class="file-upload-container">
                    <div class="file-drop-zone" @drop="handleFileDrop" @dragover.prevent @dragenter.prevent>
                      <input 
                        type="file" 
                        ref="fileInput"
                        @change="handleFileSelect"
                        accept=".pdf,.doc,.docx,.txt,.md,.html,.json"
                        multiple
                        class="file-input-hidden">
                      <div class="file-drop-content">
                        <i class="fas fa-cloud-upload-alt"></i>
                        <p class="drop-text">Drop files here or <button @click="$refs.fileInput.click()" class="browse-link">browse files</button></p>
                        <p class="file-formats">Supports: PDF, DOC, DOCX, TXT, MD, HTML, JSON</p>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Crawl Options -->
                <div v-if="activeSource === 'url'" class="crawl-options-section">
                  <div class="options-header">
                    <h4>
                      <i class="fas fa-cog"></i>
                      Crawl Configuration
                    </h4>
                  </div>
                  
                  <!-- 2024-11-27: Added Depth field for deep crawling support -->
                  <div class="options-grid">
                    <div class="option-group">
                      <label class="option-label">Max Pages</label>
                      <BaseInput
                        type="number"
                        v-model="crawlOptions.maxPages"
                        placeholder="1000"
                        class="option-input" />
                    </div>

                    <div class="option-group">
                      <label class="option-label">Depth</label>
                      <BaseInput
                        type="number"
                        v-model="crawlOptions.crawl_depth"
                        placeholder="1"
                        min="0"
                        max="10"
                        class="option-input" />
                      <small class="form-help-text">Crawl depth (1 = shallow, 2+ = deep)</small>
                    </div>

                    <div class="option-group">
                      <label class="option-label">Concurrency</label>
                      <BaseInput
                        type="number"
                        v-model="crawlOptions.maxConcurrency"
                        placeholder="5"
                        class="option-input" />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Monitored Sites Summary -->
              <div v-if="monitoredSites.length > 0" class="inventory-summary">
                <div class="summary-stats">
                  <div class="stat-item">
                    <span class="stat-label">Sites</span>
                    <span class="stat-value">{{ monitoredSites.length }}</span>
                  </div>
                  <div class="stat-item">
                    <span class="stat-label">Pages</span>
                    <span class="stat-value">{{ totalPagesCount }}</span>
                  </div>
                  <div class="stat-item">
                    <span class="stat-label">Monitored</span>
                    <span class="stat-value">{{ totalMonitoredCount }}</span>
                  </div>
                  <div class="stat-item">
                    <span class="stat-label">Changed</span>
                    <span class="stat-value changed">{{ totalChangedCount }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Article basic info editor when editing -->
          <div v-if="showArticleEditor">
            <div class="article-editor">
              <div class="editor-header">
                <h4>Edit Article</h4>
                <BaseButton 
                  @click="closeArticleEditor"
                  class="btn-secondary btn-rounded">
                  Back to Articles
                </BaseButton>
              </div>

              <!-- Article Title -->
              <div class="edit-field">
                <label>Article Title</label>
                <BaseInput
                  v-model="editingArticle.draft.title"
                  class="form-control"
                  placeholder="Enter article title..."
                  style="font-size: 16px; margin-top: 3px;" />
              </div>

              <!-- Category Selection -->
              <div class="edit-field">
                <label>Category</label>
                <select 
                  v-model="editingArticle.draft.category.name"
                  class="form-control"
                  style="font-size: 16px; margin-top: 3px;">
                  <option value="Account">Account</option>
                  <option value="Digital Banking">Digital Banking</option>
                  <option value="Loan">Loan</option>
                  <option value="General Information">General Information</option>
                  <option value="Cards">Cards</option>
                  <option value="Branches">Branches</option>
                  <option value="Support">Support</option>
                  <option value="Services">Services</option>
                </select>
              </div>

              <!-- Status - Draft/Published -->
              <div class="edit-field">
                <label>Publication Status</label>
                <div class="status-options">
                  <label class="radio-option">
                    <input 
                      type="radio" 
                      value="Published" 
                      v-model="editingArticleStatus"
                      @change="updateArticleStatus">
                    Published
                  </label>
                  <label class="radio-option">
                    <input 
                      type="radio" 
                      value="Draft" 
                      v-model="editingArticleStatus"
                      @change="updateArticleStatus">
                    Draft
                  </label>
                </div>
              </div>

              <!-- Visibility -->
              <div class="edit-field">
                <div class="option-group">
                  <input 
                    type="checkbox" 
                    id="article-visible" 
                    v-model="editingArticle.draft.visible" />
                  <label for="article-visible">Article is visible</label>
                </div>
              </div>

              <!-- Labels -->
              <div class="edit-field">
                <label>Labels (comma-separated)</label>
                <BaseInput
                  v-model="labelsText"
                  class="form-control"
                  placeholder="Payment, Account, Help..."
                  style="font-size: 16px; margin-top: 3px;"
                  @input="updateLabels" />
              </div>

              <!-- Current Labels Display -->
              <div class="edit-field">
                <label>Current Labels</label>
                <div class="current-labels-display">
                  <span 
                    v-for="label in editingArticle.draft.labels" 
                    :key="label.name"
                    class="current-label-tag">
                    {{ label.name }}
                  </span>
                  <span v-if="!editingArticle.draft.labels?.length" class="no-labels">
                    No labels assigned
                  </span>
                </div>
              </div>

              <!-- Alternative Questions -->
              <div class="edit-field">
                <label>Alternative Questions</label>
                <div class="alternatives-list">
                  <div 
                    v-for="(alt, index) in editingArticle.draft.alternatives" 
                    :key="index"
                    class="alternative-item">
                    <BaseInput
                      v-model="alt.phrase"
                      class="form-control"
                      placeholder="Alternative question..."
                      style="flex: 1; font-size: 14px;" />
                    <BaseButton 
                      @click="removeAlternative(index)"
                      class="btn-danger-small btn-rounded">
                      Remove
                    </BaseButton>
                  </div>
                  <BaseButton 
                    @click="addAlternative"
                    class="btn-secondary btn-rounded">
                    Add Alternative
                  </BaseButton>
                </div>
              </div>

              <!-- Save/Cancel Buttons -->
              <div class="editor-actions">
                <ButtonSubmit 
                  label="Save Changes"
                  @click="saveArticleChanges" />
                <BaseButton 
                  @click="closeArticleEditor"
                  class="btn-secondary btn-rounded">
                  Cancel
                </BaseButton>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column - Article Content Editor (when editing) -->
        <div class="containerdiv member-info editor-content-column" 
             id="containerdiv2" 
             v-if="showArticleEditor">
          <div class="member-info-content">
            <div class="content-editor">
              <h4>Article Content</h4>
              
              <!-- Content Blocks Editor -->
              <div class="wysiwyg-editor-container">
                    <QuillEditor
                      ref="quillEditor"
                      v-model:content="wysiwygContent"
                      :options="quillOptions"
                      content-type="html"
                      class="wysiwyg-editor"
                      @ready="onEditorReady"
                    />
                  </div>
                
            </div>
          </div>
        </div>

        <!-- Center Column - Progress/Status/Loading -->
        <div class="containerdiv member-info processing-section" id="containerdiv2" v-if="(isProcessing || processedData) && !showArticleEditor">
          <div class="member-info-content">
            <!-- Live Progress Display -->
            <div v-if="isProcessing" class="progress-container">
              <h4>Processing Status</h4>
              
              <!-- Progress Bar -->
              <div v-if="progressInfo.total_steps" class="progress-bar-container">
                <div class="progress-bar">
                  <div 
                    class="progress-fill" 
                    :style="{ width: progressPercentage + '%' }">
                  </div>
                </div>
                <div class="progress-text">
                  {{ progressInfo.current_step || 0 }} / {{ progressInfo.total_steps }} steps
                </div>
              </div>

              <!-- Current Status -->
              <div class="current-status">
                <div class="status-message">{{ currentStatus }}</div>
              </div>

              <!-- Progress Log -->
              <div class="progress-log">
                <h5>Progress Log:</h5>
                <div class="log-container">
                  <div 
                    v-for="(log, index) in progressLog" 
                    :key="index" 
                    class="log-entry"
                    :class="getLogEntryClass(log)">
                    <span class="log-timestamp">{{ formatTimestamp(log.timestamp) }}</span>
                    <span class="log-message">{{ log.message }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Results Summary (when complete) -->
            <div v-else-if="processedData" class="results-summary">
              <h4>Processing Complete</h4>
              
              <!-- Status Messages -->
              <div v-if="successMessage" class="status-message success">
                {{ successMessage }}
              </div>
              <div v-if="errorMessage" class="status-message error">
                {{ errorMessage }}
              </div>

              <!-- import Status -->
              <div v-if="processedData.import_result" class="import-status-section">
                <div class="import-success-message">
                   Successfully imported {{ importedApprovedCount || processedData.json?.documents?.length || 0 }} articles to Knowledge Base!
                <br>
                 
                </div>
              </div>

              <div v-if="processedData.import_error" class="import-error-section">
                <div class="import-error-message">
                  Import failed: {{ processedData.import_error }}
                </div>
              </div>


              <!-- View Approved Articles History -->
              <div v-if="urlInput && !processing" class="approved-history-section">
                <div class="history-buttons">
                  <BaseButton 
                    @click="loadApprovedHistory"
                    :loading="loadingHistory"
                    variant="outline"
                    size="small">
                    <i class="fas fa-history"></i> View Approved Articles History
                  </BaseButton>
                  
                  <BaseButton 
                    v-if="processedData?.json?.documents"
                    @click="compareWithExisting"
                    :loading="comparingArticles"
                    variant="outline"
                    size="small">
                    <i class="fas fa-balance-scale"></i> Compare with Existing
                  </BaseButton>
                </div>
                
                <div v-if="approvedHistory.length > 0" class="history-results">
                  <h6>Previously Approved Articles for this URL ({{ approvedHistory.length }})</h6>
                  <div class="history-list">
                    <div v-for="article in approvedHistory" :key="article.id" class="history-item">
                      <div class="article-info">
                        <strong>{{ article.title }}</strong>
                        <span class="status-badge" :class="article.status">{{ article.status }}</span>
                      </div>
                      <div class="article-meta">
                        <small>KB: {{ article.genesys_kb_id }} • Created: {{ formatDate(article.created_at) }}</small>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Comparison Results -->
                <div v-if="comparisonResults" class="comparison-results">
                  <h6>Article Comparison Results</h6>
                  <div class="comparison-summary">
                    <div class="summary-stats">
                      <span class="stat new">{{ comparisonResults.summary.new }} New</span>
                      <span class="stat updated">{{ comparisonResults.summary.updated }} Updated</span>
                      <span class="stat unchanged">{{ comparisonResults.summary.unchanged }} Unchanged</span>
                    </div>
                  </div>
                  
                  <div class="comparison-list">
                    <div v-for="(result, index) in comparisonResults.comparison_results" 
                         :key="index" 
                         class="comparison-item"
                         :class="result.status">
                      <div class="comparison-info">
                        <div class="article-title">
                          <strong>{{ result.title }}</strong>
                          <span class="comparison-status" :class="result.status">
                            <i v-if="result.status === 'new'" class="fas fa-plus-circle"></i>
                            <i v-else-if="result.status === 'updated'" class="fas fa-edit"></i>
                            <i v-else class="fas fa-check-circle"></i>
                            {{ result.status.toUpperCase() }}
                          </span>
                        </div>
                        <div v-if="result.existing_article" class="existing-info">
                          <small>
                            Previously created: {{ formatDate(result.existing_article.created_at) }}
                            <span v-if="result.status === 'updated'"> • Content changed</span>
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div class="comparison-actions">
                    <BaseButton 
                      @click="approveNewAndUpdatedArticles"
                      variant="primary"
                      size="small">
                      Auto-Approve New & Updated Articles
                    </BaseButton>
                    <BaseButton 
                      @click="clearComparison"
                      variant="outline"
                      size="small">
                      Clear Comparison
                    </BaseButton>
                  </div>
                </div>
              </div>

              <!-- Knowledge Base Selection for import - MOVED TO MANAGEMENT PAGE -->
              <div v-if="false" class="kb-import-section">
                <h5>Import Approved Articles to Knowledge Base</h5>
                
                <div class="approved-articles-info">
                  <span class="approved-count-display">
                    {{ approvedArticles.length }} article{{ approvedArticles.length !== 1 ? 's' : '' }} approved for import
                  </span>
                </div>
                
                <div class="kb-selection">
                  <label for="import-kb-select">Select Knowledge Base:</label>
                  <div class="kb-dropdown-container" style="display: flex; align-items: center; gap: 8px; margin-top: 8px;">
                    <select 
                      id="import-kb-select"
                      v-model="selectedKnowledgeBase"
                      class="form-control kb-dropdown"
                      style="font-size: 16px; flex: 1;"
                      :disabled="loadingKnowledgeBases">
                      <option :value="null">{{ loadingKnowledgeBases ? 'Loading...' : 'Select Knowledge Base' }}</option>
                      <option 
                        v-for="kb in filteredKnowledgeBases" 
                        :key="kb.id" 
                        :value="kb">
                        {{ kb.name }} ({{ kb.articleCount }} articles)
                      </option>
                    </select>
                    <button 
                      @click="loadKnowledgeBases"
                      :disabled="loadingKnowledgeBases"
                      class="kb-refresh-btn"
                      title="Refresh Knowledge Base List">
                      {{ loadingKnowledgeBases ? '⏳' : '🔄' }}
                    </button>
                  </div>
                </div>

                <div class="kb-actions" style="margin-top: 12px;">
                  
                  <BaseButton 
                    v-if="selectedKnowledgeBase" 
                    @click="importApprovedArticles" 
                    :disabled="importingManually"
                    class="btn-import btn-rounded">
                    {{ importingManually ? 'Importing...' : `Import ${approvedArticles.length} Approved Articles` }}
                  </BaseButton>
                </div>
              </div>

              <!-- No Approved Articles Warning -->
              <div v-else-if="processedData.json?.documents?.length > 0 && approvedArticles.length === 0 && !processedData.import_result" class="no-approved-warning">
                <div class="warning-message">
                  ⚠️ No articles have been approved for import. Please review and approve articles in the table below.
                </div>
              </div>

              <!-- Original import Section (fallback for when no approval system) - MOVED TO MANAGEMENT PAGE -->
              <div v-if="false" class="kb-import-section">
                <h5>Import to Knowledge Base</h5>
                
                <div class="kb-selection">
                  <label for="import-kb-select">Select Knowledge Base:</label>
                  <div class="kb-dropdown-container" style="display: flex; align-items: center; gap: 8px; margin-top: 8px;">
                    <select 
                      id="import-kb-select"
                      v-model="selectedKnowledgeBase"
                      class="form-control kb-dropdown"
                      style="font-size: 16px; flex: 1;"
                      :disabled="loadingKnowledgeBases">
                      <option :value="null">{{ loadingKnowledgeBases ? 'Loading...' : 'Select Knowledge Base' }}</option>
                      <option 
                        v-for="kb in filteredKnowledgeBases" 
                        :key="kb.id" 
                        :value="kb">
                        {{ kb.name }} ({{ kb.articleCount }} articles)
                      </option>
                    </select>
                    <button 
                      @click="loadKnowledgeBases"
                      :disabled="loadingKnowledgeBases"
                      class="kb-refresh-btn"
                      title="Refresh Knowledge Base List">
                      {{ loadingKnowledgeBases ? '⏳' : '🔄' }}
                    </button>
                  </div>
                </div>

                <div class="kb-actions" style="margin-top: 12px;">
                  
                  <BaseButton 
                    v-if="selectedKnowledgeBase" 
                    @click="importToKnowledgeBase" 
                    :disabled="importingManually"
                    class="btn-import btn-rounded">
                    {{ importingManually ? 'importing...' : `import to ${selectedKnowledgeBase.name}` }}
                  </BaseButton>
                </div>
              </div>

              <!-- Download JSON -->
              <!-- <div class="download-section" style="margin-top: 16px;">
                <BaseButton @click="downloadJson" class="btn-submit btn-rounded">
                  Download All Articles JSON
                </BaseButton>
                <BaseButton 
                  v-if="approvedArticles.length > 0"
                  @click="downloadApprovedJson" 
                  class="btn-approved-download btn-rounded"
                  style="margin-left: 8px;">
                  Download Approved Only ({{ approvedArticles.length }})
                </BaseButton>
              </div> -->
            </div>
          </div>
        </div>

        <!-- Center Column - Getting Started (initial state) -->
        <div class="containerdiv member-info" id="containerdiv2" v-if="!isProcessing && !processedData && !showArticleEditor">
          <div class="member-info-content">
            <h4>Get Started</h4>
            <div class="getting-started">
              <div class="step-item">
                <span class="step-number">1</span>
                <div class="step-content">
                  <div class="step-title">Choose Content Source</div>
                  <div class="step-description">Select URL, import files, or paste text directly</div>
                </div>
              </div>
              <div class="step-item">
                <span class="step-number">2</span>
                <div class="step-content">
                  <div class="step-title">Configure Options</div>
                  <div class="step-description">Set processing preferences and output format</div>
                </div>
              </div>
              <div class="step-item">
                <span class="step-number">3</span>
                <div class="step-content">
                  <div class="step-title">Process Content</div>
                  <div class="step-description">Click "Process Content" to see real-time progress</div>
                </div>
              </div>
              <div class="step-item">
                <span class="step-number">4</span>
                <div class="step-content">
                  <div class="step-title">Review & Approve</div>
                  <div class="step-description">Review generated articles and approve for import</div>
                </div>
              </div>
              <div class="step-item">
                <span class="step-number">5</span>
                <div class="step-content">
                  <div class="step-title">Import Approved</div>
                  <div class="step-description">Import only approved articles to Knowledge Base</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column - Analysis and import Results -->
        <div class="containerdiv member-info analysis-section-container" id="containerdiv3" v-if="processedData && !showArticleEditor">
          <div class="member-info-content">
            <!-- Genesys import Info -->
            <div v-if="processedData.import_result || processedData.import_error" class="import-info-section" style="margin-bottom: 20px;">
              <h4>import Results</h4>
              <div v-if="processedData.import_result" class="import-details success">
                <div class="import-status">Successfully Imported to Knowledge Base</div>
                <div class="import-detail-item">
                  <strong>Knowledge Base:</strong> {{ selectedKnowledgeBase?.name }}
                </div>
                <div class="import-detail-item">
                  <strong>Articles imported:</strong> {{ importedApprovedCount || processedData.json?.documents?.length || 0 }}
                </div>
              </div>
              <div v-if="processedData.import_error" class="import-details error">
                <div class="import-status">❌ Import failed</div>
                <div class="import-detail-item">
                  <strong>Error:</strong> {{ processedData.import_error }}
                </div>
              </div>
            </div>

            <!-- AI Analysis -->
            <div v-if="processedData.analysis" class="analysis-section" style="margin-bottom: 20px;">
              <h4>Content Analysis</h4>
              <div class="analysis-content">{{ processedData.analysis }}</div>
            </div>

            <!-- URL List -->
            <div v-if="crawledUrls.length > 0" class="urls-section">
              <h4>Processed URLs ({{ crawledUrls.length }})</h4>
              <div class="urls-list-container">
                <div class="urls-list">
                  <div v-for="(url, index) in crawledUrls" :key="url" class="url-item">
                    <span class="url-number">{{ index + 1 }}</span>
                    <a :href="url" target="_blank" class="url-link">{{ formatUrl(url) }}</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column - Knowledge Base Info (when no data) -->
        <div class="containerdiv member-info" id="containerdiv3" v-if="!processedData && !showArticleEditor">
          <div class="member-info-content">
            <h4>Genesys Integration</h4>
            <div class="feature-list">
              <div class="feature-item">
                <div class="feature-text">Article approval workflow</div>
              </div>
              <div class="feature-item">
                <div class="feature-text">Import only approved articles</div>
              </div>
              <div class="feature-item">
                <div class="feature-text">Bulk approval/rejection actions</div>
              </div>
              <div class="feature-item">
                <div class="feature-text">Direct import to Genesys Knowledge Bases</div>
              </div>
              <div class="feature-item">
                <div class="feature-text">Real-time import job tracking</div>
              </div>
              <div class="feature-item">
                <div class="feature-text">Article editing before import</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Articles Management Section with Approval (Full Width at Bottom) - MOVED TO MANAGEMENT PAGE -->
      <div v-if="false" class="articles-full-section">
        <div class="articles-section-wrapper">
          <div class="articles-section">
            <div class="articles-header">
              <h4>Generated Articles ({{ processedData.json.documents.length }})</h4>
              <div class="articles-header-actions">
                <div class="approval-summary-mini">
                  <span class="mini-stat approved">{{ approvedArticles.length }}</span>
                  <span class="mini-stat pending">{{ pendingArticles.length }}</span>
                  <span class="mini-stat rejected">{{ rejectedArticles.length }}</span>
                </div>
              </div>
            </div>
            
            
             <!-- Approval Actions Bar -->
              <div class="approval-actions-bar">
                <div class="selection-info">
                  <span v-if="selectedArticles.length > 0">
                    {{ selectedArticles.length }} of {{ processedData.json.documents.length }} articles selected
                  </span>
                  <span v-else>
                    Select articles to approve or reject
                  </span>
                </div>
                
                <div class="approval-actions">
                  <BaseButton 
                    @click="approveSelectedArticles"
                    :disabled="selectedArticles.length === 0"
                    class="btn-approve btn-rounded">
                    Approve Selected ({{ selectedArticles.length }})
                  </BaseButton>
                  <BaseButton 
                    @click="rejectSelectedArticles"
                    :disabled="selectedArticles.length === 0"
                    class="btn-reject btn-rounded">
                    Reject Selected
                  </BaseButton>
                  <BaseButton 
                    @click="resetSelectedApprovals"
                    :disabled="selectedArticles.length === 0"
                    class="btn-reset btn-rounded">
                    Reset Selected
                  </BaseButton>
                </div>
              </div>
              <!-- Articles Table -->
            <div class="articles-table-container">
              <table class="articles-table">
                <thead>
                  <tr>
                    <th class="checkbox-column">
                            <div class="checkbox-header-wrapper">
                              <input 
                                type="checkbox" 
                                :checked="allArticlesSelected"
                                @change="toggleAllArticles"
                                class="select-all-checkbox"
                                title="Select/Deselect All">
                            </div>
                          </th>
                    <th class="approval-column">Approval</th>
                    <th class="title-column">Title</th>
                    <th class="category-column">Category</th>
                    <th class="status-column">Status</th>
                    <th class="visible-column">Visible</th>
                    <th class="labels-column">Labels</th>
                    <th class="actions-column">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr 
                    v-for="(article, index) in processedData.json.documents" 
                    :key="index"
                    class="article-row"
                    :class="getArticleRowClass(index)">
                    
                  <td class="checkbox-cell">
                    <input 
                      type="checkbox" 
                      :checked="selectedArticles.includes(index)"
                      @change="toggleArticleSelection(index)"
                      class="article-checkbox">
                  </td>

                    <!-- Approval Column -->
                    <td class="approval-status-cell">
                      <span :class="['status-badge', 'approval-status-badge', getApprovalStatus(index)]">
                        {{ getApprovalStatusText(index) }}
                      </span>
                    </td>
                    
                    <td class="title-cell">
                      <div class="article-title">{{ getCurrentArticleData(article).title }}</div>
                      <div class="article-preview">{{ getArticlePreview(article) }}</div>
                    </td>
                    <td class="category-cell">
                      <span class="category-badge rectangle-badge">{{ getCurrentArticleData(article).category.name }}</span>
                    </td>
                    <td class="status-cell">
                      <span class="status-badge rectangle-badge" :class="getArticleStatusClass(article)">
                        {{ getArticleStatus(article) }}
                      </span>
                    </td>
                    <td class="visible-cell">
                      <div class="visibility-toggle">
                        <input 
                          type="checkbox" 
                          :checked="getCurrentArticleData(article).visible"
                          @change="toggleArticleVisibility(index, $event.target.checked)"
                          class="visibility-checkbox">
                        <span class="visibility-label">{{ getCurrentArticleData(article).visible ? 'Visible' : 'Hidden' }}</span>
                      </div>
                    </td>
                    <td class="labels-cell">
                      <div class="labels-list">
                        <span 
                          v-for="label in getCurrentArticleData(article).labels" 
                          :key="label.name"
                          class="label-tag rectangle-badge">
                          {{ label.name }}
                        </span>
                      </div>
                    </td>
                    <td class="actions-cell">
                        <div class="action-buttons">
                          <button 
                            @click="editArticle(index)"
                            class="action-icon-btn"
                            :class="{ 'disabled': getApprovalStatus(index) === 'approved' }"
                            :disabled="getApprovalStatus(index) === 'approved'"
                            :title="getApprovalStatus(index) === 'approved' ? 'Cannot edit approved articles' : 'Edit Article'">
                            <i class="fas fa-edit"></i>
                          </button>
                          <button 
                            @click="deleteArticle(index)"
                            class="action-icon-btn delete-btn"
                            title="Delete Article">
                            <i class="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                   
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Monitored Inventory Section -->
      <div v-if="monitoredSites.length > 0 || isLoadingSites" class="monitored-inventory-section">
        <div class="section-header">
          <div class="section-title">
            <h3>Monitored Inventory</h3>
            <div class="section-stats">
              <div class="stat-chip">
                <span class="stat-label">Sources</span>
                <span class="stat-value">{{ isLoadingSites ? '...' : monitoredSites.length }}</span>
              </div>
              <div class="stat-chip">
                <span class="stat-label">Files</span>
                <span class="stat-value">{{ isLoadingSites ? '...' : totalPagesCount }}</span>
              </div>
              <div class="stat-chip">
                <span class="stat-label">Monitored</span>
                <span class="stat-value">{{ isLoadingSites ? '...' : totalMonitoredCount }}</span>
              </div>
              <div class="stat-chip changed">
                <span class="stat-label">Changed</span>
                <span class="stat-value">{{ isLoadingSites ? '...' : totalChangedCount }}</span>
              </div>
            </div>
          </div>
          
        </div>

        <div v-if="isLoadingSites" class="loading-state">
          <div class="spinner-container">
            <div class="spinner"></div>
            <p>Loading monitored files...</p>
          </div>
        </div>

        <div v-else-if="monitoredSites.length === 0" class="empty-state">
          <div class="empty-icon">
            <i class="fas fa-folder-open"></i>
          </div>
          <h4>No monitored files yet</h4>
          <p>Add a file above to get started with monitoring!</p>
        </div>

        <div v-else class="inventory-content">
          <!-- Statistics Overview -->
          <div class="overview-stats">
            <div class="stat-card">
              <div class="stat-icon">
                <i class="fas fa-globe"></i>
              </div>
              <div class="stat-info">
                <span class="stat-number">{{ monitoredSites.length }}</span>
                <span class="stat-label">Total Sources</span>
              </div>
            </div>
            <div class="stat-card">
              <div class="stat-icon">
                <i class="fas fa-file-alt"></i>
              </div>
              <div class="stat-info">
                <span class="stat-number">{{ totalPagesCount }}</span>
                <span class="stat-label">Total Files</span>
              </div>
            </div>
            <div class="stat-card">
              <div class="stat-icon">
                <i class="fas fa-eye"></i>
              </div>
              <div class="stat-info">
                <span class="stat-number">{{ totalMonitoredCount }}</span>
                <span class="stat-label">Monitored Files</span>
              </div>
            </div>
            <div class="stat-card changed">
              <div class="stat-icon">
                <i class="fas fa-fire"></i>
              </div>
              <div class="stat-info">
                <span class="stat-number">{{ totalChangedCount }}</span>
                <span class="stat-label">Changed Files</span>
              </div>
            </div>
          </div>

          <!-- Sites Table -->
          <!-- Monitored Sites Table -->
          <div class="monitored-sites-table-container">
            <table class="monitored-sites-data-table">
              <thead>
                <tr>
                  <th class="th-number">#</th>
                  <th class="th-site-url">Source</th>
                  <th class="th-status">Status</th>
                  <th class="th-pages">Files</th>
                  <th class="th-monitored">Monitored</th>
                  <th class="th-changed">Changed</th>
                  <th class="th-crawled">Last Crawled</th>
                  <th class="th-scan-interval">Auto Scan</th>
                  <th class="th-knowledge-base">Default Knowledge Base</th>
                  <th class="th-site-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(site, index) in monitoredSites" :key="site.id" class="site-data-row">
                  <td class="td-number">{{ index + 1 }}</td>
                  <td class="td-site-url">
                    <template v-if="site.type === 'directory'">
                      <span :title="site.directory_path || site.root_url" class="site-link directory-path">
                        <i class="fas fa-folder"></i>
                        {{ site.directory_path || site.root_url }}
                      </span>
                    </template>
                    <template v-else>
                      <a :href="site.root_url" target="_blank" :title="site.root_url" class="site-link">
                        <i class="fas fa-globe"></i>
                        {{ site.root_url }}
                      </a>
                    </template>
                  </td>
                  <td class="td-status">
                    <span :class="['status-badge', getSiteReachabilityStatus(site).isReachable ? 'status-active' : 'status-inactive']">
                      <i :class="getSiteReachabilityStatus(site).isReachable ? 'fas fa-circle' : 'fas fa-circle'"></i>
                      {{ getSiteReachabilityStatus(site).status }}
                    </span>
                  </td>
                  <td class="td-pages">
                    <span v-if="site.loading" class="loading-indicator">
                      <i class="fas fa-spinner fa-spin"></i>
                    </span>
                    <span v-else-if="site.error" class="error-indicator" :title="site.error">
                      <i class="fas fa-exclamation-triangle"></i>
                    </span>
                    <span v-else class="count-badge">{{ site.totalPages || 0 }}</span>
                  </td>
                  <td class="td-monitored">
                    <span v-if="site.loading" class="loading-indicator">
                      <i class="fas fa-spinner fa-spin"></i>
                    </span>
                    <span v-else-if="site.error" class="error-indicator">-</span>
                    <span v-else class="count-badge monitored">{{ getMonitoredCount(site) }}</span>
                  </td>
                  <td class="td-changed">
                    <span v-if="site.loading" class="loading-indicator">
                      <i class="fas fa-spinner fa-spin"></i>
                    </span>
                    <span v-else-if="site.error" class="error-indicator">-</span>
                    <span v-else :class="['count-badge', getChangedCount(site) > 0 ? 'changed' : '']">{{ getChangedCount(site) }}</span>
                  </td>
                  <td class="td-crawled">
                    <span class="time-badge">{{ site.last_crawled_at ? formatRelativeTime(site.last_crawled_at) : 'Never' }}</span>
                  </td>
                  <td class="td-scan-interval">
                    <select
                      v-model="site.scan_interval_seconds"
                      @change="updateScanInterval(site)"
                      class="scan-interval-select"
                      :disabled="site.loading">
                      <option value="0">Disabled</option>
                      <option value="60">Every 1 minute</option>
                      <option value="300">Every 5 minutes</option>
                      <option value="1800">Every 30 minutes</option>
                      <option value="86400">Every day</option>
                      <option value="604800">Every week</option>
                      <option value="2592000">Every month</option>
                    </select>
                  </td>
                  <td class="td-knowledge-base">
                    <select
                      v-model="site.default_knowledge_base_id"
                      @change="updateDefaultKnowledgeBase(site)"
                      class="knowledge-base-select"
                      :disabled="site.loading || loadingKnowledgeBases">
                      <option :value="null">None</option>
                      <option v-for="kb in knowledgeBases" :key="kb.id" :value="kb.id">
                        {{ kb.name }}
                      </option>
                    </select>
                  </td>
                  <td class="td-site-actions">
                    <div class="site-actions-row">
                      <button 
                        @click="viewSiteDocuments(site)"
                        class="site-action-btn documents-btn"
                        :title="`View all ${site.type === 'directory' ? 'files' : 'files'} from this ${site.type === 'directory' ? 'directory' : 'site'}`">
                        <i class="fas fa-file-alt"></i>
                        {{ site.type === 'directory' ? 'Files' : 'Files' }}
                      </button>
                      <button 
                        @click="quickScanSite(site)"
                        class="site-action-btn scan-btn"
                        :disabled="isScanning"
                        title="Scan for changes">
                        <i class="fas fa-search"></i>
                        Scan
                      </button>
                      <button 
                        @click="removeSite(site.id)"
                        class="site-action-btn delete-btn"
                        title="Remove site">
                        <i class="fas fa-trash"></i>
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- All Documents View -->
          <!-- 2024-11-27: Fix - Renamed "documents" to "files" for consistency -->
          <div v-if="selectedSiteForDocuments" class="all-pages-section">
            <div class="section-subheader">
              <h4>
                <i class="fas fa-file-alt"></i>
                All files from {{ selectedSiteForDocuments.type === 'directory' ? selectedSiteForDocuments.directory_path : selectedSiteForDocuments.root_url }}
              </h4>
              <div class="header-actions">
                <button
                  @click="batchProcessReplace"
                  class="btn btn-primary"
                  :disabled="isBatchProcessing || getReplaceConfiguredCount() === 0"
                  :title="getReplaceConfiguredCount() === 0 ? 'No files configured for Replace' : `Batch process ${getReplaceConfiguredCount()} file(s) configured for Replace`">
                  <i class="fas fa-sync-alt"></i>
                  Batch Process ({{ getReplaceConfiguredCount() }})
                </button>
                <button @click="closeSiteDocuments" class="btn btn-secondary">
                  <i class="fas fa-arrow-left"></i>
                  Back to Sites
                </button>
              </div>
            </div>
              
            <!-- Search and filters -->
            <div class="pages-filters">
                <div class="search-container">
                  <i class="fas fa-search search-icon"></i>
                  <input 
                    v-model="allPagesSearchQuery"
                    type="text"
                    :placeholder="`Search all ${selectedSiteForDocuments.type === 'directory' ? 'files' : 'documents'}...`"
                    class="search-input">
                </div>
                
                <label class="filter-checkbox">
                  <input 
                    v-model="showOnlyMonitoredPages"
                    type="checkbox">
                  <span class="checkbox-label">Monitored</span>
                </label>
                
                <label class="filter-checkbox">
                  <input 
                    v-model="showOnlyChangedPages"
                    type="checkbox">
                  <span class="checkbox-label">Changed</span>
                </label>
              </div>
            </div>

            <!-- Page/File Actions -->
            <!-- 2024-11-27: Fix - Renamed "Documents" to "Files" for consistency -->
            <div v-if="selectedSiteForDocuments && allPages.length > 0" class="page-bulk-actions">
              <span class="bulk-actions-label">Monitor All Files:</span>
              <button @click="selectAllGlobalPages" class="bulk-action-btn" :title="`Check all ${selectedSiteForDocuments.type === 'directory' ? 'files' : 'documents'}`">
                <i class="fas fa-check"></i>
                Check All
              </button>
              <button @click="clearAllGlobalPages" class="bulk-action-btn" :title="`Uncheck all ${selectedSiteForDocuments.type === 'directory' ? 'files' : 'documents'}`">
                <i class="fas fa-times"></i>
                Uncheck All
              </button>
            </div>

            <!-- All pages table -->
            <div v-if="selectedSiteForDocuments && allPages.length > 0" class="all-pages-table-container">
              <table class="all-pages-data-table">
                <thead>
                  <tr>
                    <th class="th-number">#</th>
                    <th class="th-monitor">Monitor</th>
                    <th class="th-url">{{ selectedSiteForDocuments.type === 'directory' ? 'File Name' : 'URL' }}</th>
                    <th class="th-status">Status</th>
                    <th class="th-changed">Last Changed</th>
                    <th class="th-processed">Last Processed</th>
                    <th class="th-kb">Knowledge Base</th>
                    <th class="th-on-process">On Process</th>
                    <th class="th-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="isLoadingSites">
                    <td colspan="9" class="loading-state">
                      <div class="spinner-container">
                        <div class="spinner"></div>
                        <span>Loading pages...</span>
                      </div>
                    </td>
                  </tr>
                  <tr v-else-if="filteredSiteDocuments.length === 0">
                    <td colspan="9" class="empty-state">
                      No {{ selectedSiteForDocuments.type === 'directory' ? 'files' : 'documents' }} found for this {{ selectedSiteForDocuments.type === 'directory' ? 'directory' : 'site' }} matching the current filters
                    </td>
                  </tr>
                  <tr v-else v-for="(page, index) in paginatedSiteDocuments" :key="`${page.site_id}-${page.id}`" class="data-row">
                    <td class="td-number">{{ (currentPage - 1) * documentsPerPage + index + 1 }}</td>
                    <td class="td-monitor">
                      <label class="monitor-checkbox">
                        <input 
                          type="checkbox" 
                          :checked="page.monitored !== 0"
                          @change="togglePageMonitoring(page, $event.target.checked)"
                          title="Toggle monitoring">
                        <span class="checkmark"></span>
                      </label>
                    </td>
                    <td class="td-url">
                      <span v-if="page.changed_at" class="changed-indicator" title="Page has changes">
                        <i class="fas fa-fire"></i>
                      </span>
                      <span v-if="page.status_code >= 300 && page.status_code < 400" class="redirect-indicator" title="Redirected">
                        <i class="fas fa-external-link-alt"></i>
                      </span>
                      <template v-if="selectedSiteForDocuments.type === 'directory'">
                        <span :title="page.url" class="file-name">
                          <i class="fas fa-file" :class="`file-type-${(page.file_type || '').split('/')[1] || 'generic'}`"></i>
                          {{ page.title || page.url }}
                        </span>
                      </template>
                      <template v-else>
                        <a :href="page.url" target="_blank" :title="page.url" class="page-link">
                          {{ page.url }}
                        </a>
                      </template>
                    </td>
                    <td class="td-status">
                      <span :class="['status-badge', `status-${page.status_code}`]">
                        {{ page.status_code || '?' }}
                      </span>
                    </td>
                    <td class="td-changed">
                      <span v-if="page.changed_at" class="changed-time">
                        {{ formatRelativeTime(page.changed_at) }}
                      </span>
                      <span v-else class="no-changes">No changes</span>
                    </td>
                    <td class="td-processed">
                      <span v-if="page.processed_at" class="processed-time">
                        {{ formatDateTime(page.processed_at) }}
                      </span>
                      <span v-else class="not-processed">Not processed</span>
                    </td>
                    <td class="td-kb">
                      <select
                        :value="getEffectiveKnowledgeBase(page)"
                        @change="updatePageKnowledgeBase(page, $event.target.value)"
                        class="knowledge-base-select kb-select-small"
                        :disabled="loadingKnowledgeBases">
                        <option value="">
                          {{ selectedSiteForDocuments.default_knowledge_base_id
                             ? `Use Default (${getKnowledgeBaseName(selectedSiteForDocuments.default_knowledge_base_id)})`
                             : 'None' }}
                        </option>
                        <option v-for="kb in knowledgeBases" :key="kb.id" :value="kb.id">
                          {{ kb.name }}
                        </option>
                      </select>
                    </td>
                    <td class="td-on-process">
                      <select
                        v-model="page.on_process_action"
                        @change="updateOnProcessAction(page)"
                        class="on-process-select kb-select-small">
                        <option value="do_nothing">Do Nothing</option>
                        <option value="replace">Replace</option>
                      </select>
                    </td>
                    <td class="td-actions">
                      <div class="action-buttons-row">
                        <button
                          v-if="page.processingState === 'completed'"
                          @click="showPageArticles(page)"
                          class="action-btn articles-btn"
                          title="View generated articles">
                          <i class="fas fa-file-alt"></i>
                          <span class="btn-text">Articles</span>
                        </button>
                        <button
                          v-else
                          @click="processPageContent(page)"
                          class="action-btn process-btn"
                          :disabled="isProcessing || page.processingState === 'processing'"
                          :title="page.processingState === 'processing' ? 'Processing...' : 'Process content from this page'">
                          <i class="fas" :class="page.processingState === 'processing' ? 'fa-spinner fa-spin' : 'fa-cogs'"></i>
                          <span class="btn-text">{{ page.processingState === 'processing' ? 'Processing...' : 'Process' }}</span>
                        </button>
                        <button
                          @click="showPageHistory(page)"
                          class="action-btn history-btn"
                          title="View article history">
                          <i class="fas fa-history"></i>
                          <span class="btn-text">History</span>
                        </button>
                        <button
                          @click="scanSinglePage(page)"
                          class="action-btn scan-btn"
                          :disabled="isScanning"
                          title="Scan this page for changes">
                          <i class="fas fa-search"></i>
                          <span class="btn-text">Scan</span>
                        </button>
                        <button
                          @click="deletePage(page)"
                          class="action-btn delete-btn"
                          title="Delete this page">
                          <i class="fas fa-trash"></i>
                          <span class="btn-text">Delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
              
            <!-- Pagination Controls -->
            <div v-if="totalPages > 1" class="pagination-controls">
              <div class="pagination-info">
                Showing {{ startIndex + 1 }}-{{ endIndex }} of {{ filteredSiteDocuments.length }} documents
              </div>
              <div class="pagination-buttons">
                  <button 
                    @click="goToPage(1)" 
                    :disabled="currentPage === 1"
                    class="pagination-btn">
                    <i class="fas fa-angle-double-left"></i>
                  </button>
                  <button 
                    @click="goToPage(currentPage - 1)" 
                    :disabled="currentPage === 1"
                    class="pagination-btn">
                    <i class="fas fa-angle-left"></i>
                  </button>
                  
                  <span class="page-numbers">
                    <button 
                      v-for="page in visiblePages" 
                      :key="page"
                      @click="goToPage(page)"
                      :class="['page-number', { active: page === currentPage }]">
                      {{ page }}
                    </button>
                  </span>
                  
                  <button 
                    @click="goToPage(currentPage + 1)" 
                    :disabled="currentPage === totalPages"
                    class="pagination-btn">
                    <i class="fas fa-angle-right"></i>
                  </button>
                  <button 
                    @click="goToPage(totalPages)" 
                    :disabled="currentPage === totalPages"
                    class="pagination-btn">
                    <i class="fas fa-angle-double-right"></i>
                  </button>
                </div>
                <div class="per-page-selector">
                  <label>
                    Per page:
                    <select v-model="documentsPerPage" @change="resetToFirstPage">
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                      <option value="200">200</option>
                    </select>
                  </label>
                </div>
              </div>
            </div>
        </div>
      </div>

      <!-- Articles Modal -->
      <div v-if="showArticlesModal" class="articles-modal-overlay" @click.self="showArticlesModal = false">
        <div class="articles-modal-container">
          <!-- Modal Header -->
          <div class="articles-modal-header">
            <div class="modal-title-section">
              <h3 class="modal-title">
                <i class="fas fa-file-alt"></i>
                Articles from {{ selectedUrl }}
              </h3>
              <div class="articles-count" v-if="!loadingModalArticles && modalArticles.length > 0">
                {{ modalArticles.length }} article{{ modalArticles.length !== 1 ? 's' : '' }} found
              </div>
            </div>
            <div class="modal-header-actions">
              <button 
                v-if="!loadingModalArticles && modalArticles.length > 0"
                @click="refreshModalArticles()"
                class="action-btn secondary"
                :disabled="loadingModalArticles">
                <i class="fas fa-refresh" :class="{ 'fa-spin': loadingModalArticles }"></i>
                Refresh
              </button>
              <button type="button" class="close-modal-btn" @click="showArticlesModal = false">
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>

          <!-- Modal Content -->
          <div class="articles-modal-content">
            <!-- Loading State -->
            <div v-if="loadingModalArticles" class="modal-loading-state">
              <div class="loading-spinner"></div>
              <p>Loading articles...</p>
            </div>

            <!-- Articles Table -->
            <div v-else-if="modalArticles.length > 0" class="articles-table-wrapper">
              <div class="articles-table-container">
                <table class="articles-table">
                  <thead>
                    <tr>
                      <th class="col-title">Title</th>
                      <th class="col-date">Created</th>
                      <th class="col-status">Status</th>
                      <th class="col-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="article in modalArticles" :key="article.id" class="article-row">
                      <td class="col-title">
                        <div class="article-title">{{ article.title }}</div>
                      </td>
                      <td class="col-date">{{ formatDate(article.created_at) }}</td>
                      <td class="col-status">
                        <span class="status-badge" :class="article.status.toLowerCase()">
                          {{ article.status }}
                        </span>
                      </td>
                      <td class="col-actions">
                        <div class="article-actions">
                          <button class="action-btn primary small" @click="viewArticleDetails(article)">
                            <i class="fas fa-eye"></i>
                            View
                          </button>
                          <button class="action-btn danger small" @click="deleteApprovedArticle(article)">
                            <i class="fas fa-trash"></i>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Empty State -->
            <div v-else class="modal-empty-state">
              <div class="empty-icon">
                <i class="fas fa-file-alt"></i>
              </div>
              <h4>No Articles Found</h4>
              <p>No approved articles have been generated from this URL yet.</p>
            </div>
          </div>

          <!-- Modal Footer -->
          <div class="articles-modal-footer">
            <button @click="showArticlesModal = false" class="action-btn secondary">
              <i class="fas fa-times"></i>
              Close
            </button>
          </div>
        </div>
      </div>

      <!-- Article Preview Modal -->
      <div 
        v-if="showArticlePreviewModal"
        class="article-preview-modal-overlay"
        @click.self="showArticlePreviewModal = false">
        <div class="article-preview-modal">
          <!-- Header -->
          <div class="modal-header">
            <div class="header-content">
              <div class="header-icon">📄</div>
              <div class="header-text">
                <h3>{{ previewingArticle?.title || 'Article Preview' }}</h3>
                <div class="header-meta" v-if="previewingArticle">
                  <span class="status-badge" :class="previewingArticle.status?.toLowerCase()">
                    {{ previewingArticle.status }}
                  </span>
                  <span class="date-badge">{{ formatDate(previewingArticle.created_at) }}</span>
                </div>
              </div>
            </div>
            <button type="button" class="close" @click="showArticlePreviewModal = false">
              ✕
            </button>
          </div>
          
          <!-- Content -->
          <div class="article-preview-content" v-if="previewingArticle">
            <!-- Article Info Grid -->
            <div class="article-info-grid">
              <div class="article-info-item">
                <strong>Source URL</strong>
                <span class="source-url">{{ previewingArticle.source_url || previewingArticle.sourceUrl || 'Not specified' }}</span>
              </div>
              <div class="article-info-item">
                <strong>KB ID</strong>
                <span class="kb-id">{{ previewingArticle.genesys_kb_id }}</span>
              </div>
              <div class="article-info-item" v-if="previewingArticle.genesys_document_id">
                <strong>Genesys Document ID</strong>
                <span class="genesys-document-id">{{ previewingArticle.genesys_document_id }}</span>
              </div>
              <div class="article-info-item" v-if="previewingArticle.genesys_document_state">
                <strong>Document State</strong>
                <span class="genesys-document-state">{{ previewingArticle.genesys_document_state }}</span>
              </div>
              <div class="article-info-item" v-if="previewingArticle.import_job_id">
                <strong>Import Job ID</strong>
                <span class="import-job-id">{{ previewingArticle.import_job_id }}</span>
              </div>
              <div class="article-info-item" v-if="previewingArticle.import_date_created">
                <strong>Import Date</strong>
                <span class="import-date">{{ new Date(previewingArticle.import_date_created).toLocaleString() }}</span>
              </div>
            </div>

            <!-- Meta Information -->
            <div class="article-meta-info" v-if="previewingArticle.data">
              <div class="article-meta-row" v-if="getContentField(previewingArticle.data, 'category.name')">
                <strong>Category</strong>
                <span class="category-tag">{{ getContentField(previewingArticle.data, 'category.name') }}</span>
              </div>
              
              <div class="article-meta-row" v-if="getContentField(previewingArticle.data, 'labels')?.length">
                <strong>Labels</strong>
                <div class="tags-container">
                  <span 
                    v-for="label in getContentField(previewingArticle.data, 'labels')" 
                    :key="label.name"
                    class="label-tag">
                    {{ label.name }}
                  </span>
                </div>
              </div>
              
              <div class="article-meta-row" v-if="getContentField(previewingArticle.data, 'alternatives')?.length">
                <strong>Alternatives</strong>
                <div class="tags-container">
                  <span 
                    v-for="alt in getContentField(previewingArticle.data, 'alternatives')" 
                    :key="alt.phrase"
                    class="alternative-item">
                    {{ alt.phrase }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Article Content -->
            <div class="content-preview-section">
              <h4>Content</h4>
              <div class="content-text" v-html="renderArticleContent(previewingArticle.data)"></div>
            </div>
          </div>
          
          <!-- Footer -->
          <div class="modal-footer">
            <button @click="showArticlePreviewModal = false" class="btn-secondary">
              Close
            </button>
          </div>
        </div>
      </div>


    </div>
  
</template>

<script setup>
import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css'
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import BaseButton from "./UI/BaseButton.vue"
import BaseInput from "./UI/BaseInput.vue"
import ButtonSubmit from "./ButtonSubmit.vue"
import ButtonCancel from "./ButtonCancel.vue"
import { flashMessage } from "@/functions"

// Router instance
const router = useRouter()

// Reactive data
// Add these to your existing ref declarations
const isInitialLoading = ref(true)
const wysiwygContent = ref('')
const quillOptions = ref({
  theme: 'snow',
  modules: {
    toolbar: [
      [{ 'header': [3, 4, false] }], // This will show "Header 3", "Header 4", "Normal"
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ]
  },
  formats: ['header', 'bold', 'italic', 'underline', 'list', 'bullet', 'link'], // Specify allowed formats
  placeholder: 'Write your article content here...'
})
const quillEditor = ref(null)
const editorInstance = ref(null)
const pendingContent = ref('')
const activeSource = ref('url')
const urlInput = ref('')
const textInput = ref('')

// Genesys-specific data
const knowledgeBases = ref([])
const selectedKnowledgeBase = ref(null)
const loadingKnowledgeBases = ref(false)
const importingManually = ref(false)
const genesysStatus = ref({ connected: false })

// Approved articles history
const loadingHistory = ref(false)
const approvedHistory = ref([])
const comparisonResults = ref(null)
const comparingArticles = ref(false)

// Site-specific approved articles
const siteApprovedArticles = ref({})
const activeTab = ref({})

// Processing states
const isProcessing = ref(false)
const isBatchProcessing = ref(false)
const batchProgress = ref({
  current: 0,
  total: 0,
  currentFile: ''
})
const processedData = ref(null)
const successMessage = ref('')
const errorMessage = ref('')

// Article approval system - NEW
const articleApprovalStates = ref({}) // { articleIndex: 'approved' | 'rejected' | 'pending' }
const importedApprovedCount = ref(0)
const selectedArticles = ref([]) 

// Article editing states
const showArticleEditor = ref(false)
const editingArticle = ref(null)
const editingArticleIndex = ref(-1)
const editingArticleStatus = ref('Published')
const labelsText = ref('')

// Progress tracking
const currentStatus = ref('')
const progressInfo = ref({
  current_step: null,
  total_steps: null
})
const progressLog = ref([])

// Knowledge Inventory data
const knowledgeUrl = ref('')
const monitoredSites = ref([])
// Removed unused expandedSites variable
const isScanning = ref(false)
const isLoadingSites = ref(false)

// Site-specific documents view
const selectedSiteForDocuments = ref(null)
const currentPage = ref(1)
const documentsPerPage = ref(50)

// Add sites section
// 2024-11-27: Added crawl_depth for deep crawling support
const crawlOptions = ref({
  maxPages: 0,
  crawl_depth: 1,  // Default depth is 1 (shallow crawl)
  maxConcurrency: 5,
  quickScan: false
})
const monitoringInterval = ref(null)
const autoMonitoringEnabled = ref(false)

// Template refs

// Chunk buffer for large data
const chunkBuffer = ref({
  raw_content: null,
  analysis: null,
  documents: [],
  categories: [],
  labels: [],
  contexts: [],
  version: 2,
  isChunked: false
})

//Configuration
const showConfigModal = ref(false)
const savingConfig = ref(false)
const testingConfig = ref(false)
const configStatus = ref(null)
const showArticlesModal = ref(false)
const loadingModalArticles = ref(false)
const modalArticles = ref([])
const selectedUrl = ref('')

// Article preview modal
const showArticlePreviewModal = ref(false)
const previewingArticle = ref(null)

// All pages data
const allPagesSearchQuery = ref('')
const showOnlyMonitoredPages = ref(false)
const showOnlyChangedPages = ref(false)

const aiConfig = ref({
  bedrock_api_key: '',
  bedrock_model: 'openai.gpt-oss-120b-1:0',
  alert_email: '',
  from_email: '',
  genesys_client_id: '',
  genesys_client_secret: '',
  genesys_environment: 'usw2.pure.cloud',
  upload_directory: '/uploaded-files'
})

// Approval system computed properties - NEW
const approvedArticles = computed(() => {
  if (!processedData.value?.json?.documents) return []
  return processedData.value.json.documents.filter((_, index) => 
    articleApprovalStates.value[index] === 'approved'
  )
})

const pendingArticles = computed(() => {
  if (!processedData.value?.json?.documents) return []
  return processedData.value.json.documents.filter((_, index) => 
    !articleApprovalStates.value[index] || articleApprovalStates.value[index] === 'pending'
  )
})

const rejectedArticles = computed(() => {
  if (!processedData.value?.json?.documents) return []
  return processedData.value.json.documents.filter((_, index) => 
    articleApprovalStates.value[index] === 'rejected'
  )
})

// All Sites Modal computed properties
const totalPagesCount = computed(() => {
  return monitoredSites.value.reduce((total, site) => total + (site.totalPages || 0), 0)
})

const totalMonitoredCount = computed(() => {
  return monitoredSites.value.reduce((total, site) => total + getMonitoredCount(site), 0)
})

const totalChangedCount = computed(() => {
  return monitoredSites.value.reduce((total, site) => total + getChangedCount(site), 0)
})

// Computed property for all pages from all sites
const allPages = computed(() => {
  const pages = []
  monitoredSites.value.forEach(site => {
    if (site.pages && Array.isArray(site.pages)) {
      site.pages.forEach(page => {
        pages.push({
          ...page,
          site_id: site.id,
          site_root_url: site.root_url
        })
      })
    }
  })
  return pages
})

// Filtered all pages based on search and filters
// Site-specific document filtering
const filteredSiteDocuments = computed(() => {
  if (!selectedSiteForDocuments.value) return []
  
  let filtered = allPages.value.filter(page => page.site_id === selectedSiteForDocuments.value.id)
  
  // Apply search filter
  if (allPagesSearchQuery.value.trim()) {
    const query = allPagesSearchQuery.value.toLowerCase().trim()
    filtered = filtered.filter(page => 
      page.url.toLowerCase().includes(query) || 
      (page.site_root_url && page.site_root_url.toLowerCase().includes(query))
    )
  }
  
  // Apply monitoring filter
  if (showOnlyMonitoredPages.value) {
    filtered = filtered.filter(page => page.monitored !== 0)
  }
  
  // Apply changes filter
  if (showOnlyChangedPages.value) {
    filtered = filtered.filter(page => page.changed_at)
  }
  
  // Sort by ID to maintain stable order (pages stay in place after processing)
  return filtered.sort((a, b) => {
    // Sort by page ID to maintain consistent order
    return (a.id || 0) - (b.id || 0)
  })
})

// Pagination computed properties
const totalPages = computed(() => {
  return Math.ceil(filteredSiteDocuments.value.length / documentsPerPage.value)
})

const paginatedSiteDocuments = computed(() => {
  const start = (currentPage.value - 1) * documentsPerPage.value
  const end = start + documentsPerPage.value
  return filteredSiteDocuments.value.slice(start, end)
})

const startIndex = computed(() => {
  return (currentPage.value - 1) * documentsPerPage.value
})

const endIndex = computed(() => {
  const end = currentPage.value * documentsPerPage.value
  return Math.min(end, filteredSiteDocuments.value.length)
})

const visiblePages = computed(() => {
  const pages = []
  const total = totalPages.value
  const current = currentPage.value
  
  // Show up to 5 page numbers around current page
  let start = Math.max(1, current - 2)
  let end = Math.min(total, current + 2)
  
  // Adjust if we're near the beginning or end
  if (end - start < 4) {
    if (start === 1) {
      end = Math.min(total, start + 4)
    } else {
      start = Math.max(1, end - 4)
    }
  }
  
  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  
  return pages
})

const crawledUrls = computed(() => {
  return processedData.value?.raw_content?.crawled_urls || []
})

const progressPercentage = computed(() => {
  if (!progressInfo.value.total_steps || !progressInfo.value.current_step) return 0
  return Math.round((progressInfo.value.current_step / progressInfo.value.total_steps) * 100)
})


const allArticlesSelected = computed(() => {
  if (!processedData.value?.json?.documents?.length) return false
  return selectedArticles.value.length === processedData.value.json.documents.length
})

// Show all available knowledge bases
const filteredKnowledgeBases = computed(() => {
  return knowledgeBases.value
})

//Config Methods
const openConfigModal = async () => {
  showConfigModal.value = true
  configStatus.value = null
  await loadAIConfiguration()
}

const closeConfigModal = () => {
  showConfigModal.value = false
  configStatus.value = null
}

const loadAIConfiguration = async () => {
  try {
    const company = localStorage.getItem("companyID") || "default"
    const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/ai-configuration?company=${company}`, {
      headers: {
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      if (data.success && data.configuration) {
        console.log('Loaded AI configuration from backend:', data.configuration)
        // Mask sensitive data for display
        aiConfig.value = {
          bedrock_api_key: data.configuration.bedrock_api_key ? '••••••••••••••••' : '',
          bedrock_model: data.configuration.bedrock_model || 'openai.gpt-oss-120b-1:0',
          alert_email: data.configuration.alert_email || '',
          from_email: data.configuration.from_email || '',
          genesys_client_id: data.configuration.genesys_client_id || '',
          genesys_client_secret: data.configuration.genesys_client_secret ? '••••••••••••••••' : '',
          genesys_environment: data.configuration.genesys_environment || 'usw2.pure.cloud',
          upload_directory: data.configuration.upload_directory || '/uploaded-files'
        }
        
        // Update Genesys connection status based on whether credentials exist
        const hasGenesysConfig = data.configuration.genesys_client_id && data.configuration.genesys_client_secret
        if (hasGenesysConfig) {
          // Test connection silently
          testGenesysConnection()
        } else {
          genesysStatus.value.connected = false
        }
        
        console.log('AI configuration loaded successfully')
      } else {
        console.log('No AI configuration found, using defaults')
        genesysStatus.value.connected = false
      }
    } else {
      console.warn('Failed to load AI configuration:', response.status)
      genesysStatus.value.connected = false
    }
  } catch (error) {
    console.error('Failed to load AI configuration:', error)
    genesysStatus.value.connected = false
  }
}

const saveAIConfiguration = async () => {
  savingConfig.value = true
  configStatus.value = null
  const company = localStorage.getItem("companyID")
  try {
    // Only send non-masked values
    const configData = {}

    if (aiConfig.value.bedrock_api_key && !aiConfig.value.bedrock_api_key.includes('••••')) {
      configData.bedrock_api_key = aiConfig.value.bedrock_api_key
    }

    if (aiConfig.value.bedrock_model) {
      configData.bedrock_model = aiConfig.value.bedrock_model
    }

    if (aiConfig.value.alert_email) {
      configData.alert_email = aiConfig.value.alert_email
    }

    if (aiConfig.value.from_email) {
      configData.from_email = aiConfig.value.from_email
    }

    if (aiConfig.value.genesys_client_id) {
      configData.genesys_client_id = aiConfig.value.genesys_client_id
    }

    if (aiConfig.value.genesys_client_secret && !aiConfig.value.genesys_client_secret.includes('••••')) {
      configData.genesys_client_secret = aiConfig.value.genesys_client_secret
    }

    configData.genesys_environment = aiConfig.value.genesys_environment
    configData.upload_directory = aiConfig.value.upload_directory

    console.log('Saving AI configuration:', configData)

    const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/ai-configuration?company=${company}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(configData)
    })
    
    const result = await response.json()
    
    if (result.success) {
      configStatus.value = {
        type: 'success',
        message: 'Configuration saved successfully!'
      }
      showFlashMessage('success', 'AI Configuration saved successfully')
      
      // Refresh knowledge bases list to test connection
      setTimeout(() => {
        loadKnowledgeBases()
      }, 1000)
    } else {
      configStatus.value = {
        type: 'error',
        message: result.message || 'Failed to save configuration'
      }
    }
  } catch (error) {
    console.error('Failed to save AI configuration:', error)
    configStatus.value = {
      type: 'error',
      message: 'Failed to save configuration'
    }
  } finally {
    savingConfig.value = false
  }
}

// Approved Articles History Functions
const loadApprovedHistory = async () => {
  if (!urlInput.value) return
  
  loadingHistory.value = true
  try {
    const company = localStorage.getItem("companyID") || "default"
    const encodedUrl = encodeURIComponent(urlInput.value)
    const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/approved-articles/${encodedUrl}?company=${company}`)
    const data = await response.json()
    
    if (data.success) {
      approvedHistory.value = data.articles
      showFlashMessage('success', `Found ${data.total_count} previously approved articles`)
    } else {
      showFlashMessage('error', 'Failed to load approved articles history')
    }
  } catch (error) {
    console.error('Failed to load approved history:', error)
    showFlashMessage('error', 'Failed to load approved articles history')
  } finally {
    loadingHistory.value = false
  }
}

const compareWithExisting = async () => {
  if (!processedData.value?.json?.documents || !urlInput.value) return
  
  comparingArticles.value = true
  try {
    const company = localStorage.getItem("companyID") || "default"
    const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/compare-articles?company=${company}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        articles: processedData.value.json.documents,
        source_url: urlInput.value
      })
    })
    
    const data = await response.json()
    if (data.success) {
      comparisonResults.value = data
      showFlashMessage('info', `Comparison: ${data.summary.new} new, ${data.summary.updated} updated, ${data.summary.unchanged} unchanged`)
    } else {
      showFlashMessage('error', 'Failed to compare articles')
    }
  } catch (error) {
    console.error('Failed to compare articles:', error)
    showFlashMessage('error', 'Failed to compare articles with existing ones')
  } finally {
    comparingArticles.value = false
  }
}

const approveNewAndUpdatedArticles = () => {
  if (!comparisonResults.value || !processedData.value?.json?.documents) return
  
  // Get indices of articles that are new or updated
  const articlesToApprove = []
  
  comparisonResults.value.comparison_results.forEach((result, index) => {
    if (result.status === 'new' || result.status === 'updated') {
      articlesToApprove.push(index)
    }
  })
  
  // Update approval states
  articlesToApprove.forEach(index => {
    articleApprovalStates.value[index] = 'approved'
  })
  
  showFlashMessage('success', `Auto-approved ${articlesToApprove.length} articles (new and updated only)`)
}

const clearComparison = () => {
  comparisonResults.value = null
}

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString()
}

// Link articles with Genesys documents based on import date
const linkArticlesWithGenesysDocuments = async (articles) => {
  try {
    const company = localStorage.getItem("companyID") || "default"
    
    // Get all knowledge bases first
    const kbResponse = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/knowledge-bases?company=${company}`)
    if (!kbResponse.ok) {
      console.warn('Could not fetch knowledge bases for linking')
      return
    }
    
    // Strategy: Since import results aren't stored with individual articles,
    // we'll search for Genesys documents that match by title across all KBs
    // and were created recently (within the last 7 days)
    const sevenDaysAgo = new Date()
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)
    const sevenDaysAgoISO = sevenDaysAgo.toISOString()
    
    for (const article of articles) {
      if (article.genesys_kb_id) {
        // We have a specific KB to search in
        try {
          const docsResponse = await fetch(
            `${process.env.VUE_APP_AI_BASE_URL}/knowledge-bases/${article.genesys_kb_id}/documents?company=${company}&created_after=${sevenDaysAgoISO}`
          )
          
          if (docsResponse.ok) {
            const docsData = await docsResponse.json()
            const genesysDocuments = docsData.documents || []
            
            // Find matching document by title similarity
            const articleTitle = article.title
            const matchingDoc = genesysDocuments.find(doc => {
              if (!doc.title || !articleTitle) return false
              
              // Check exact match or partial match
              return doc.title.toLowerCase() === articleTitle.toLowerCase() || 
                     doc.title.toLowerCase().includes(articleTitle.toLowerCase()) ||
                     articleTitle.toLowerCase().includes(doc.title.toLowerCase())
            })
            
            if (matchingDoc) {
              // Add Genesys document info to the article
              article.genesys_document = {
                id: matchingDoc.id,
                title: matchingDoc.title,
                state: matchingDoc.state,
                dateCreated: matchingDoc.dateCreated,
                selfUri: matchingDoc.selfUri
              }
              console.log(`✅ Linked article "${articleTitle}" with Genesys doc ${matchingDoc.id}`)
            } else {
              console.log(`⚠️ No match found for article "${articleTitle}" in KB ${article.genesys_kb_id}`)
            }
          }
        } catch (error) {
          console.warn(`Failed to fetch documents for KB ${article.genesys_kb_id}:`, error)
        }
      }
    }
  } catch (error) {
    console.warn('Failed to link articles with Genesys documents:', error)
  }
}

// Articles Modal Functions
const loadModalArticles = async (url) => {
  loadingModalArticles.value = true
  
  try {
    const company = localStorage.getItem("companyID") || "default"
    const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/approved-articles-by-site?company=${company}&site_url=${encodeURIComponent(url)}`)
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }
    
    const articles = await response.json()
    
    // Link articles with Genesys documents
    await linkArticlesWithGenesysDocuments(articles)
    
    modalArticles.value = articles
    
  } catch (error) {
    console.error('Error loading articles for modal:', error)
    showFlashMessage('error', `Failed to load articles: ${error.message}`)
    modalArticles.value = []
  } finally {
    loadingModalArticles.value = false
  }
}

const loadModalArticlesByUrl = async (url) => {
  loadingModalArticles.value = true
  
  try {
    const company = localStorage.getItem("companyID") || "default"
    
    // Check if this is a file-based source (starts with "Files:")
    if (url.startsWith('Files:')) {
      console.log('Loading articles for file-based source:', url)
      
      // For file-based articles, use the exact source URL match
      const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/approved-articles/${encodeURIComponent(url)}?company=${company}`)
      
      if (response.ok) {
        const data = await response.json()
        modalArticles.value = data.articles || []
      } else {
        console.warn(`File-based articles not found for: ${url}`)
        modalArticles.value = []
      }
      return
    }
    
    // Use the correct backend endpoint for URL-specific articles
    let response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/approved-articles/${encodeURIComponent(url)}?company=${company}`)
    
    // If that endpoint doesn't work, fallback to site-based and filter client-side
    if (!response.ok && response.status === 404) {
      console.log('URL-specific endpoint not found, using site-based endpoint with client filtering')
      
      // Extract the site root URL from the page URL
      const urlObj = new URL(url)
      const siteRootUrl = `${urlObj.protocol}//${urlObj.hostname}`
      
      response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/approved-articles-by-site?company=${company}&site_url=${encodeURIComponent(siteRootUrl)}`)
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }
      
      const allArticles = await response.json()
      // Filter articles to only show those from the specific URL
      const filteredArticles = allArticles.filter(article => 
        article.source_url === url || article.source_url === url + '/' || article.source_url === url.replace(/\/$/, '')
      )
      modalArticles.value = filteredArticles
      
    } else if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    } else {
      // URL-specific endpoint worked
      const result = await response.json()
      modalArticles.value = result.articles || []
    }
    
  } catch (error) {
    console.error('Error loading articles by URL:', error)
    showFlashMessage('error', `Failed to load articles: ${error.message}`)
    modalArticles.value = []
  } finally {
    loadingModalArticles.value = false
  }
}

const refreshModalArticles = async () => {
  if (!selectedUrl.value) return
  
  // Determine if this is a specific URL or site root URL
  // If it's a full URL with path, use the URL-specific endpoint
  if (selectedUrl.value.includes('/') && !selectedUrl.value.endsWith('/')) {
    loadModalArticlesByUrl(selectedUrl.value)
  } else {
    loadModalArticles(selectedUrl.value)
  }
}

const viewArticleDetails = (article) => {
  try {
    console.log('=== ARTICLE PREVIEW DEBUG ===')
    console.log('Article object:', article)
    console.log('Article content type:', typeof article.content)
    console.log('Article content:', article.content)
    
    // Check if content exists and is valid
    if (!article.content || 
        article.content === 'undefined' || 
        article.content === 'null' ||
        article.content.includes('"error": "Content missing')) {
      showFlashMessage('error', 'Article content is missing. The article may need to be reprocessed.')
      return
    }
    
    // Parse the article content JSON
    let articleData
    if (typeof article.content === 'string') {
      articleData = JSON.parse(article.content)
    } else {
      articleData = article.content
    }
    
    console.log('Parsed articleData:', articleData)
    console.log('ArticleData type:', typeof articleData)
    console.log('ArticleData keys:', Object.keys(articleData || {}))
    
    // Set up preview data
    previewingArticle.value = {
      title: article.title,
      status: article.status,
      created_at: article.created_at,
      genesys_kb_id: article.genesys_kb_id,
      source_url: article.source_url || articleData?.draft?.sourceUrl,
      data: articleData,
      rawContent: article.content,  // Add raw content for debugging
      genesys_document_id: article.genesys_document_id,
      genesys_document_state: article.genesys_document_state,
      genesys_document_uri: article.genesys_document_uri,
      import_job_id: article.import_job_id,
      import_upload_key: article.import_upload_key,
      import_date_created: article.import_date_created
    }
    
    console.log('Preview article set:', previewingArticle.value)
    
    // Show preview modal
    showArticlePreviewModal.value = true
    
  } catch (error) {
    console.error('Error parsing article content:', error)
    console.error('Content that failed to parse:', article.content)
    showFlashMessage('error', `Failed to open article: ${error.message}`)
  }
}

const deleteApprovedArticle = async (article) => {
  if (!confirm(`Are you sure you want to delete the article "${article.title}"?`)) {
    return
  }
  
  try {
    const company = localStorage.getItem("companyID") || "default"
    const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/approved-articles/${article.id}?company=${company}`, {
      method: 'DELETE'
    })
    
    const data = await response.json()
    if (data.success) {
      showFlashMessage('success', 'Article deleted successfully')
      // Remove from site articles cache and refresh
      for (const siteId in siteApprovedArticles.value) {
        const articles = siteApprovedArticles.value[siteId]
        const index = articles.findIndex(a => a.id === article.id)
        if (index !== -1) {
          articles.splice(index, 1)
          break
        }
      }
    } else {
      showFlashMessage('error', 'Failed to delete article')
    }
  } catch (error) {
    console.error('Failed to delete article:', error)
    showFlashMessage('error', 'Failed to delete article')
  }
}

// Initialize site tabs to pages
const initializeSiteTabs = () => {
  monitoredSites.value.forEach(site => {
    activeTab.value[site.id] = 'pages'
  })
}

const testGenesysConnection = async () => {
  try {
    const company = localStorage.getItem("companyID") || "default"
    const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/test-ai-configuration?company=${company}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    })
    
    const result = await response.json()
    
    if (result.success) {
      // Check if Genesys status indicates connection
      genesysStatus.value.connected = result.genesys_status === 'Connected'
    } else {
      genesysStatus.value.connected = false
    }
  } catch (error) {
    console.error('Silent Genesys connection test failed:', error)
    genesysStatus.value.connected = false
  }
}

const testConfiguration = async () => {
  testingConfig.value = true
  configStatus.value = null
  const company = localStorage.getItem("companyID")
  try {
    const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/test-ai-configuration?company=${company}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    })
    
    const result = await response.json()
    
    if (result.success) {
      configStatus.value = {
        type: 'success',
        message: `Connection test successful! Bedrock: ${result.bedrock_status}, Genesys: ${result.genesys_status}`
      }
    } else {
      configStatus.value = {
        type: 'error',
        message: result.message || 'Connection test failed'
      }
    }
  } catch (error) {
    console.error('Configuration test failed:', error)
    configStatus.value = {
      type: 'error',
      message: 'Connection test failed'
    }
  } finally {
    testingConfig.value = false
  }
}

// Approval system methods - NEW
const getApprovalStatus = (index) => {
  return articleApprovalStates.value[index] || 'pending'
}

const getApprovalStatusText = (index) => {
  const status = getApprovalStatus(index)
  switch (status) {
    case 'approved': return 'Approved'
    case 'rejected': return 'Rejected'
    default: return 'Pending'
  }
}

const getArticleRowClass = (index) => {
  const status = getApprovalStatus(index)
  return `approval-${status}`
}

const initializeApprovalStates = () => {
  // Reset approval states when new data is processed
  articleApprovalStates.value = {}
  importedApprovedCount.value = 0
  selectedArticles.value = []
}

// Selection methods - ADD THESE
const toggleArticleSelection = (index) => {
  const selectedIndex = selectedArticles.value.indexOf(index)
  if (selectedIndex > -1) {
    selectedArticles.value.splice(selectedIndex, 1)
  } else {
    selectedArticles.value.push(index)
  }
}

const toggleAllArticles = () => {
  if (allArticlesSelected.value) {
    selectedArticles.value = []
  } else {
    selectedArticles.value = processedData.value.json.documents.map((_, index) => index)
  }
}

const approveSelectedArticles = () => {
  if (selectedArticles.value.length === 0) return
  
  console.log('=== APPROVING ARTICLES ===')
  console.log('Selected articles to approve:', selectedArticles.value)
  console.log('Current approval states before approval:', { ...articleApprovalStates.value })
  
  selectedArticles.value.forEach(index => {
    const article = processedData.value.json.documents[index]
    const title = article.published?.title || article.draft?.title || 'Untitled'
    const status = article.published ? 'Published' : 'Draft'
    
    // Set approval status
    articleApprovalStates.value[index] = 'approved'
    
    console.log(`✅ Approved article ${index}: "${title}" (${status})`)
  })
  
  console.log('Approval states after approval:', { ...articleApprovalStates.value })
  console.log('=== APPROVAL COMPLETE ===')
  
  showFlashMessage('success', `Approved ${selectedArticles.value.length} articles`)
  selectedArticles.value = []
}

const onEditorReady = (quill) => {
  console.log('Editor is ready:', quill)
  editorInstance.value = quill
  
  // If we have pending content, set it now
  if (pendingContent.value) {
    console.log('Setting pending content:', pendingContent.value)
    quill.root.innerHTML = pendingContent.value
    wysiwygContent.value = pendingContent.value
    pendingContent.value = ''
  }
  
  // Also check if wysiwygContent has content but editor is empty
  if (wysiwygContent.value && (!quill.root.innerHTML || quill.root.innerHTML === '<p><br></p>')) {
    console.log('Setting wysiwygContent to editor:', wysiwygContent.value)
    quill.root.innerHTML = wysiwygContent.value
  }
}

const rejectSelectedArticles = () => {
  if (selectedArticles.value.length === 0) return
  
  selectedArticles.value.forEach(index => {
    articleApprovalStates.value[index] = 'rejected'
  })
  
  showFlashMessage('info', `Rejected ${selectedArticles.value.length} articles`)
  selectedArticles.value = []
}

const resetSelectedApprovals = () => {
  if (selectedArticles.value.length === 0) return
  
  selectedArticles.value.forEach(index => {
    articleApprovalStates.value[index] = 'pending'
  })
  
  showFlashMessage('info', `Reset ${selectedArticles.value.length} articles to pending`)
  selectedArticles.value = []
}

// Enhanced import methods - NEW
// CORRECTED: importApprovedArticles method with proper Genesys format
const importApprovedArticles = async () => {
  if (!selectedKnowledgeBase.value) {
    showFlashMessage('error', 'No knowledge base selected')
    return
  }

  console.log('=== IMPORT PROCESS START ===')
  console.log('All current approval states:', { ...articleApprovalStates.value })
  console.log('Total documents:', processedData.value?.json?.documents?.length || 0)
  
  if (!processedData.value?.json?.documents) {
    showFlashMessage('error', 'No documents found to import')
    return
  }
  
  // VALIDATION: Ensure all articles have valid IDs before import
  let hasInvalidIds = false
  processedData.value.json.documents.forEach((article, index) => {
    if (!article.id) {
      console.error(`❌ Article ${index} missing ID:`, article)
      hasInvalidIds = true
      // Fix it immediately
      article.id = `${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`
      console.log(`🔧 Generated emergency ID: ${article.id}`)
    }
  })
  
  if (hasInvalidIds) {
    console.log('⚠️ Fixed missing IDs before import')
  }
  
  // Get approved article indices with ID tracking
  const approvedIndices = []
  
  for (let i = 0; i < processedData.value.json.documents.length; i++) {
    const approvalStatus = articleApprovalStates.value[i] || 'pending'
    const article = processedData.value.json.documents[i]
    
    if (!article) {
      console.log(`Article ${i}: NULL/UNDEFINED`)
      continue
    }
    
    const title = article.published?.title || article.draft?.title || 'Untitled'
    const status = article.published ? 'Published' : 'Draft'
    const articleId = article.id || 'NO-ID'
    
    console.log(`Article ${i}: "${title}" (${status}) [ID: ${articleId}] - Approval: ${approvalStatus}`)
    
    if (approvalStatus === 'approved') {
      approvedIndices.push(i)
      console.log(`  → WILL BE IMPORTED [ID: ${articleId}]`)
    } else {
      console.log(`  → SKIPPED (not approved)`)
    }
  }
  
  console.log('Final approved indices for import:', approvedIndices)
  
  if (approvedIndices.length === 0) {
    console.log('❌ No approved articles found')
    showFlashMessage('error', 'No approved articles found to import. Please approve some articles first.')
    return
  }

  importingManually.value = true
  try {
    // Process approved articles with STRICT ID validation
    const approvedArticlesData = approvedIndices.map(index => {
      const originalArticle = processedData.value.json.documents[index]
      const articleTitle = originalArticle.published?.title || originalArticle.draft?.title || 'Untitled'
      let articleId = originalArticle.id
      
      // CRITICAL: Ensure article has a valid ID
      if (!articleId) {
        articleId = `${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`
        console.log(`🚨 EMERGENCY ID GENERATION for "${articleTitle}": ${articleId}`)
      }
      
      // Determine if this should be published
      const shouldBePublished = !!originalArticle.published
      
      console.log(`Processing for import: "${articleTitle}" [ID: ${articleId}]`, {
        index: index,
        hasDraft: !!originalArticle.draft,
        hasPublished: !!originalArticle.published,
        willBePublished: shouldBePublished
      })
      
      if (shouldBePublished) {
        // Create PUBLISHED article - ensure ID
        const publishedArticle = {
          id: articleId,
          published: JSON.parse(JSON.stringify(originalArticle.published))
        }
        
        if (!publishedArticle.published.dateImport) {
          publishedArticle.published.dateImport = Date.now()
        }
        
        console.log(`✅ Created PUBLISHED import structure for "${articleTitle}" [ID: ${articleId}]`)
        return publishedArticle
        
      } else {
        // Create DRAFT article - ensure ID
        const draftArticle = {
          id: articleId,
          draft: JSON.parse(JSON.stringify(originalArticle.draft))
        }
        
        console.log(`📝 Created DRAFT import structure for "${articleTitle}" [ID: ${articleId}]`)
        return draftArticle
      }
    }).filter(article => article !== null)

    console.log(`Created ${approvedArticlesData.length} articles for import`)
    
    // FINAL VALIDATION: Check all import articles have IDs
    const invalidImportArticles = approvedArticlesData.filter(article => !article.id)
    if (invalidImportArticles.length > 0) {
      console.error('❌ CRITICAL: Articles without IDs found in import data:', invalidImportArticles)
      throw new Error(`${invalidImportArticles.length} articles missing IDs in import data`)
    }
    
    // Log the final import data structure
    approvedArticlesData.forEach((article, i) => {
      const title = article.published?.title || article.draft?.title
      const status = article.published ? 'PUBLISHED' : 'DRAFT'
      console.log(`Final Import Article ${i}: "${title}" [ID: ${article.id}] - ${status}`)
    })

    // Create import data with source URL tracking
    const importData = {
      ...processedData.value.json,
      documents: approvedArticlesData,
      // Only set source_url if we have a single source, otherwise let individual documents handle it
      ...(urlInput.value || textInput.value ? { source_url: urlInput.value || textInput.value } : {}),
      source_type: activeSource.value,  // Track whether it was URL, text, or file
      processed_at: new Date().toISOString()  // Track when this was processed
    }

    // Count published vs draft
    const publishedCount = approvedArticlesData.filter(a => !!a.published).length
    const draftCount = approvedArticlesData.filter(a => !!a.draft).length

    console.log(`Import summary:`)
    console.log(`- Published articles: ${publishedCount}`)
    console.log(`- Draft articles: ${draftCount}`)
    console.log(`- Total articles: ${approvedArticlesData.length}`)

    // Send to API
    console.log('Sending to API...')
    const company = localStorage.getItem("companyID") || "default"
    const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/upload-to-knowledge-base?company=${company}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        knowledge_base_id: selectedKnowledgeBase.value.id,
        json_data: importData
      })
    })

    const result = await response.json()
    console.log('API Response:', result)
    
    if (result.success) {
      processedData.value.import_result = result.result
      importedApprovedCount.value = approvedArticlesData.length
      showFlashMessage('success', `Successfully imported ${approvedArticlesData.length} approved articles (${publishedCount} published, ${draftCount} drafts) to ${selectedKnowledgeBase.value.name}`)
      console.log('✅ IMPORT SUCCESSFUL')
    } else {
      throw new Error(result.message || 'Import failed')
    }
  } catch (error) {
    console.error('❌ Import failed:', error)
    processedData.value.import_error = error.message
    showFlashMessage('error', `Import failed: ${error.message}`)
  } finally {
    importingManually.value = false
    console.log('=== IMPORT PROCESS END ===')
  }
}



const getSourceTypeLabel = () => {
  switch (activeSource.value) {
    case 'url': return 'Website URL'
    case 'file': return 'Document Import'
    case 'text': return 'Direct Text Input'
    default: return 'Unknown'
  }
}

const getContentLength = () => {
  if (activeSource.value === 'text' && textInput.value) {
    return textInput.value.length.toLocaleString()
  }
  return null
}

const formatUrl = (url) => {
  try {
    const urlObj = new URL(url)
    return `${urlObj.hostname}${urlObj.pathname}`
  } catch {
    return url
  }
}

const getCurrentArticleData = (article) => {
  // Return published if it exists, otherwise return draft, otherwise return empty structure
  const data = article.published || article.draft || {
    title: '',
    alternatives: [],
    category: { name: 'General Information' },
    labels: [],
    visible: true,
    variations: [{
      name: "Default",
      priority: 1,
      body: {
        blocks: [{
          type: "Paragraph",
          paragraph: {
            blocks: [{ type: "Text", text: { text: "" } }],
            properties: { fontType: "Paragraph" }
          }
        }]
      }
    }]
  }
  
  return data
}

const getLogEntryClass = (log) => {
  if (log.message.includes('Error') || log.type === 'error') return 'log-error'
  if (log.message.includes('Complete') || log.message.includes('success') || log.message.includes('✅')) return 'log-success'
  if (log.message.includes('Warning') || log.message.includes('⚠️')) return 'log-warning'
  if (log.message.includes('🚀') || log.message.includes('import')) return 'log-import'
  return 'log-info'
}

const formatTimestamp = (timestamp) => {
  return new Date(timestamp).toLocaleTimeString()
}

const clearProgressLog = () => {
  progressLog.value = []
}

const getArticleStatus = (article) => {
  // If article has published property, it's published
  if (article.published) {
    return 'Published'
  }
  // Otherwise it's a draft
  return 'Draft'
}

const getArticleStatusClass = (article) => {
  const status = getArticleStatus(article)
  return status === 'Published' ? 'published' : 'draft'
}

// Genesys Knowledge Base Methods
const loadKnowledgeBases = async () => {
  loadingKnowledgeBases.value = true
  try {
    const company = localStorage.getItem("companyID") || "default"
    const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/knowledge-bases?company=${company}`)
    const data = await response.json()

    if (data.success) {
      knowledgeBases.value = data.knowledge_bases.sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
      )
      genesysStatus.value.connected = true
      showFlashMessage('success', `Loaded ${data.knowledge_bases.length} knowledge bases`)
    } else {
      throw new Error('Failed to load knowledge bases')
    }
  } catch (error) {
    console.error('Failed to load knowledge bases:', error)
    genesysStatus.value.connected = false
    showFlashMessage('error', `Failed to load knowledge bases: ${error.message}`)
    knowledgeBases.value = []
  } finally {
    loadingKnowledgeBases.value = false
  }
}

const importToKnowledgeBase = async () => {
  if (!selectedKnowledgeBase.value || !processedData.value?.json) {
    showFlashMessage('error', 'No knowledge base selected or no data to import')
    return
  }

  importingManually.value = true
  try {
    const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/import-to-knowledge-base`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        knowledge_base_id: selectedKnowledgeBase.value.id,
        json_data: processedData.value.json
      })
    })

    const result = await response.json()
    
    if (result.success) {
      processedData.value.import_result = result.result
      showFlashMessage('success', `Successfully imported to ${selectedKnowledgeBase.value.name}`)
    } else {
      throw new Error(result.message || 'Import failed')
    }
  } catch (error) {
    console.error('Manual import failed:', error)
    processedData.value.import_error = error.message
    showFlashMessage('error', `Import failed: ${error.message}`)
  } finally {
    importingManually.value = false
  }
}

// Add these functions to your component
const convertGenesysToHtml = (article) => {
  console.log('Converting article to HTML:', article)
  
  let html = ''
  
  try {
    // Check both published and draft for content
    const articleData = article.published || article.draft || {}
    const blocks = articleData.variations?.[0]?.body?.blocks || []
    
    console.log('Found blocks:', blocks)
    
    if (blocks.length === 0) {
      return '<p>No content available - start editing here...</p>'
    }
    
    for (const block of blocks) {
      if (block.type === 'Paragraph') {
        const fontType = block.paragraph?.properties?.fontType || 'Paragraph'
        let tag = 'p'
        
        if (fontType === 'Heading3') tag = 'h3'
        else if (fontType === 'Heading4') tag = 'h4'
        
        let content = ''
        const textBlocks = block.paragraph?.blocks || []
        
        for (const textBlock of textBlocks) {
          if (textBlock.type === 'Text') {
            let text = textBlock.text?.text || ''
            
            // Apply formatting
            if (textBlock.text?.marks?.includes('Bold')) {
              text = `<strong>${text}</strong>`
            }
            
            // Handle hyperlinks
            if (textBlock.text?.hyperlink) {
              text = `<a href="${textBlock.text.hyperlink}">${text}</a>`
            }
            
            content += text
          }
        }
        
        // Only add non-empty content
        if (content.trim()) {
          html += `<${tag}>${content}</${tag}>`
        }
      }
      else if (block.type === 'OrderedList' || block.type === 'UnorderedList') {
        const listTag = block.type === 'OrderedList' ? 'ol' : 'ul'
        html += `<${listTag}>`
        
        const listItems = block.list?.blocks || []
        for (const item of listItems) {
          const itemBlocks = item.blocks || []
          let itemContent = ''
          
          for (const textBlock of itemBlocks) {
            if (textBlock.type === 'Text') {
              itemContent += textBlock.text?.text || ''
            }
          }
          
          if (itemContent.trim()) {
            html += `<li>${itemContent}</li>`
          }
        }
        
        html += `</${listTag}>`
      }
    }
    
    console.log('Generated HTML:', html)
    
    // Return default content if nothing was generated
    return html || '<p>Start editing your article content here...</p>'
    
  } catch (error) {
    console.error('Error converting Genesys to HTML:', error)
    return '<p>Error loading content - please refresh and try again.</p>'
  }
}

const convertHtmlToGenesys = (html) => {
  // Create a temporary DOM element to parse HTML
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = html
  
  const blocks = []
  
  // Convert each child element to Genesys blocks
  for (const element of tempDiv.children) {
    const tagName = element.tagName.toLowerCase()
    
    if (['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName)) {
      let fontType = 'Paragraph'
      if (tagName === 'h3') fontType = 'Heading3'
      else if (tagName === 'h4') fontType = 'Heading4'
      
      const textBlocks = []
      
      // Handle mixed content (text + formatting)
      for (const node of element.childNodes) {
        if (node.nodeType === Node.TEXT_NODE) {
          if (node.textContent.trim()) {
            textBlocks.push({
              type: 'Text',
              text: { text: node.textContent }
            })
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const text = { text: node.textContent }
          
          if (node.tagName.toLowerCase() === 'strong' || node.tagName.toLowerCase() === 'b') {
            text.marks = ['Bold']
          }
          
          if (node.tagName.toLowerCase() === 'a' && node.href) {
            text.hyperlink = node.href
          }
          
          textBlocks.push({
            type: 'Text',
            text: text
          })
        }
      }
      
      if (textBlocks.length === 0) {
        textBlocks.push({
          type: 'Text',
          text: { text: element.textContent || '' }
        })
      }
      
      blocks.push({
        type: 'Paragraph',
        paragraph: {
          blocks: textBlocks,
          properties: { fontType }
        }
      })
    }
    else if (['ol', 'ul'].includes(tagName)) {
      const listType = tagName === 'ol' ? 'OrderedList' : 'UnorderedList'
      const listBlocks = []
      
      for (const li of element.querySelectorAll('li')) {
        listBlocks.push({
          type: 'ListItem',
          blocks: [{
            type: 'Text',
            text: { text: li.textContent || '' }
          }],
          properties: { fontType: 'Paragraph' }
        })
      }
      
      blocks.push({
        type: listType,
        list: { blocks: listBlocks }
      })
    }
  }
  
  return blocks
}

// Article Management Methods
const getArticlePreview = (article) => {
  try {
    // Check both published and draft for content
    const contentData = article.published || article.draft
    
    if (contentData?.variations?.[0]?.body?.blocks?.length > 0) {
      const firstBlock = contentData.variations[0].body.blocks[0]
      if (firstBlock.type === 'Paragraph' && firstBlock.paragraph?.blocks?.length > 0) {
        const text = firstBlock.paragraph.blocks[0].text?.text || ''
        return text.length > 100 ? text.substring(0, 100) + '...' : text
      }
    }
    return 'No content preview available'
  } catch (error) {
    console.warn('Error getting article preview:', error)
    return 'Preview unavailable'
  }
}

const toggleArticleVisibility = (index, isVisible) => {
  const article = processedData.value.json.documents[index]
  
  if (article.published) {
    article.published.visible = isVisible
  } else if (article.draft) {
    article.draft.visible = isVisible
  }
  
  console.log(`Updated visibility for article ${index}:`, isVisible)
}

const editArticle = async (index) => {
  console.log('=== EDITING ARTICLE ===')
  console.log('Editing article at index:', index)
  console.log('Current approval status:', getApprovalStatus(index))
  
  editingArticleIndex.value = index
  const originalArticle = processedData.value.json.documents[index]
  
  console.log('Original article:', {
    id: originalArticle.id,
    title: originalArticle.published?.title || originalArticle.draft?.title,
    hasDraft: !!originalArticle.draft,
    hasPublished: !!originalArticle.published
  })
  
  // Create editing copy - PRESERVE THE ORIGINAL ID
  editingArticle.value = JSON.parse(JSON.stringify(originalArticle))
  
  // Determine current status based on which key exists
  editingArticleStatus.value = originalArticle.published ? 'Published' : 'Draft'
  
  // Get the content from whichever key exists
  const contentData = originalArticle.published || originalArticle.draft
  
  if (contentData?.labels?.length > 0) {
    labelsText.value = contentData.labels.map(l => l.name).join(', ')
  } else {
    labelsText.value = ''
  }
  
  // Convert content to HTML
  const convertedHtml = convertGenesysToHtml(originalArticle)
  console.log('Editing article:', {
    id: originalArticle.id,
    title: contentData?.title,
    status: editingArticleStatus.value,
    hasDraft: !!originalArticle.draft,
    hasPublished: !!originalArticle.published,
    approvalStatus: getApprovalStatus(index)
  })
  
  showArticleEditor.value = true
  
  await nextTick()
  
  // Set content in editor with improved timing
  wysiwygContent.value = convertedHtml
  
  const setEditorContent = () => {
    if (editorInstance.value) {
      console.log('Setting content on ready editor')
      editorInstance.value.root.innerHTML = convertedHtml
      return true
    } else {
      console.log('Editor not ready, storing content for later')
      pendingContent.value = convertedHtml
      return false
    }
  }
  
  // Try immediately
  if (!setEditorContent()) {
    // If editor not ready, try again with increasing delays
    setTimeout(() => {
      if (!setEditorContent()) {
        setTimeout(() => {
          setEditorContent()
        }, 500)
      }
    }, 150)
  }
}

const updateLabels = () => {
  const labels = labelsText.value.split(',').map(label => label.trim()).filter(label => label.length > 0)
  editingArticle.value.draft.labels = labels.map(name => ({ name }))
}

const addAlternative = () => {
  if (!editingArticle.value.draft.alternatives) {
    editingArticle.value.draft.alternatives = []
  }
  editingArticle.value.draft.alternatives.push({ phrase: '', autocomplete: true })
}

const removeAlternative = (index) => {
  editingArticle.value.draft.alternatives.splice(index, 1)
}



const closeArticleEditor = () => {
  showArticleEditor.value = false
  editingArticle.value = null
  editingArticleIndex.value = -1
}

const updateArticleStatus = () => {
  if (editingArticleStatus.value === 'Draft') {
    // Keep only draft, remove published
    if (!editingArticle.value.draft) {
      editingArticle.value.draft = editingArticle.value.published || {
        title: '',
        alternatives: [],
        category: { name: 'General Information' },
        labels: [],
        visible: true,
        variations: [{
          name: "Default",
          priority: 1,
          body: {
            blocks: [{
              type: "Paragraph",
              paragraph: {
                blocks: [{ type: "Text", text: { text: "" } }],
                properties: { fontType: "Paragraph" }
              }
            }]
          }
        }]
      }
    }
    // Remove published completely for draft status
    delete editingArticle.value.published
    
  } else if (editingArticleStatus.value === 'Published') {
    // Ensure we have both draft and published for published status
    if (!editingArticle.value.draft) {
      editingArticle.value.draft = {
        title: '',
        alternatives: [],
        category: { name: 'General Information' },
        labels: [],
        visible: true,
        variations: [{
          name: "Default",
          priority: 1,
          body: {
            blocks: [{
              type: "Paragraph",
              paragraph: {
                blocks: [{ type: "Text", text: { text: "" } }],
                properties: { fontType: "Paragraph" }
              }
            }]
          }
        }]
      }
    }
    
    // Create published from current draft content
    editingArticle.value.published = JSON.parse(JSON.stringify(editingArticle.value.draft))
  }
}

const saveArticleChanges = () => {
  if (editingArticleIndex.value >= 0 && editingArticle.value) {
    console.log('=== SAVING ARTICLE ===')
    console.log('Editing article index:', editingArticleIndex.value)
    
    // Get the ORIGINAL article to preserve its ID and approval status
    const originalArticle = processedData.value.json.documents[editingArticleIndex.value]
    const originalId = originalArticle.id
    const currentApprovalStatus = articleApprovalStates.value[editingArticleIndex.value] || 'pending'
    
    console.log('Original article ID:', originalId)
    console.log('Current approval status BEFORE save:', currentApprovalStatus)
    
    // Convert WYSIWYG HTML back to Genesys blocks
    const blocks = convertHtmlToGenesys(wysiwygContent.value)
    
    // Prepare the content structure
    const contentStructure = {
      title: editingArticle.value.draft?.title || editingArticle.value.published?.title || '',
      alternatives: editingArticle.value.draft?.alternatives || editingArticle.value.published?.alternatives || [],
      category: editingArticle.value.draft?.category || editingArticle.value.published?.category || { name: 'General Information' },
      labels: editingArticle.value.draft?.labels || editingArticle.value.published?.labels || [],
      visible: editingArticle.value.draft?.visible !== undefined ? editingArticle.value.draft.visible : 
               editingArticle.value.published?.visible !== undefined ? editingArticle.value.published.visible : true,
      variations: [{
        name: "Default",
        priority: 1,
        body: { blocks: blocks }
      }]
    }
    
    console.log('Saving article with status:', editingArticleStatus.value)
    
    // Create the article structure based on the selected status
    // CRITICAL: Use the ORIGINAL ID, not a new one
    const savedArticle = {
      id: originalId || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    }
    
    if (editingArticleStatus.value === 'Published') {
      // Published article - ONLY published key
      savedArticle.published = {
        ...contentStructure,
        dateImport: Date.now()
      }
      console.log('✅ Article saved as Published - created published key only')
    } else {
      // Draft article - ONLY draft key  
      savedArticle.draft = contentStructure
      console.log('📝 Article saved as Draft - created draft key only')
    }
    
    console.log('Preserving original ID:', savedArticle.id)
    console.log('Preserving approval status:', currentApprovalStatus)
    
    // Save back to main data AT THE SAME INDEX
    processedData.value.json.documents[editingArticleIndex.value] = savedArticle
    
    // IMMEDIATELY restore the approval status using the SAME index
    articleApprovalStates.value[editingArticleIndex.value] = currentApprovalStatus
    
    console.log('Article saved. Final structure:', {
      index: editingArticleIndex.value,
      id: savedArticle.id,
      title: savedArticle.published?.title || savedArticle.draft?.title,
      hasDraft: !!savedArticle.draft,
      hasPublished: !!savedArticle.published,
      finalStatus: savedArticle.published ? 'Published' : 'Draft'
    })
    console.log('Approval status AFTER save:', articleApprovalStates.value[editingArticleIndex.value])
    console.log('=== SAVE COMPLETE ===')
    
    const finalStatus = savedArticle.published ? 'Published' : 'Draft'
    showFlashMessage('success', `Article updated successfully as ${finalStatus} (approval: ${currentApprovalStatus})`)
    closeArticleEditor()
  }
}

const deleteArticle = (index) => {
  if (processedData.value.json.documents.length > 1) {
    // Remove from selected articles if present
    const selectedIndex = selectedArticles.value.indexOf(index)
    if (selectedIndex > -1) {
      selectedArticles.value.splice(selectedIndex, 1)
    }
    
    // Adjust selected articles indices for articles after the deleted one
    selectedArticles.value = selectedArticles.value.map(selectedIdx => 
      selectedIdx > index ? selectedIdx - 1 : selectedIdx
    )
    
    // Remove approval state for this article and adjust indices
    delete articleApprovalStates.value[index]
    
    // Shift approval states for articles after the deleted one
    const newApprovalStates = {}
    Object.keys(articleApprovalStates.value).forEach(key => {
      const articleIndex = parseInt(key)
      if (articleIndex < index) {
        newApprovalStates[articleIndex] = articleApprovalStates.value[key]
      } else if (articleIndex > index) {
        newApprovalStates[articleIndex - 1] = articleApprovalStates.value[key]
      }
    })
    articleApprovalStates.value = newApprovalStates
    
    processedData.value.json.documents.splice(index, 1)
    showFlashMessage('success', 'Article deleted successfully')
  } else {
    showFlashMessage('error', 'Cannot delete the last remaining article')
  }
}


// Knowledge Inventory processing method


const addProgressLog = (message, type = 'info') => {
  progressLog.value.push({
    message,
    type,
    timestamp: new Date().toISOString()
  })
  
  // Keep only last 20 log entries
  if (progressLog.value.length > 20) {
    progressLog.value = progressLog.value.slice(-20)
  }
}

// const startStreaming = async (url, body) => {
//   return new Promise((resolve, reject) => {
//     const requestOptions = {
//       method: 'POST',
//       headers: activeSource.value === 'file' ? {} : { 'Content-Type': 'application/json' },
//       body: body
//     }
    
//     fetch(url, requestOptions)
//       .then(response => {
//         if (!response.ok) {
//           throw new Error(`HTTP error! status: ${response.status}`)
//         }
        
//         const reader = response.body.getReader()
//         const decoder = new TextDecoder()
//         let buffer = ''
        
//         const readStream = () => {
//           return reader.read().then(({ done, value }) => {
//             if (done) {
//               resolve()
//               return
//             }
            
//             const chunk = decoder.decode(value)
//             buffer += chunk
            
//             const lines = buffer.split('\n')
//             buffer = lines.pop() || ''
            
//             for (const line of lines) {
//               if (line.startsWith('data: ')) {
//                 try {
//                   const data = JSON.parse(line.slice(6))
//                   handleStreamUpdate(data)
//                 } catch (e) {
//                   console.warn('Parse error:', e.message)
//                 }
//               }
//             }
            
//             return readStream()
//           })
//         }
        
//         return readStream()
//       })
//       .catch(error => {
//         reject(error)
//       })
//   })
// }

const startStreamingJSON = async (url, body) => {
  return new Promise((resolve, reject) => {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: body
    }
    
    fetch(url, requestOptions)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        
        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        
        const readStream = () => {
          return reader.read().then(({ done, value }) => {
            if (done) {
              resolve()
              return
            }
            
            const chunk = decoder.decode(value)
            buffer += chunk
            
            const lines = buffer.split('\n')
            buffer = lines.pop() || ''
            
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.slice(6))
                  handleStreamUpdate(data)
                } catch (e) {
                  console.warn('Parse error:', e.message)
                }
              }
            }
            
            return readStream()
          })
        }
        
        return readStream()
      })
      .catch(error => {
        reject(error)
      })
  })
}

const handleStreamUpdate = (data) => {
  if (data.type === 'heartbeat') {
    return
  }
  
  currentStatus.value = data.message
  addProgressLog(data.message, data.type)
  
  if (data.current_step !== undefined) {
    progressInfo.value.current_step = data.current_step
  }
  
  if (data.total_steps !== undefined) {
    progressInfo.value.total_steps = data.total_steps
  }
  
  // Handle chunked data
  if (data.type === 'complete_start') {
    chunkBuffer.value = {
      raw_content: null,
      analysis: null,
      documents: [],
      categories: [],
      labels: [],
      contexts: [],
      version: 2,
      isChunked: true
    }
    return
  }
  
  if (data.type === 'chunk_raw_content' && data.data?.raw_content) {
    chunkBuffer.value.raw_content = data.data.raw_content
    return
  }
  
  if (data.type === 'chunk_analysis' && data.data?.analysis) {
    chunkBuffer.value.analysis = data.data.analysis
    return
  }
  
  if (data.type === 'chunk_documents' && data.data?.json_chunk?.documents) {
    const chunkDocs = data.data.json_chunk.documents
    chunkBuffer.value.documents.push(...chunkDocs)
    
    const chunkInfo = data.data.json_chunk.chunk_info
    if (chunkInfo) {
      currentStatus.value = `Received ${chunkBuffer.value.documents.length} of ${chunkInfo.total_documents} articles...`
    }
    return
  }
  
  if (data.type === 'chunk_metadata' && data.data?.json_metadata) {
    const metadata = data.data.json_metadata
    chunkBuffer.value.categories = metadata.categories || []
    chunkBuffer.value.labels = metadata.labels || []
    chunkBuffer.value.contexts = metadata.contexts || []
    chunkBuffer.value.version = metadata.version || 2
    return
  }
  
  if (data.type === 'chunk_json' && data.data?.json) {
    const json = data.data.json
    chunkBuffer.value.documents = json.documents || []
    chunkBuffer.value.categories = json.categories || []
    chunkBuffer.value.labels = json.labels || []
    chunkBuffer.value.contexts = json.contexts || []
    chunkBuffer.value.version = json.version || 2
    return
  }
  
  if (data.type === 'complete_final' && chunkBuffer.value.isChunked) {
    // Assemble the final result from chunks
    const assembledData = {
      success: true,
      json: {
        version: chunkBuffer.value.version,
        documents: chunkBuffer.value.documents,
        categories: chunkBuffer.value.categories,
        labels: chunkBuffer.value.labels,
        contexts: chunkBuffer.value.contexts
      },
      analysis: chunkBuffer.value.analysis || 'Processing completed successfully',
      raw_content: chunkBuffer.value.raw_content || {}
    }
    
    // CRITICAL: Ensure all articles have IDs
    if (assembledData.json.documents) {
      assembledData.json.documents.forEach((article, index) => {
        if (!article.id) {
          article.id = `${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`
          console.log(`Generated ID for chunked article ${index}: ${article.id}`)
        }
      })
    }
    
    processedData.value = assembledData
    
    // Initialize approval states for new articles
    initializeApprovalStates()
    
    let successMsg = `Content processed successfully! Generated ${chunkBuffer.value.documents.length} articles.`
    successMessage.value = successMsg
    showFlashMessage('success', successMsg)
    
    // Note: Use "View Generated Articles" button in progress log to navigate
    
    // Reset chunk buffer
    chunkBuffer.value = {
      raw_content: null,
      analysis: null,
      documents: [],
      categories: [],
      labels: [],
      contexts: [],
      version: 2,
      isChunked: false
    }
    return
  }
  
  // Handle normal completion (non-chunked)
  if (data.type === 'complete' && data.data && !chunkBuffer.value.isChunked) {
    console.log('🔍 DEBUG: Received complete data:', data.data)
    console.log('🔍 DEBUG: Documents count:', data.data.json?.documents?.length)
    
    // CRITICAL: Ensure all articles have IDs
    if (data.data.json?.documents) {
      data.data.json.documents.forEach((article, index) => {
        if (!article.id) {
          article.id = `${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`
          console.log(`Generated ID for article ${index}: ${article.id}`)
        }
      })
    }
    
    processedData.value = data.data
    console.log('🔍 DEBUG: processedData set to:', processedData.value)
    console.log('🔍 DEBUG: Button condition check:', processedData.value?.json?.documents?.length > 0)
    
    // Initialize approval states for new articles
    initializeApprovalStates()
    
    let successMsg = 'Content processed successfully!'
    successMessage.value = successMsg
    showFlashMessage('success', successMsg)
    
    // Note: Use "View Generated Articles" button in progress log to navigate
  } else if (data.type === 'error') {
    errorMessage.value = data.message
    showFlashMessage('error', data.message)
  }
}

// const downloadJson = () => {
//   if (processedData.value?.json) {
//     downloadFile(
//       JSON.stringify(processedData.value.json, null, 2),
//       'genesys-kb-article.json',
//       'application/json'
//     )
//   }
// }

// const downloadFile = (content, filename, mimeType) => {
//   const blob = new Blob([content], { type: mimeType })
//   const url = URL.createObjectURL(blob)
//   const link = document.createElement('a')
//   link.href = url
//   link.download = filename
//   document.body.appendChild(link)
//   link.click()
//   document.body.removeChild(link)
//   URL.revokeObjectURL(url)
  
//   showFlashMessage('success', `${filename} downloaded successfully`)
// }


const showFlashMessage = (type, message) => {
  const color = type === 'success' ? "#059669" : type === 'info' ? "#3b82f6" : "#dc2626"
  const textColor = "#ffffff"
  
  try {
    if (typeof flashMessage === 'function') {
      flashMessage(undefined, color, textColor, message)
    }
  } catch (error) {
    console.warn('FlashMessage error:', error)
  }
  
  if (type === 'success') {
    successMessage.value = message
    setTimeout(() => successMessage.value = '', 5000)
  } else if (type === 'error') {
    errorMessage.value = message
    setTimeout(() => errorMessage.value = '', 8000)
  }
}

// Knowledge Inventory Methods
const addToMonitored = async () => {
  if (!knowledgeUrl.value) return

  isScanning.value = true
  isProcessing.value = true
  progressLog.value = []
  currentStatus.value = 'Starting crawl...'

  try {
    // 2024-11-27: Added crawl_depth parameter
    const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/api/crawl-stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        url: knowledgeUrl.value,
        maxPages: crawlOptions.value.maxPages,
        crawl_depth: crawlOptions.value.crawl_depth || 1,
        maxConcurrency: crawlOptions.value.maxConcurrency,
        quickScan: crawlOptions.value.quickScan
      })
    })

    if (!response.ok) {
      throw new Error('Failed to start crawl')
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    let done = false
    while (!done) {
      const result = await reader.read()
      done = result.done
      if (done) break
      const value = result.value

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6))

            if (data.type === 'progress') {
              progressInfo.value.current_step = data.current_step || 0
              progressInfo.value.total_steps = data.total_steps || 0
              currentStatus.value = data.message || 'Processing...'
              addProgressLog(data.message || 'Processing...', 'info')
            } else if (data.type === 'status') {
              currentStatus.value = data.message || ''
              addProgressLog(data.message || '', 'info')
            } else if (data.type === 'complete' || data.type === 'complete_final') {
              const message = data.message || `Site added successfully! Discovered ${data.data?.pagesDiscovered || 0} pages`
              showFlashMessage('success', message)
              addProgressLog(message, 'success')
              knowledgeUrl.value = ''
              await loadMonitoredSites()
            } else if (data.type === 'error') {
              throw new Error(data.message || 'Crawl failed')
            }
          } catch (parseError) {
            console.error('Error parsing SSE data:', parseError)
          }
        }
      }
    }
  } catch (error) {
    console.error('Error adding site:', error)
    showFlashMessage('error', `Failed to add site: ${error.message}`)
    addProgressLog(`Error: ${error.message}`, 'error')
  } finally {
    isScanning.value = false
    isProcessing.value = false
    currentStatus.value = ''
  }
}

// File handling methods for document upload
const handleFileDrop = (event) => {
  event.preventDefault()
  const files = Array.from(event.dataTransfer.files)
  if (files.length > 0) {
    processDocumentFiles(files)
  }
}

const handleFileSelect = (event) => {
  const files = Array.from(event.target.files)
  if (files.length > 0) {
    processDocumentFiles(files)
  }
}

const processDocumentFiles = async (files) => {
  if (!files || files.length === 0) return
  
  // Validate file types
  const validTypes = ['.pdf', '.doc', '.docx', '.txt', '.md', '.html', '.json']
  const invalidFiles = files.filter(file => {
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase()
    return !validTypes.includes(fileExtension)
  })
  
  if (invalidFiles.length > 0) {
    showFlashMessage('error', `Invalid file types: ${invalidFiles.map(f => f.name).join(', ')}. Please upload PDF, DOC, DOCX, TXT, MD, HTML, or JSON files only.`)
    return
  }

  isScanning.value = true
  showFlashMessage('info', `Processing ${files.length} file(s)...`)

  try {
    // Prepare file data for upload
    const formData = new FormData()
    
    files.forEach((file, index) => {
      formData.append('files', file)
      // Add metadata for each file
      formData.append(`fileInfo[${index}]`, JSON.stringify({
        name: file.name,
        size: file.size,
        lastModified: file.lastModified,
        type: file.type
      }))
    })
    
    // Add directory path from configuration
    const directory = aiConfig.value.upload_directory || '/uploaded-files'
    formData.append('directory', directory)
    
    // Send files to backend for processing
    const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/api/files/upload`, {
      method: 'POST',
      body: formData
    })
    
    if (response.ok) {
      const result = await response.json()
      showFlashMessage('success', `Successfully uploaded ${result.uploaded} file(s) to directory: ${directory}`)
      
      // Refresh the monitored files list
      loadMonitoredSites()
    } else {
      const error = await response.json()
      throw new Error(error.message || 'Upload failed')
    }
    
  } catch (error) {
    console.error('Error processing files:', error)
    showFlashMessage('error', `Failed to process files: ${error.message}`)
  } finally {
    isScanning.value = false
  }
}

const loadMonitoredSites = async () => {
  isLoadingSites.value = true
  try {
    const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/api/sites`)
    if (response.ok) {
      const sitesData = await response.json()
      
      // First, set sites without details for immediate display
      monitoredSites.value = sitesData.map(site => ({ 
        ...site, 
        pages: [], 
        totalPages: 0,
        loading: true 
      }))
      
      // Then load details for each site with controlled concurrency
      const BATCH_SIZE = 3 // Process 3 sites at a time to avoid overwhelming the server
      
      for (let i = 0; i < sitesData.length; i += BATCH_SIZE) {
        const batch = sitesData.slice(i, i + BATCH_SIZE)
        
        await Promise.all(batch.map(async (site, batchIndex) => {
          const globalIndex = i + batchIndex
          try {
            // Pass source type parameter based on the site type
            const sourceType = site.type || 'site'
            const detailResponse = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/api/sites/${site.id}?source_type=${sourceType}`)
            if (detailResponse.ok) {
              const details = await detailResponse.json()
              // Update the specific site in the array
              monitoredSites.value[globalIndex] = { 
                ...site, 
                ...details, 
                totalPages: details.totalPages || details.pages?.length || 0,
                changedCount: details.changedCount || 0,
                loading: false
              }
            } else {
              monitoredSites.value[globalIndex] = { 
                ...site, 
                pages: [], 
                totalPages: 0,
                changedCount: 0,
                loading: false,
                error: `HTTP ${detailResponse.status}`
              }
            }
          } catch (error) {
            console.error(`Failed to load details for site ${site.id}:`, error)
            monitoredSites.value[globalIndex] = { 
              ...site, 
              pages: [], 
              totalPages: 0,
              loading: false,
              error: error.message
            }
          }
        }))
        
        // Small delay between batches to prevent server overload
        if (i + BATCH_SIZE < sitesData.length) {
          await new Promise(resolve => setTimeout(resolve, 100))
        }
      }
    } else {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }
  } catch (error) {
    console.error('Failed to load monitored sites:', error)
    showFlashMessage('error', `Failed to load monitored sites: ${error.message}`)
  } finally {
    isLoadingSites.value = false
  }
}

const getKnowledgeBaseName = (kbId) => {
  if (!kbId) return 'None'
  const kb = knowledgeBases.value.find(k => k.id === kbId)
  return kb ? kb.name : 'Unknown'
}

const updateDefaultKnowledgeBase = async (site) => {
  try {
    const endpoint = site.type === 'directory'
      ? `${process.env.VUE_APP_AI_BASE_URL}/api/directories/${site.id}/default-knowledge-base`
      : `${process.env.VUE_APP_AI_BASE_URL}/api/sites/${site.id}/default-knowledge-base`

    console.log('Updating KB for site:', { id: site.id, type: site.type, endpoint, kb_id: site.default_knowledge_base_id })

    const response = await fetch(endpoint, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        default_knowledge_base_id: site.default_knowledge_base_id
      })
    })

    if (response.ok) {
      await response.json()
      const kbName = getKnowledgeBaseName(site.default_knowledge_base_id)
      showFlashMessage('success', `Default knowledge base updated to: ${kbName}`)

      // If this is the currently selected site for documents, update it so the file dropdowns reflect the change
      if (selectedSiteForDocuments.value && selectedSiteForDocuments.value.id === site.id) {
        selectedSiteForDocuments.value.default_knowledge_base_id = site.default_knowledge_base_id
      }
    } else {
      const errorText = await response.text()
      console.error('Update KB failed:', { status: response.status, statusText: response.statusText, body: errorText })
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }
  } catch (error) {
    console.error('Failed to update default knowledge base:', error)
    showFlashMessage('error', `Failed to update default knowledge base: ${error.message}`)
  }
}

const getEffectiveKnowledgeBase = (page) => {
  // If page has its own KB set, use that
  if (page.knowledge_base_id) {
    return page.knowledge_base_id
  }
  // Otherwise, return empty string to show "Use Default" option (matches option value="")
  return ''
}

const updatePageKnowledgeBase = async (page, newValue) => {
  try {
    const isFile = selectedSiteForDocuments.value?.type === 'directory'
    const endpoint = isFile
      ? `${process.env.VUE_APP_AI_BASE_URL}/api/files/${page.id}/knowledge-base`
      : `${process.env.VUE_APP_AI_BASE_URL}/api/pages/${page.id}/knowledge-base`

    // Convert empty string to null for API
    const kbValue = newValue === '' ? null : newValue

    console.log('Updating page KB:', { page_id: page.id, isFile, endpoint, kb_value: kbValue })

    const response = await fetch(endpoint, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        default_knowledge_base_id: kbValue
      })
    })

    if (response.ok) {
      await response.json()
      // Update the page object locally (store as null or the actual KB id)
      page.knowledge_base_id = kbValue

      const kbName = kbValue
        ? getKnowledgeBaseName(kbValue)
        : (selectedSiteForDocuments.value.default_knowledge_base_id
            ? `Default (${getKnowledgeBaseName(selectedSiteForDocuments.value.default_knowledge_base_id)})`
            : 'None')
      showFlashMessage('success', `Knowledge base set to: ${kbName}`)
    } else {
      const errorText = await response.text()
      console.error('Update page KB failed:', { status: response.status, statusText: response.statusText, body: errorText })
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }
  } catch (error) {
    console.error('Failed to update knowledge base:', error)
    showFlashMessage('error', `Failed to update knowledge base: ${error.message}`)
  }
}

const updateOnProcessAction = async (page) => {
  try {
    const isFile = selectedSiteForDocuments.value?.type === 'directory'
    const endpoint = isFile
      ? `${process.env.VUE_APP_AI_BASE_URL}/api/files/${page.id}/on-process-action`
      : `${process.env.VUE_APP_AI_BASE_URL}/api/pages/${page.id}/on-process-action`

    const response = await fetch(endpoint, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        on_process_action: page.on_process_action
      })
    })

    if (response.ok) {
      await response.json()
      const actionLabel = page.on_process_action === 'replace' ? 'Replace' : 'Do Nothing'
      showFlashMessage('success', `On process action set to: ${actionLabel}`)
    } else {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }
  } catch (error) {
    console.error('Failed to update on process action:', error)
    showFlashMessage('error', `Failed to update on process action: ${error.message}`)
  }
}

const quickScanSite = async (site) => {
  isScanning.value = true
  
  try {
    const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/api/scan`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        url: site.root_url,
        maxConcurrency: crawlOptions.value.maxConcurrency,
        quickScan: true
      })
    })
    
    const result = await response.json()
    
    if (response.ok) {
      showFlashMessage('success', result.message || 'Quick scan completed')
      await loadMonitoredSites()
    } else {
      throw new Error(result.error || 'Scan failed')
    }
  } catch (error) {
    console.error('Quick scan error:', error)
    showFlashMessage('error', `Quick scan failed: ${error.message}`)
  } finally {
    isScanning.value = false
  }
}

// Site-specific document view methods
const viewSiteDocuments = (site) => {
  selectedSiteForDocuments.value = site
  currentPage.value = 1 // Reset to first page when viewing new site
}

const closeSiteDocuments = () => {
  selectedSiteForDocuments.value = null
  currentPage.value = 1
}

// Get count of files configured for Replace
const getReplaceConfiguredCount = () => {
  if (!selectedSiteForDocuments.value) return 0
  return allPages.value.filter(page =>
    page.site_id === selectedSiteForDocuments.value.id &&
    page.on_process_action === 'replace'
  ).length
}

// Batch process all files configured for Replace
const batchProcessReplace = async () => {
  if (!selectedSiteForDocuments.value) return

  const replaceFiles = allPages.value.filter(page =>
    page.site_id === selectedSiteForDocuments.value.id &&
    page.on_process_action === 'replace'
  )

  if (replaceFiles.length === 0) {
    showFlashMessage('info', 'No files configured for Replace')
    return
  }

  const confirmed = confirm(
    `This will process ${replaceFiles.length} file(s) configured for Replace.\n\n` +
    `Old articles will be removed from Genesys but kept in history.\n` +
    `New articles will be generated and imported.\n\n` +
    `Continue?`
  )

  if (!confirmed) return

  try {
    isBatchProcessing.value = true
    batchProgress.value = {
      current: 0,
      total: replaceFiles.length,
      currentFile: ''
    }

    const company = localStorage.getItem('companyID') || 'default'
    const sourceType = selectedSiteForDocuments.value.type === 'directory' ? 'directory' : 'site'
    const sourceId = selectedSiteForDocuments.value.id

    showFlashMessage('info', `Starting batch process for ${replaceFiles.length} file(s)...`)

    // Call backend batch process endpoint
    const response = await fetch(
      `${process.env.VUE_APP_AI_BASE_URL}/batch-process-replace?company=${company}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          source_type: sourceType,
          source_id: sourceId,
          files: replaceFiles.map(f => ({
            id: f.id,
            url: f.url,
            title: f.title,
            knowledge_base_id: f.knowledge_base_id || selectedSiteForDocuments.value.default_knowledge_base_id
          }))
        })
      }
    )

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()

    let reading = true
    while (reading) {
      const { value, done } = await reader.read()
      if (done) {
        reading = false
        break
      }

      const chunk = decoder.decode(value)
      const lines = chunk.split('\n')

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6))

            if (data.type === 'progress') {
              batchProgress.value.current = data.current || 0
              batchProgress.value.currentFile = data.file || ''
              currentStatus.value = data.message || ''
            } else if (data.type === 'status') {
              currentStatus.value = data.message || ''
              progressLog.value.push(data.message)
            } else if (data.type === 'complete') {
              showFlashMessage('success', data.message || 'Batch process completed successfully!')
              await loadMonitoredSites()

              // Reload the current site's pages
              if (selectedSiteForDocuments.value) {
                const reloadedSite = monitoredSites.value.find(s => s.id === selectedSiteForDocuments.value.id)
                if (reloadedSite) {
                  selectedSiteForDocuments.value = reloadedSite
                }
              }
            } else if (data.type === 'error') {
              showFlashMessage('error', data.message || 'An error occurred during batch processing')
            }
          } catch (e) {
            console.error('Error parsing SSE data:', e)
          }
        }
      }
    }

  } catch (error) {
    console.error('Batch process error:', error)
    showFlashMessage('error', `Batch process failed: ${error.message}`)
  } finally {
    isBatchProcessing.value = false
    batchProgress.value = {
      current: 0,
      total: 0,
      currentFile: ''
    }
  }
}

// Pagination methods
const goToPage = (pageNumber) => {
  if (pageNumber >= 1 && pageNumber <= totalPages.value) {
    currentPage.value = pageNumber
  }
}

const resetToFirstPage = () => {
  currentPage.value = 1
}

const updateScanInterval = async (site) => {
  try {
    // Use different endpoint based on whether it's a site or directory
    const endpoint = site.type === 'directory'
      ? `${process.env.VUE_APP_AI_BASE_URL}/api/directories/${site.id}/scan-interval`
      : `${process.env.VUE_APP_AI_BASE_URL}/api/sites/${site.id}/scan-interval`

    const response = await fetch(endpoint, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        scan_interval_seconds: parseInt(site.scan_interval_seconds)
      })
    })

    if (response.ok) {
      const result = await response.json()
      const intervalText = site.scan_interval_seconds == 0 ? 'Disabled' :
                          site.scan_interval_seconds == 60 ? 'Every 1 minute' :
                          site.scan_interval_seconds == 300 ? 'Every 5 minutes' :
                          site.scan_interval_seconds == 1800 ? 'Every 30 minutes' :
                          site.scan_interval_seconds == 86400 ? 'Every day' :
                          site.scan_interval_seconds == 604800 ? 'Every week' :
                          site.scan_interval_seconds == 2592000 ? 'Every month' :
                          `${site.scan_interval_seconds} seconds`
      showFlashMessage('success', `Scan interval updated to ${intervalText}`)
      console.log('Scan interval updated:', result)
    } else {
      const error = await response.json()
      throw new Error(error.detail || 'Failed to update scan interval')
    }
  } catch (error) {
    console.error('Error updating scan interval:', error)
    showFlashMessage('error', `Failed to update scan interval: ${error.message}`)
    // Reload sites to revert the dropdown to the actual saved value
    await loadMonitoredSites()
  }
}

const removeSite = async (siteId) => {
  const site = monitoredSites.value.find(s => s.id === siteId)
  if (!site) return
  
  const itemType = site.type === 'directory' ? 'directory' : 'site'
  const itemName = site.type === 'directory' ? (site.directory_path || site.root_url) : site.root_url
  const itemsToDelete = site.type === 'directory' 
    ? `• All monitored files\n• All file snapshots\n• All change detection data`
    : `• All discovered pages\n• All monitoring configurations\n• All change detection data`
  
  const userInput = prompt(
    `⚠️ DANGER: Delete all data for this ${itemType}?\n\n` +
    `This will permanently remove:\n` +
    `${itemsToDelete}\n\n` +
    `To confirm, type the complete ${itemType} path exactly:\n` +
    `${itemName}\n\n` +
    `Type the ${itemType} path to confirm deletion:`
  )
  
  if (!userInput || userInput.trim() !== itemName) {
    if (userInput !== null) {
      alert(`${itemType.charAt(0).toUpperCase() + itemType.slice(1)} path does not match. Deletion cancelled for safety.`)
    }
    return
  }
  
  try {
    // Use different endpoint based on site type
    const endpoint = site.type === 'directory' 
      ? `${process.env.VUE_APP_AI_BASE_URL}/api/directories/${siteId}` 
      : `${process.env.VUE_APP_AI_BASE_URL}/api/sites/${siteId}`
    
    const response = await fetch(endpoint, { method: 'DELETE' })
    const result = await response.json()
    
    if (response.ok) {
      const itemType = site.type === 'directory' ? 'Directory' : 'Site'
      showFlashMessage('success', `${itemType} removed from monitoring`)
      await loadMonitoredSites()
    } else {
      throw new Error(result.error || 'Failed to remove site')
    }
  } catch (error) {
    console.error('Error removing site:', error)
    showFlashMessage('error', `Failed to remove site: ${error.message}`)
  }
}

const togglePageMonitoring = async (page, monitored) => {
  try {
    await togglePageMonitoringAPI(page, monitored)
    page.monitored = monitored ? 1 : 0
  } catch (error) {
    console.error('Error updating page monitoring:', error)
    showFlashMessage('error', `Failed to update monitoring: ${error.message}`)
  }
}

// Global page actions for All Pages modal
const selectAllGlobalPages = async () => {
  const pagesToUpdate = []
  
  // Collect all pages that need to be enabled
  for (const site of monitoredSites.value) {
    if (site.pages) {
      for (const page of site.pages) {
        if (page.monitored === 0) {
          pagesToUpdate.push(page)
        }
      }
    }
  }
  
  if (pagesToUpdate.length === 0) return
  
  // Update pages in batches for better performance
  const BATCH_SIZE = 10
  for (let i = 0; i < pagesToUpdate.length; i += BATCH_SIZE) {
    const batch = pagesToUpdate.slice(i, i + BATCH_SIZE)
    await Promise.all(batch.map(async page => {
      try {
        // Update the UI immediately for better UX
        page.monitored = 1
        await togglePageMonitoringAPI(page, true)
      } catch (error) {
        // Revert on error
        page.monitored = 0
        console.error(`Failed to enable monitoring for page ${page.id}:`, error)
      }
    }))
  }
}

const clearAllGlobalPages = async () => {
  const pagesToUpdate = []
  
  // Collect all pages that need to be disabled
  for (const site of monitoredSites.value) {
    if (site.pages) {
      for (const page of site.pages) {
        if (page.monitored !== 0) {
          pagesToUpdate.push(page)
        }
      }
    }
  }
  
  if (pagesToUpdate.length === 0) return
  
  // Update pages in batches for better performance
  const BATCH_SIZE = 10
  for (let i = 0; i < pagesToUpdate.length; i += BATCH_SIZE) {
    const batch = pagesToUpdate.slice(i, i + BATCH_SIZE)
    await Promise.all(batch.map(async page => {
      try {
        // Update the UI immediately for better UX
        page.monitored = 0
        await togglePageMonitoringAPI(page, false)
      } catch (error) {
        // Revert on error
        page.monitored = 1
        console.error(`Failed to disable monitoring for page ${page.id}:`, error)
      }
    }))
  }
}

// Separate API call function for better reusability
const togglePageMonitoringAPI = async (page, monitored) => {
  const endpoint = page.file_type ? `/api/files/${page.id}/monitoring` : `/api/pages/${page.id}/monitoring`
  const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}${endpoint}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ monitored })
  })
  
  if (!response.ok) {
    const result = await response.json()
    throw new Error(result.error || 'Failed to update monitoring')
  }
  
  return response.json()
}

const processPageContent = async (page) => {
  // Set this page as processing
  page.processingState = 'processing'
  isProcessing.value = true
  processedData.value = null
  successMessage.value = ''
  errorMessage.value = ''
  currentStatus.value = 'Processing page content...'
  progressInfo.value = { current_step: null, total_steps: null }
  progressLog.value = []
  const company = localStorage.getItem("companyID") || "default"

  try {
    // Check if this is a file (has file_type) or a website URL
    if (page.file_type) {
      // This is a file - use file processing endpoint
      const streamUrl = process.env.VUE_APP_AI_BASE_URL + `/process-monitored-files-stream?company=${encodeURIComponent(company)}`
      const requestBody = JSON.stringify({ file_ids: [page.id] }) // Send file ID for file processing

      await startStreamingJSON(streamUrl, requestBody)

      if (processedData.value) {
        successMessage.value = `Successfully processed file: ${page.title}`
        // Update local page object immediately
        page.changed_at = null
        page.processed_at = new Date().toISOString()
        page.processingState = 'completed'

        // Store info before reload
        const pageId = page.id
        const siteId = selectedSiteForDocuments.value?.id
        const processingState = 'completed'

        // Refresh the monitored sites list to update changed_at flags
        await loadMonitoredSites()

        // Restore processing state after reload
        if (siteId) {
          const reloadedSite = monitoredSites.value.find(s => s.id === siteId)
          if (reloadedSite?.pages) {
            const reloadedPage = reloadedSite.pages.find(p => p.id === pageId)
            if (reloadedPage) {
              reloadedPage.processingState = processingState
            }
            // Update selectedSiteForDocuments reference
            selectedSiteForDocuments.value = reloadedSite
          }
        }
      }
    } else {
      // This is a website URL - use URL processing endpoint
      const streamUrl = process.env.VUE_APP_AI_BASE_URL + `/process-monitored-content-stream?company=${encodeURIComponent(company)}`
      const requestBody = JSON.stringify({
        urls: [page.url],
        source_info: `Knowledge Inventory - Single Page - ${page.url}`,
        auto_upload: true
      })

      await startStreamingJSON(streamUrl, requestBody)

      if (processedData.value) {
        successMessage.value = `Successfully processed content from ${page.url}`
        // Update local page object immediately
        page.changed_at = null
        page.processed_at = new Date().toISOString()
        page.processingState = 'completed'

        // Store info before reload
        const pageId = page.id
        const siteId = selectedSiteForDocuments.value?.id
        const processingState = 'completed'

        // Refresh the monitored sites list to update changed_at flags
        await loadMonitoredSites()

        // Restore processing state after reload
        if (siteId) {
          const reloadedSite = monitoredSites.value.find(s => s.id === siteId)
          if (reloadedSite?.pages) {
            const reloadedPage = reloadedSite.pages.find(p => p.id === pageId)
            if (reloadedPage) {
              reloadedPage.processingState = processingState
            }
            // Update selectedSiteForDocuments reference
            selectedSiteForDocuments.value = reloadedSite
          }
        }
      }
    }

  } catch (error) {
    console.error('Error processing page content:', error)
    errorMessage.value = error.message || 'Failed to process page content'
    currentStatus.value = 'Error: ' + error.message
    addProgressLog(`Error: ${error.message}`, 'error')
    page.processingState = 'idle'
  } finally {
    isProcessing.value = false
  }
}

const showPageArticles = async (page) => {
  // Navigate to article management screen with the newly generated articles
  if (!processedData.value?.json?.documents) {
    showFlashMessage('error', 'No generated articles available to manage')
    return
  }

  // Determine which KB to use: page's KB > source's default KB > dropdown selection
  let kbToUse = selectedKnowledgeBase.value // Start with dropdown selection

  // Get the effective KB for this page
  const pageKbId = page.knowledge_base_id || selectedSiteForDocuments.value?.default_knowledge_base_id

  if (pageKbId) {
    // Load the full KB object from the knowledgeBases list
    const pageKb = knowledgeBases.value.find(kb => kb.id === pageKbId)
    if (pageKb) {
      kbToUse = pageKb
      console.log('Using page/source KB:', pageKb.name)
    }
  }

  // Determine the source info for historical articles lookup
  let sourceInfo
  if (selectedSiteForDocuments.value?.type === 'directory' && page.title) {
    // This is a file - use "Files: filename" format
    sourceInfo = `Files: ${page.title}`
  } else {
    // This is a URL
    sourceInfo = page.url
  }

  // Add source_info to processedData for the management page
  const dataToStore = {
    ...processedData.value,
    source_info: sourceInfo
  }

  // Store complete processedData for the management page
  localStorage.setItem('processedData', JSON.stringify(dataToStore))

  // Also store approval states to maintain state across page navigation
  localStorage.setItem('articleApprovalStates', JSON.stringify(articleApprovalStates.value))

  // Store legacy format for compatibility (if needed)
  localStorage.setItem('articleManagementData', JSON.stringify({
    articles: processedData.value.json.documents,
    sourceUrl: sourceInfo,
    approvedArticles: approvedArticles.value,
    selectedKnowledgeBase: kbToUse,
    generatedAt: new Date().toISOString()
  }))

  // Navigate to the article management page
  router.push('/article_management')
}

const showPageHistory = (page) => {
  // For files, construct the source URL format used when processing: "Files: filename"
  // For regular pages, use the URL as-is
  let sourceUrl = page.url
  if (selectedSiteForDocuments.value?.type === 'directory' && page.title) {
    // This is a file - use "Files: filename" format
    sourceUrl = `Files: ${page.title}`
  }

  selectedUrl.value = sourceUrl
  showArticlesModal.value = true
  loadModalArticlesByUrl(sourceUrl)
}

const scanSinglePage = async (page) => {
  if (isScanning.value) return

  isScanning.value = true
  try {
    // Determine if this is a file or a URL
    const isFile = selectedSiteForDocuments.value?.type === 'directory'
    const endpoint = isFile ? '/api/scan-file' : '/api/scan-page'
    const requestBody = isFile
      ? { file_path: page.url }  // For files, 'url' field actually contains file_path
      : { url: page.url }

    const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody)
    })

    const result = await response.json()
    if (response.ok) {
      if (result.changed) {
        page.changed_at = new Date().toISOString()
        const displayPath = isFile ? page.url.split('/').pop() : page.url
        showFlashMessage('info', `📄 ${isFile ? 'File' : 'Page'} updated: ${displayPath}`)
      } else {
        const displayPath = isFile ? page.url.split('/').pop() : page.url
        showFlashMessage('success', `✅ No changes detected: ${displayPath}`)
      }
    } else {
      throw new Error(result.detail || result.message || 'Scan failed')
    }
  } catch (error) {
    console.error('Single scan error:', error)
    showFlashMessage('error', `Scan failed: ${error.message}`)
  } finally {
    isScanning.value = false
  }
}

const deletePage = async (page) => {
  if (!page || !page.id) return

  const isFile = selectedSiteForDocuments.value?.type === 'directory'
  const displayPath = isFile ? page.url.split('/').pop() : truncateUrl(page.url, 60)
  const itemType = isFile ? 'file' : 'page'

  const confirmed = confirm(
    `⚠️ Delete this ${itemType}?\n\n` +
    `${itemType.charAt(0).toUpperCase() + itemType.slice(1)}: ${displayPath}\n\n` +
    `This will permanently remove:\n` +
    `• This ${itemType} from monitoring\n` +
    `• All change detection data\n` +
    `• All generated articles\n` +
    `• All history records\n\n` +
    `This action cannot be undone. Continue?`
  )

  if (!confirmed) return

  try {
    const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/api/pages/${page.id}`, {
      method: 'DELETE'
    })

    const result = await response.json()

    if (response.ok) {
      showFlashMessage('success', `${itemType.charAt(0).toUpperCase() + itemType.slice(1)} deleted successfully`)

      // Reload the site's data to refresh the pages list
      if (selectedSiteForDocuments.value) {
        const siteId = selectedSiteForDocuments.value.id
        const sourceType = selectedSiteForDocuments.value.type || 'site'

        try {
          const detailResponse = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/api/sites/${siteId}?source_type=${sourceType}`)
          if (detailResponse.ok) {
            const details = await detailResponse.json()

            // Update the site in monitoredSites array
            const siteIndex = monitoredSites.value.findIndex(s => s.id === siteId)
            if (siteIndex !== -1) {
              monitoredSites.value[siteIndex] = {
                ...monitoredSites.value[siteIndex],
                ...details,
                totalPages: details.totalPages || details.pages?.length || 0,
                changedCount: details.changedCount || 0,
                loading: false
              }

              // Update selectedSiteForDocuments to trigger reactive updates
              selectedSiteForDocuments.value = monitoredSites.value[siteIndex]
            }
          }
        } catch (reloadError) {
          console.error('Failed to reload site data:', reloadError)
        }
      }
    } else {
      throw new Error(result.detail || result.message || 'Delete failed')
    }
  } catch (error) {
    console.error('Delete page error:', error)
    showFlashMessage('error', `Failed to delete ${itemType}: ${error.message}`)
  }
}

const getMonitoredCount = (site) => {
  if (!site.pages) return 0
  return site.pages.filter(page => page.monitored !== 0).length
}

const getChangedCount = (site) => {
  if (!site.pages) return 0
  return site.pages.filter(page => page.changed_at).length
}

const truncateUrl = (url, maxLength = 50) => {
  if (!url) return ''
  return url.length > maxLength ? url.substring(0, maxLength) + '...' : url
}

const getSiteReachabilityStatus = (site) => {
  if (site.type === 'directory') {
    // For directories, check if we have a valid directory path configured
    const directoryPath = site.directory_path || site.root_url
    if (directoryPath && directoryPath.length > 0 && directoryPath !== '/') {
      // Additional checks for common directory path patterns
      const isAbsolutePath = directoryPath.startsWith('/') || /^[A-Za-z]:/.test(directoryPath)
      const hasValidChars = !/[<>:"|?*]/.test(directoryPath.replace(/^[A-Za-z]:/, ''))
      
      if (isAbsolutePath && hasValidChars) {
        return { isReachable: true, status: 'Directory Ready' }
      } else {
        return { isReachable: false, status: 'Invalid Path' }
      }
    }
    return { isReachable: false, status: 'No Directory' }
  } else {
    // For URLs, check if it looks like a valid URL
    try {
      const url = new URL(site.root_url)
      if (url.protocol === 'http:' || url.protocol === 'https:') {
        return { isReachable: true, status: 'URL Ready' }
      }
      return { isReachable: false, status: 'Invalid Protocol' }
    } catch {
      return { isReachable: false, status: 'Invalid URL' }
    }
  }
}

const formatRelativeTime = (dateString) => {
  const now = new Date()
  const date = new Date(dateString + (dateString.includes('Z') ? '' : 'Z'))
  const diffMs = now - date
  const diffSecs = Math.floor(diffMs / 1000)
  const diffMins = Math.floor(diffSecs / 60)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffSecs < 60) return diffSecs <= 10 ? 'Just now' : `${diffSecs}s ago`
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return new Date(dateString).toLocaleDateString()
}

const formatDateTime = (dateString) => {
  if (!dateString) return ''
  // Display datetime as-is from database without timezone conversion
  // Remove 'Z' if present and parse as local time
  const dateStr = dateString.replace('Z', '')
  const [datePart, timePart] = dateStr.split('T')
  const [year, month, day] = datePart.split('-')
  const [hour, minute] = timePart.split(':')

  const date = new Date(year, month - 1, day, hour, minute)
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  })
}

const stopAutoMonitoring = () => {
  if (monitoringInterval.value) {
    clearInterval(monitoringInterval.value)
    monitoringInterval.value = null
  }
  
  autoMonitoringEnabled.value = false
  showFlashMessage('info', '🔴 Automatic monitoring stopped')
  console.log('🔴 Auto-monitoring disabled')
}

onMounted( async () => {
  try {
    await loadAIConfiguration()
    // Load knowledge bases on component mount
    await loadKnowledgeBases()
    // Load monitored sites for knowledge inventory
    await loadMonitoredSites()
  } finally {
    // Set loading to false after initialization
    setTimeout(() => {
      isInitialLoading.value = false
    }, 1000) // Small delay to show the loading state
  }
  // Initialize site tabs
  initializeSiteTabs()
})

// Helper functions for article preview modal
const getContentField = (content, path) => {
  if (!content) return null
  
  const keys = path.split('.')
  let current = content
  
  for (const key of keys) {
    if (current && typeof current === 'object' && key in current) {
      current = current[key]
    } else {
      return null
    }
  }
  
  return current
}

const renderArticleContent = (articleData) => {
  console.log('=== RENDER CONTENT DEBUG ===')
  console.log('Input articleData:', articleData)
  console.log('Type:', typeof articleData)
  
  if (!articleData) {
    console.log('No articleData provided')
    return 'No content available'
  }
  
  try {
    let html = ''
    
    // Check if articleData has published or draft structure
    let contentData = articleData
    if (articleData.published) {
      console.log('Using published content')
      contentData = articleData.published
    } else if (articleData.draft) {
      console.log('Using draft content')
      contentData = articleData.draft
    }
    
    console.log('Content data:', contentData)
    console.log('Content data keys:', Object.keys(contentData || {}))
    
    const blocks = contentData.variations?.[0]?.body?.blocks || []
    console.log('Found blocks:', blocks.length)
    console.log('Blocks:', blocks)
    
    if (blocks.length === 0) {
      // Show debug info if no blocks found
      const debugInfo = `
        <div style="background: #f3f4f6; padding: 16px; border-radius: 8px; margin: 16px 0;">
          <h4 style="color: #374151; margin: 0 0 12px 0;">Debug Info:</h4>
          <p><strong>Has published:</strong> ${!!articleData.published}</p>
          <p><strong>Has draft:</strong> ${!!articleData.draft}</p>
          <p><strong>Has variations:</strong> ${!!(contentData.variations?.length)}</p>
          <p><strong>Structure:</strong> <pre style="background: white; padding: 8px; border-radius: 4px; font-size: 12px; overflow-x: auto;">${JSON.stringify(contentData, null, 2)}</pre></p>
        </div>
      `
      return debugInfo
    }
    
    for (const block of blocks) {
      console.log('Processing block:', block.type, block)
      
      if (block.type === 'Paragraph') {
        const fontType = block.paragraph?.properties?.fontType || 'Paragraph'
        let tag = 'p'
        
        if (fontType === 'Heading3') tag = 'h3'
        else if (fontType === 'Heading4') tag = 'h4'
        
        let content = ''
        const textBlocks = block.paragraph?.blocks || []
        
        for (const textBlock of textBlocks) {
          if (textBlock.type === 'Text') {
            let text = textBlock.text?.text || ''
            
            // Apply formatting
            if (textBlock.text?.marks?.includes('Bold')) {
              text = `<strong>${text}</strong>`
            }
            
            // Handle hyperlinks
            if (textBlock.text?.hyperlink) {
              text = `<a href="${textBlock.text.hyperlink}" target="_blank" rel="noopener">${text}</a>`
            }
            
            content += text
          }
        }
        
        // Only add non-empty content
        if (content.trim()) {
          html += `<${tag}>${content}</${tag}>`
        }
      }
      else if (block.type === 'OrderedList' || block.type === 'UnorderedList') {
        const listTag = block.type === 'OrderedList' ? 'ol' : 'ul'
        html += `<${listTag}>`
        
        const listItems = block.list?.blocks || []
        for (const item of listItems) {
          const itemBlocks = item.blocks || []
          let itemContent = ''
          
          for (const textBlock of itemBlocks) {
            if (textBlock.type === 'Text') {
              itemContent += textBlock.text?.text || ''
            }
          }
          
          if (itemContent.trim()) {
            html += `<li>${itemContent}</li>`
          }
        }
        
        html += `</${listTag}>`
      }
    }
    
    console.log('Generated HTML:', html)
    return html || 'No renderable content found'
    
  } catch (error) {
    console.error('Error rendering article content:', error)
    return `<div style="color: #dc2626; background: #fef2f2; padding: 16px; border-radius: 8px;">
              <strong>Error displaying content:</strong> ${error.message}<br>
              <small>Check console for details</small>
            </div>`
  }
}

// Cleanup monitoring when component unmounts
onBeforeUnmount(() => {
  stopAutoMonitoring()
})
</script>

<style scoped>
/* Approval System Styles */
.approved-count {
  color: #059669;
  font-weight: 600;
}

.approval-summary-section {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  margin: 16px 0;
}

.approval-summary-section h5 {
  margin: 0 0 12px 0;
  color: #334155;
  font-size: 14px;
  font-weight: 600;
}

.approval-stats {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.approval-stat-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
}

.approval-stat-item.approved {
  background: #dcfce7;
  color: #166534;
  border: 1px solid #bbf7d0;
}

.approval-stat-item.pending {
  background: #fef3c7;
  color: #92400e;
  border: 1px solid #fde68a;
}

.approval-stat-item.rejected {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
}

.stat-icon {
  font-size: 16px;
}

.stat-text {
  font-size: 13px;
}

.bulk-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.btn-approve-all {
  background-color: #059669;
  color: white;
  padding: 7px 16px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  border: 0;
  min-width: 70px;
  transition: all 0.2s ease;
  box-shadow: 2px 4px #ccc;
  margin: 0 5px;
}

.btn-approve-all:hover:not(:disabled) {
  background-color: #047857;
}

.btn-reject-all {
  background-color: #dc2626;
  color: white;
  padding: 7px 16px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  border: 0;
  min-width: 70px;
  transition: all 0.2s ease;
  box-shadow: 2px 4px #ccc;
  margin: 0 5px;
}

.btn-reject-all:hover:not(:disabled) {
  background-color: #b91c1c;
}

.btn-reset-all {
  background-color: #6b7280;
  color: white;
  padding: 7px 16px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  border: 0;
  min-width: 70px;
  transition: all 0.2s ease;
  box-shadow: 2px 4px #ccc;
  margin: 0 5px;
}

.btn-reset-all:hover:not(:disabled) {
  background-color: #4b5563;
}

.approved-articles-info {
  background: #dcfce7;
  border: 1px solid #bbf7d0;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 12px;
}

.approved-count-display {
  color: #166534;
  font-weight: 500;
  font-size: 14px;
}

.no-approved-warning {
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 6px;
  padding: 12px;
  margin: 16px 0;
}

.approved-history-section {
  margin-bottom: 20px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.history-results {
  margin-top: 16px;
}

.history-results h6 {
  margin-bottom: 12px;
  color: #374151;
  font-weight: 600;
}

.history-list {
  max-height: 200px;
  overflow-y: auto;
}

.history-item {
  padding: 8px 12px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  margin-bottom: 8px;
}

.article-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.status-badge {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.status-badge.published {
  background: #dcfce7;
  color: #166534;
}

.status-badge.draft {
  background: #fef3c7;
  color: #92400e;
}

.article-meta {
  color: #6b7280;
}

.history-buttons {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.comparison-results {
  margin-top: 20px;
  padding: 16px;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
}

.comparison-summary {
  margin-bottom: 16px;
}

.summary-stats {
  display: flex;
  gap: 16px;
}

.stat {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

.stat.new {
  background: #dcfce7;
  color: #166534;
}

.stat.updated {
  background: #fef3c7;
  color: #92400e;
}

.stat.unchanged {
  background: #e5e7eb;
  color: #374151;
}

.comparison-list {
  max-height: 300px;
  overflow-y: auto;
  margin-bottom: 16px;
}

.comparison-item {
  padding: 12px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  margin-bottom: 8px;
}

.comparison-item.new {
  border-left: 4px solid #22c55e;
}

.comparison-item.updated {
  border-left: 4px solid #f59e0b;
}

.comparison-item.unchanged {
  border-left: 4px solid #9ca3af;
}

.article-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.comparison-status {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 12px;
}

.comparison-status.new {
  background: #dcfce7;
  color: #166534;
}

.comparison-status.updated {
  background: #fef3c7;
  color: #92400e;
}

.comparison-status.unchanged {
  background: #e5e7eb;
  color: #374151;
}

.existing-info {
  color: #6b7280;
}

.comparison-actions {
  display: flex;
  gap: 12px;
  border-top: 1px solid #e5e7eb;
  padding-top: 12px;
}

/* All Approved Articles Management Styles */
.view-all-articles-section {
  margin-top: 40px;
  padding: 24px;
  background: #fafbfc;
  border-radius: 12px;
  border: 1px solid #e1e5e9;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
}

.section-header h4 {
  margin: 0 0 8px 0;
  color: #1a202c;
  font-size: 20px;
}

.section-header p {
  margin: 0;
  color: #718096;
  font-size: 14px;
}

.management-actions {
  display: flex;
  gap: 12px;
}

.articles-summary {
  display: flex;
  gap: 24px;
  margin-bottom: 20px;
  padding: 16px;
  background: white;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.summary-stat {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  color: #4a5568;
}

.summary-stat i {
  color: #667eea;
}

.website-list {
  space-y: 12px;
}

.website-item {
  background: white;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  margin-bottom: 12px;
}

.website-header {
  padding: 16px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #f7fafc;
  transition: background-color 0.2s;
}

.website-header:hover {
  background: #f7fafc;
}

.website-info {
  flex: 1;
}

.website-domain {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.website-domain i {
  color: #667eea;
}

.website-domain strong {
  font-size: 16px;
  color: #1a202c;
}

.article-count {
  color: #718096;
  font-size: 14px;
  margin-left: 8px;
}

.website-url {
  font-size: 13px;
  color: #a0aec0;
  font-family: monospace;
}

.expand-icon {
  color: #a0aec0;
  transition: transform 0.2s;
}

.articles-list {
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
}

.article-item {
  border-bottom: 1px solid #e2e8f0;
}

.article-item:last-child {
  border-bottom: none;
}

.article-header {
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.article-info h6 {
  margin: 0 0 6px 0;
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
}

.article-meta {
  display: flex;
  gap: 12px;
  align-items: center;
}

.kb-info, .date-info {
  font-size: 12px;
  color: #718096;
}

.article-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  padding: 10px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 40px;
}

.view-btn {
  background: #e2e8f0;
  color: #4a5568;
}

.view-btn:hover {
  background: #cbd5e0;
}

.delete-btn {
  background: #fed7d7;
  color: #c53030;
}

.delete-btn:hover {
  background: #feb2b2;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #718096;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.empty-state h6 {
  margin: 0 0 8px 0;
  font-size: 18px;
  color: #4a5568;
}

.empty-state p {
  margin: 0;
  font-size: 14px;
}

.warning-message {
  color: #92400e;
  font-weight: 500;
  font-size: 14px;
}

.btn-approved-download {
  background-color: #059669;
  color: white;
  padding: 7px 16px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  border: 0;
  min-width: 70px;
  transition: all 0.2s ease;
  box-shadow: 2px 4px #ccc;
  margin: 0 5px;
}

.btn-approved-download:hover {
  background-color: #047857;
}

/* Articles Table with Approval */
.articles-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.articles-header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.approval-summary-mini {
  display: flex;
  gap: 12px;
  align-items: center;
}

.mini-stat {
  font-size: 12px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 4px;
}

.mini-stat.approved {
  background: #dcfce7;
  color: #166534;
}

.mini-stat.pending {
  background: #fef3c7;
  color: #92400e;
}

.mini-stat.rejected {
  background: #fef2f2;
  color: #dc2626;
}

.approval-column {
  width: 100px; /* Changed from 140px to 100px */
}

.approval-cell {
  text-align: center;
  vertical-align: middle;
}

.approval-controls {
  display: flex;
  justify-content: center;
  gap: 4px;
  margin-bottom: 8px;
}

.approval-btn {
  background: none;
  border: 2px solid transparent;
  border-radius: 6px;
  padding: 4px 6px;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.2s ease;
  opacity: 0.6;
}

.approval-btn:hover:not(:disabled) {
  opacity: 1;
  transform: scale(1.1);
}

.approval-btn:disabled {
  cursor: not-allowed;
  opacity: 0.3;
}

.approve-btn.active {
  background: #dcfce7;
  border-color: #059669;
  opacity: 1;
}

.reject-btn.active {
  background: #fef2f2;
  border-color: #dc2626;
  opacity: 1;
}

.reset-btn:hover:not(:disabled) {
  background: #f3f4f6;
  border-color: #6b7280;
}

.approval-status {
  margin-top: 4px;
}

.approval-status-badge {
  font-size: 12px; /* Changed from 10px to 12px */
  padding: 4px 10px; /* Changed from 2px 6px to 4px 10px */
  border-radius: 4px; /* Changed from 3px to 4px */
  font-weight: 500;
  text-align: left;
  white-space: nowrap;
}

.approval-status-badge.approved {
  background: #dcfce7;
  color: #166534;
}

.approval-status-badge.pending {
  background: #fef3c7;
  color: #92400e;
}

.approval-status-badge.rejected {
  background: #fef2f2;
  color: #dc2626;
}

/* Article Row Styling Based on Approval */
.article-row.approval-approved {
  background: #f0fdf4;
  border-left: 4px solid #059669;
}

.article-row.approval-rejected {
  background: #fef2f2;
  border-left: 4px solid #dc2626;
  opacity: 0.7;
}

.article-row.approval-pending {
  background: #fffbeb;
  border-left: 4px solid #f59e0b;
}

/* Enhanced Table Layout */
.title-column {
  width: 25%;
}

.category-column {
  width: 12%;
}

.status-column {
  width: 10%;
}

.visible-column {
  width: 10%;
}

.labels-column {
  width: 15%;
}

.actions-column {
  width: 8%;
}

/* Rest of your existing styles from the original paste.txt file */
.flex-child {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 88vh;
  padding: 0;
  overflow-x: hidden !important;
}

.body-container {
  align-self: unset;
  height: auto;
  width: auto;
  min-height: calc(100vh - 16vh);
}

header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid #e5e7eb;
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}


.config-btn {
  background: #6b7280;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.config-btn:hover {
  background: #4b5563;
  transform: scale(1.05);
}

/* AI Configuration Modal */
.dialog-ai-config {
  width: 600px;
  max-width: 90vw; /* Responsive width */
  border: 1px solid rgba(0, 0, 0, 0.2);
  padding: 0;
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%); /* This centers it perfectly */
  background-color: #fff;
  z-index: 100;
  box-shadow: 2px 4px 20px rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  border: 2px solid #767676;
  margin: 0; /* Remove any margin */
}

.dialog-ai-config .dialog-grid-container {
  display: grid;
  align-items: start;
  column-gap: 16px;
  row-gap: 16px;
  grid-template-columns: 160px 1fr;
  margin-bottom: 20px;
}

.dialog-ai-config .dialog-grid-container label {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  text-align: right;
  margin-bottom: 0;
  padding-top: 8px;
}

.dialog-ai-config .form-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.dialog-ai-config .form-field input,
.dialog-ai-config .form-field select {
  width: 100%;
}

.config-status {
  margin: 16px 0;
}

.form-help-text {
  display: block;
  font-size: 11px;
  color: #6b7280;
  margin: 0;
  font-style: italic;
  line-height: 1.4;
}

.status-message {
  padding: 12px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
}

.status-message.success {
  background: #dcfce7;
  color: #166534;
  border: 1px solid #bbf7d0;
}

.status-message.error {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
}

/* Common dialog styles (if not already present) */
.dialog-backdrop {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: -10;
}

.dialog-body {
  z-index: 10;
  background-color: #fff;
  padding: 2rem;
  border-radius: 8px;
  position: relative;
}

.dialog-header {
  margin: 0;
  margin-bottom: 20px;
  font-size: 24px;
  color: #111827;
  font-weight: 600;
}

.close {
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  font-size: 24px;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  line-height: 1;
}

.close:hover {
  color: #374151;
}

.button-group {
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
}

.button-group button {
  min-width: 120px;
}

header h2 {
  color: #111827;
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}

.genesys-status {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-indicator {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.status-indicator.connected {
  background: #dcfce7;
  color: #166534;
}

.status-indicator.disconnected {
  background: #fef2f2;
  color: #dc2626;
}

/* FIXED ARTICLE EDITOR LAYOUT STYLES */
#centerdiv.article-editor-mode {
  display: flex;
  flex-wrap: nowrap; /* Prevent wrapping to next line */
  gap: 16px;
  align-items: flex-start; /* Align to top */
}

/* Left column in editor mode - basic article info */
.containerdiv.editor-left-column {
  width: 600px; /* Fixed width instead of percentage */
  min-width: 600px;
  flex-shrink: 0; /* Prevent shrinking */
}

/* Right column in editor mode - content editor */
.containerdiv.editor-content-column {
  flex: 1; /* Take remaining space */
  width: auto;
  max-width: none;
}

/* Ensure content editor takes full available width */
.content-editor {
  width: 100%;
}

/* Mobile responsiveness for editor mode */
@media only screen and (max-width: 1200px) {
  #centerdiv.article-editor-mode {
    flex-direction: column; /* Stack vertically on smaller screens */
  }
  
  .containerdiv.editor-left-column {
    width: 100%;
    min-width: auto;
  }
  
  .containerdiv.editor-content-column {
    width: 100%;
  }
}

/* Enhanced Article Editor Styles for Proper Layout */
.blocks-editor-expanded {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 20px;
}

.block-editor-expanded {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  background: #fafafa;
}

.block-header-expanded {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e5e7eb;
}

.block-type-section {
  display: flex;
  align-items: center;
  gap: 8px;
}

.block-type-section label {
  font-weight: 500;
  color: #374151;
  font-size: 14px;
}

.paragraph-editor-expanded, .list-editor-expanded {
  background: white;
  border-radius: 6px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.paragraph-options-expanded {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f3f4f6;
}

.paragraph-options-expanded label {
  font-weight: 500;
  color: #374151;
  font-size: 14px;
}

.text-blocks-expanded {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.text-block-expanded {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 16px;
  background: #fafafa;
}

.text-input-row {
  margin-bottom: 12px;
}

.text-input-expanded {
  width: 100%;
  min-height: 80px;
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-family: inherit;
  font-size: 14px;
  resize: vertical;
  line-height: 1.5;
  background: white;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.text-input-expanded:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.text-options-expanded {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.hyperlink-input-expanded {
  flex: 1;
  min-width: 200px;
  padding: 6px 10px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 13px;
}

.list-items-expanded {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.list-item-editor-expanded {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 12px;
  background: #fafafa;
}

.list-item-content-expanded {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.list-item-number {
  color: #6b7280;
  font-weight: 500;
  min-width: 20px;
  font-size: 14px;
  margin-top: 8px;
}

.list-item-input-expanded {
  flex: 1;
  min-height: 60px;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-family: inherit;
  font-size: 14px;
  resize: vertical;
  background: white;
}

.add-block-section-expanded {
  text-align: center;
  padding: 24px;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  background: #fafafa;
}

/* Article Editor Inline Styles */
.article-editor, .alternatives-editor-section, .content-preview-section, .content-editor, .article-info-section {
  background: #fafbfc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  margin-bottom: 16px;
}

.editor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
}

.edit-field {
  margin-bottom: 16px;
}

.edit-field label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #374151;
  font-size: 14px;
}

.status-options {
  display: flex;
  gap: 16px;
  margin-top: 8px;
}

.radio-option {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}

.radio-option input[type="radio"] {
  margin: 0;
}

.alternatives-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.alternative-item {
  display: flex;
  gap: 8px;
  align-items: center;
}

.editor-actions {
  margin-top: 20px;
  display: flex;
  gap: 8px;
}

.btn-danger-small {
  background-color: #ef4444;
  color: white;
  padding: 4px 8px;
  font-size: 12px;
  min-width: auto;
  border-radius: 12px;
}

.btn-danger-small:hover {
  background-color: #dc2626;
}

.btn-secondary-small {
  background-color: #6c757d;
  color: white;
  padding: 4px 8px;
  font-size: 12px;
  min-width: auto;
  border-radius: 12px;
}

.current-labels-display {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
  padding: 8px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  min-height: 32px;
  align-items: center;
}

.current-label-tag {
  background: #e5e7eb;
  color: #374151;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.no-labels {
  color: #9ca3af;
  font-style: italic;
  font-size: 13px;
}

/* Rest of existing styles continue with your original CSS... */
/* Replace your existing #subheader styles with this */
.subheader-aligned {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  padding: 12px 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin: 16px 8px;
  align-items: center;
}

.subheader-aligned .info-item {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.subheader-aligned .info-label {
  font-weight: 500;
  color: #374151;
  font-size: 14px;
}

.subheader-aligned .info-value {
  font-weight: 600;
  color: #059669;
  font-size: 14px;
}

.subheader-aligned .info-value.approved-count {
  color: #059669;
}

.subheader-aligned .info-value.import-success {
  color: #166534;
}

/* Responsive behavior */
@media (max-width: 768px) {
  .subheader-aligned {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}

.member-info {
  display: flex;
}

#centerdiv {
  padding: 0 8px;
  margin-top: 25px;
  display: flex;
  flex-wrap: nowrap;
  height: auto;
  width: 98%;
  justify-content: space-between;
  gap: 16px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  padding: 24px 16px;
  margin: 25px 8px 20px 8px;
  overflow-x: auto;
}

.containerdiv {
  display: flex;
  flex-flow: column;
  height: fit-content;
  width: calc(33.333% - 16px);
  padding: 8px;
  box-sizing: border-box;
  background: transparent;
}

/* Make the first container (add sites) wider */
#containerdiv1 {
  width: calc(40% - 16px);
  min-width: 400px;
}

/* Adjust other containers to fit properly */
#containerdiv2 {
  width: calc(30% - 16px);
  min-width: 300px;
}

#containerdiv3 {
  width: calc(30% - 16px);
  min-width: 300px;
}

.containerdiv > * {
  padding: 8px;
}

.member-info-content {
  padding: 8px;
}

.processing-section {
  background: #fafbfc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.analysis-section-container {
  background: #fafbfc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.source-section, .options-section {
  background: #fafbfc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  margin-bottom: 16px;
}

.import-success {
  color: #166534;
  font-weight: 600;
}

.import-status-section {
  background: #dcfce7;
  border: 1px solid #bbf7d0;
  border-radius: 6px;
  padding: 12px;
  margin: 12px 0;
}

.import-success-message {
  color: #166534;
  font-weight: 500;
  font-size: 14px;
}

.import-success-message small {
  color: #16a34a;
  font-weight: 400;
  display: block;
  margin-top: 4px;
}

.import-error-section {
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  padding: 12px;
  margin: 12px 0;
}

.import-error-message {
  color: #dc2626;
  font-weight: 500;
  font-size: 14px;
}

.import-info-section {
  background: #f0f9ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 16px;
}

.import-info-section h4 {
  margin: 0 0 12px 0;
  color: #1e40af;
  font-size: 16px;
  font-weight: 600;
}

.import-details {
  padding: 12px;
  border-radius: 6px;
}

.import-details.success {
  background: #dcfce7;
  border: 1px solid #bbf7d0;
}

.import-details.error {
  background: #fef2f2;
  border: 1px solid #fecaca;
}

.import-status {
  font-weight: 600;
  margin-bottom: 8px;
}

.import-details.success .import-status {
  color: #166534;
}

.import-details.error .import-status {
  color: #dc2626;
}

.import-detail-item {
  margin: 4px 0;
  font-size: 13px;
}

.import-details.success .import-detail-item {
  color: #16a34a;
}

.import-details.error .import-detail-item {
  color: #dc2626;
}

.source-content {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.source-options-row {
  display: flex;
  gap: 8px;
  align-items: center;
  width: fit-content; /* This prevents it from stretching full width */
}

.source-options-row label {
  margin: 0; /* Remove default label margin */
  white-space: nowrap; /* Prevent label text from wrapping */
  font-size: 14px;
  color: #374151;
}

.option-group {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  margin-bottom: 8px;
}

.radiolist {
  display: flex;
}

.radiolist div {
  padding-right: 1rem;
  padding-bottom: .5rem;
}

.radiolist div input[type="radio"] {
  margin: 0 !important;
  margin-right: 6px !important;
  padding: 0 !important;
  width: auto;
}

.form-control {
  display: block;
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  background-color: #ffffff;
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

.form-control:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-control-textarea {
  display: block;
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-family: inherit;
  resize: vertical;
  font-size: 16px;
  background-color: #ffffff;
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

.form-control-textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  line-height: 1.4;
}

.file-input {
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  width: 97%;
  font-size: 14px;
}

.field-help {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
}

h4 {
  margin: 10px 0;
  color: #111827;
  font-size: 18px;
  font-weight: 600;
}

h5 {
  margin: 8px 0;
  color: #374151;
  font-size: 14px;
  font-weight: 600;
}

label {
  font-size: 1rem;
  margin: 0;
  font-weight: 500;
  color: #374151;
}

.btn {
  box-shadow: 2px 4px #ccc;
  margin: 0 5px;
  padding: 7px 16px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  border: 0;
  min-width: 70px;
  transition: all 0.2s ease;
}

.btn-primary {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.2);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 35px;
  line-height: 1;
  text-align: center;
  white-space: nowrap;
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
}

.btn-primary:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.btn-rounded {
  border-radius: 24px !important;
}

.btn-submit {
  background-color: #007bff;
  color: white;
  border-radius: 20px;
}

.btn-secondary {
  background: #6b7280;
  color: white;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 35px;
  line-height: 1;
  text-align: center;
  white-space: nowrap;
}

.btn-secondary:hover:not(:disabled) {
  background: #4b5563;
  transform: translateY(-1px);
}

.btn-secondary:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  transform: none;
}

.btn-import {
  background-color: #1e40af;
  color: white;
  border-radius: 20px;
}

.btn-import:hover {
  background-color: #1e3a8a;
}

.btn-import:disabled {
  background-color: #9ca3af;
  cursor: not-allowed;
}

.btn-icon {
  background: #3b82f6;
  color: white;
  padding: 8px 10px;
  font-size: 14px;
  min-width: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-icon:hover {
  background: #2563eb;
}

.progress-container {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.progress-bar-container {
  margin: 16px 0;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: #f3f4f6;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 8px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #1d4ed8);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 12px;
  color: #6b7280;
  text-align: center;
  font-weight: 500;
}

.current-status {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  margin: 16px 0;
}

.status-message {
  font-size: 14px;
  color: #374151;
  font-weight: 500;
  flex: 1;
}

.progress-log {
  margin-top: 20px;
}

.log-container {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fafafa;
}

.log-entry {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 13px;
  line-height: 1.4;
}

.log-entry:last-child {
  border-bottom: none;
}

.log-timestamp {
  color: #6b7280;
  font-size: 11px;
  min-width: 60px;
  font-family: monospace;
}

.log-message {
  flex: 1;
  color: #374151;
}

.log-entry.log-success {
  background: #f0fdf4;
  border-left: 3px solid #22c55e;
}

.log-entry.log-error {
  background: #fef2f2;
  border-left: 3px solid #ef4444;
}

.log-entry.log-warning {
  background: #fffbeb;
  border-left: 3px solid #f59e0b;
}

.log-entry.log-import {
  background: #f0f9ff;
  border-left: 3px solid #1e40af;
}

.log-entry.log-import .log-message {
  color: #1e40af;
}

.log-entry.log-info {
  background: #f8fafc;
  border-left: 3px solid #3b82f6;
}

.results-summary {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.status-message.success {
  background: #dcfce7;
  color: #166534;
  border: 1px solid #bbf7d0;
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 16px;
  font-size: 14px;
  font-weight: 500;
}

.status-message.error {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 16px;
  font-size: 14px;
  font-weight: 500;
}

.analysis-section {
  background: #f0f9ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 16px;
}

.analysis-section h4 {
  margin: 0 0 12px 0;
  color: #1e40af;
  font-size: 16px;
  font-weight: 600;
}

.analysis-content {
  color: #1e40af;
  line-height: 1.5;
  font-size: 14px;
}

.urls-section {
  background: #fefce8;
  border: 1px solid #fde047;
  border-radius: 8px;
  padding: 16px;
}

.urls-section h4 {
  margin: 0 0 12px 0;
  color: #a16207;
  font-size: 16px;
  font-weight: 600;
}

.urls-list-container {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #fde047;
  border-radius: 4px;
  background: white;
}

.urls-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
}

.url-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px;
  background: #fefce8;
  border-radius: 6px;
  border: 1px solid #fbbf24;
}

.url-number {
  font-weight: 600;
  color: white;
  min-width: 20px;
  text-align: center;
  background: #fbbf24;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  flex-shrink: 0;
}

.url-link {
  color: #1d4ed8;
  text-decoration: none;
  font-size: 13px;
  word-break: break-all;
  line-height: 1.4;
}

.url-link:hover {
  text-decoration: underline;
}

.getting-started {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 20px;
}

.step-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}

.step-number {
  width: 24px;
  height: 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 12px;
  flex-shrink: 0;
}

.step-content {
  flex: 1;
}

.step-title {
  font-weight: 500;
  color: #111827;
  margin-bottom: 4px;
  font-size: 14px;
}

.step-description {
  color: #6b7280;
  font-size: 13px;
  line-height: 1.4;
}

.feature-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}

.feature-text {
  color: #374151;
  font-weight: 400;
  font-size: 14px;
}

.kb-stats-section h5 {
  margin: 0 0 12px 0;
  color: #334155;
  font-size: 14px;
  font-weight: 600;
}

.kb-stats {
  display: flex;
  gap: 16px;
  justify-content: space-around;
}

.stat-item {
  text-align: center;
  flex: 1;
}

.stat-number {
  display: block;
  font-size: 24px;
  font-weight: 700;
  color: #1e40af;
  line-height: 1;
}

.stat-label {
  display: block;
  font-size: 12px;
  color: #64748b;
  margin-top: 4px;
}

.action-icon-btn {
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin: 0 2px;
}

.action-icon-btn:hover {
  background: #2563eb;
  transform: scale(1.05);
}

.action-icon-btn.delete-btn {
  background: #ef4444;
  color: white;
}

.action-icon-btn.delete-btn:hover {
  background: #dc2626;
  color: white;
}

.action-icon-btn.delete-btn i {
  color: white !important;
}

.action-buttons {
  display: flex;
  gap: 4px;
  justify-content: center;
  align-items: center;
}

.articles-full-section {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  margin: 20px 8px;
  padding: 20px;
}

.articles-table-container {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  background: white;
  margin-bottom: 16px;
}

.articles-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.articles-table thead {
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.articles-table th {
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-right: 1px solid #e5e7eb;
}

.articles-table th:last-child {
  border-right: none;
}

.articles-table tbody tr {
  border-bottom: 1px solid #f3f4f6;
  transition: background-color 0.2s ease;
}

.articles-table tbody tr:hover {
  background: #f9fafb;
}

.articles-table tbody tr:last-child {
  border-bottom: none;
}

.articles-table td {
  padding: 12px 16px;
  vertical-align: top;
  border-right: 1px solid #f3f4f6;
}

.articles-table td:last-child {
  border-right: none;
}

.article-title {
  font-weight: 600;
  color: #111827;
  margin-bottom: 4px;
  line-height: 1.4;
}

.article-preview {
  color: #6b7280;
  font-size: 12px;
  line-height: 1.3;
  max-height: 36px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.rectangle-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}

.category-badge {
  background: #3b82f6;
  color: white;
}

.status-badge.published {
  background: #dcfce7;
  color: #166534;
}

.status-badge.draft {
  background: #fef3c7;
  color: #92400e;
}

.visibility-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
}

.visibility-checkbox {
  width: 16px;
  height: 16px;
}

.visibility-label {
  font-size: 12px;
  color: #6b7280;
}

.labels-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.label-tag {
  background: #f3f4f6;
  color: #374151;
  padding: 2px 6px;
  font-size: 10px;
  font-weight: 500;
  white-space: nowrap;
}

.action-buttons {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.kb-import-section {
  background: #f0f9ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 16px;
  margin: 16px 0;
}

.kb-import-section h5 {
  margin: 0 0 12px 0;
  color: #1e40af;
  font-size: 16px;
  font-weight: 600;
}

.kb-selection {
  margin-bottom: 12px;
}

.kb-dropdown {
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 14px;
  width: 100%;
}

.kb-dropdown:disabled {
  background: #f9fafb;
  color: #6b7280;
}

.kb-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.download-section {
  text-align: center;
  padding: 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
}

/* New Approval Actions Bar */
.approval-actions-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin-bottom: 16px;
}

.selection-info {
  color: #475569;
  font-weight: 500;
  font-size: 14px;
}

.approval-actions {
  display: flex;
  gap: 8px;
}

.btn-approve {
  background-color: #059669;
  color: white;
  padding: 7px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: 0;
  transition: all 0.2s ease;
}

.btn-reject {
  background-color: #dc2626;
  color: white;
  padding: 7px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: 0;
  transition: all 0.2s ease;
}

.btn-reset {
  background-color: #6b7280;
  color: white;
  padding: 7px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: 0;
  transition: all 0.2s ease;
}

/* Checkbox Column Styles */
.checkbox-column {
  width: 40px;
  text-align: center;
}

.select-all-checkbox, .article-checkbox {
  width: 16px;
  height: 16px;
  cursor: pointer;
  align-self: center;
}

.checkbox-cell {
  text-align: center;
  vertical-align: middle;
}

.approval-status-cell {
  text-align: left;
  vertical-align: middle;
}

/* Center checkbox in header */
.checkbox-header-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
}

/* Disabled edit button */
.action-icon-btn.disabled {
  background: #9ca3af;
  cursor: not-allowed;
  opacity: 0.5;
}

.action-icon-btn.disabled:hover {
  background: #9ca3af;
  transform: none;
}



/* WYSIWYG Editor Styles */
.wysiwyg-editor-container {
  background: white;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  margin-bottom: 20px;
  height: calc(100vh - 250px);
  display: flex;
  flex-direction: column;
}

.wysiwyg-editor {
  min-height: 400px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.wysiwyg-editor :deep(.ql-editor) {
  min-height: 350px;
  font-size: 14px;
  line-height: 1.6;
  padding: 20px;
}

.wysiwyg-editor :deep(.ql-toolbar) {
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
}

.wysiwyg-editor :deep(.ql-container) {
  border: none;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.wysiwyg-editor :deep(.ql-editor) {
  flex: 1;
}

.wysiwyg-editor :deep(.ql-editor.ql-blank::before) {
  color: #9ca3af;
  font-style: italic;
}

/* Mobile responsiveness for approval system */
@media only screen and (max-width: 800px) {
  .approval-controls {
    flex-direction: column;
    gap: 2px;
  }
  
  .approval-btn {
    font-size: 14px;
    padding: 6px 8px;
  }
  
  .bulk-actions {
    flex-direction: column;
    gap: 8px;
  }
  
  .bulk-actions button {
    width: 100%;
    font-size: 13px;
  }
  
  .approval-stats {
    flex-direction: column;
    gap: 8px;
  }
  
  .articles-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .approval-summary-mini {
    flex-direction: column;
    gap: 6px;
    align-items: flex-start;
  }

  #centerdiv {
    margin: 16px 8px 20px 8px;
    flex-direction: column;
    flex-wrap: wrap;
    gap: 16px;
    padding: 16px;
  }

  .containerdiv,
  #containerdiv1,
  #containerdiv2,
  #containerdiv3 {
    width: 100%;
    min-width: auto;
    margin: 0;
    padding: 8px;
  }

  .articles-table-container {
    overflow-x: auto;
  }

  .articles-table {
    min-width: 600px;
  }

  .action-buttons {
    flex-direction: column;
    gap: 2px;
  }

  .action-buttons button {
    width: 100%;
    font-size: 10px;
    padding: 3px 6px;
  }
}

/* Knowledge Inventory Styles */
.knowledge-inventory-section {
  margin-top: 20px;
}

.knowledge-inventory-section .member-info-content {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 20px;
}

.knowledge-input-section {
  margin-bottom: 20px;
  background: #ffffff;
  padding: 20px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.knowledge-actions {
  margin-top: 20px;
  display: flex;
  gap: 10px;
}

.knowledge-inventory {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 20px;
  margin-top: 15px;
}

.knowledge-header {
  margin-bottom: 20px;
}

.knowledge-header h5 {
  color: #1e293b;
  margin: 0 0 8px 0;
  font-size: 18px;
}

.knowledge-header p {
  color: #64748b;
  margin: 0;
  font-size: 14px;
}

.add-site-section {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 15px;
  margin-bottom: 20px;
}

.url-input-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 15px;
}

.url-input-row .form-control {
  flex: 1;
  min-width: 300px;
}

.crawl-options {
  margin-top: 10px;
}

.option-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.option-row label {
  font-size: 14px;
  color: #374151;
  white-space: nowrap;
}

.sites-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  flex-wrap: wrap;
  gap: 10px;
}

.sites-header h5 {
  color: #1e293b;
  margin: 0;
  font-size: 16px;
}

.monitoring-controls {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.monitoring-toggle.active {
  background: #ef4444;
  color: white;
  border-color: #ef4444;
}

.monitoring-toggle.inactive {
  background: #22c55e;
  color: white;
  border-color: #22c55e;
}

.scan-all-btn {
  background: #3b82f6 !important;
  color: white !important;
  border-color: #3b82f6 !important;
  border: 1px solid #3b82f6 !important;
}

.scan-all-btn:hover:not(:disabled) {
  background: #2563eb !important;
  border-color: #2563eb !important;
  color: white !important;
}

.scan-all-btn:disabled {
  background: #9ca3af !important;
  border-color: #9ca3af !important;
  color: white !important;
  cursor: not-allowed;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #6b7280;
  font-style: italic;
}

.sites-grid {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.site-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}

.site-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.site-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.site-status.active {
  color: #22c55e;
}

.site-status.inactive {
  color: #ef4444;
}

.site-url {
  font-weight: 500;
  color: #1e293b;
}

.site-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 8px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  transition: all 0.2s ease;
  min-width: 44px;
  line-height: 1;
}

.action-btn:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.action-btn.view-btn {
  background: #dbeafe;
  border-color: #3b82f6;
  color: #1e40af;
}

.action-btn.view-btn:hover:not(:disabled) {
  background: #bfdbfe;
  border-color: #2563eb;
}

.action-btn.delete-btn {
  background: #fee2e2;
  border-color: #ef4444;
  color: #dc2626;
}

.action-btn.delete-btn:hover:not(:disabled) {
  background: #fecaca;
  border-color: #dc2626;
}

.scan-btn {
  border-color: #3b82f6;
  color: #3b82f6;
}

.details-btn {
  border-color: #10b981;
  color: #10b981;
}

.delete-btn {
  border-color: #ef4444;
  color: #ef4444;
}

.site-stats {
  display: flex;
  gap: 15px;
  padding: 10px 15px;
  font-size: 13px;
  flex-wrap: wrap;
}

.stat {
  color: #6b7280;
}

.stat.has-changes {
  color: #ef4444;
  font-weight: 600;
}

.site-details {
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
}

.pages-list {
  padding: 15px;
}

.pages-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  font-weight: 500;
  color: #374151;
}

.page-controls {
  display: flex;
  gap: 8px;
}

.control-btn {
  padding: 4px 8px;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}

.control-btn:hover {
  background: #f3f4f6;
}

/* Removed conflicting flex styles for pages-table - using table layout instead */

/* Removed flex styles for page-row - using table row layout instead */

.page-checkbox {
  margin: 0;
}

.page-url {
  flex: 1;
  color: #374151;
}

.changed-indicator {
  color: #ef4444;
}

.page-status {
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 2px;
  font-weight: 600;
}

.page-status.status-200 {
  background: #dcfce7;
  color: #166534;
}

.page-status.status-404 {
  background: #fef2f2;
  color: #991b1b;
}

.more-pages {
  text-align: center;
  color: #6b7280;
  font-style: italic;
  padding: 8px;
}

@media (max-width: 768px) {
  .knowledge-inventory .url-input-row {
    flex-direction: column;
  }
  
  .knowledge-inventory .option-row {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .knowledge-inventory .site-header {
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
  }
  
  .knowledge-inventory .site-stats {
    gap: 10px;
  }
}

/* Action Buttons Row */
.action-buttons-row {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  flex-wrap: wrap;
}

@media (max-width: 600px) {
  .action-buttons-row {
    flex-direction: column;
    gap: 5px;
  }
  
  .action-buttons-row button {
    width: 100%;
    margin-left: 0 !important;
  }
}

/* View All Sites Modal Styles */
.view-all-btn {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
}

.view-all-btn:hover {
  background: #2563eb;
  transform: translateY(-1px);
}

.dialog-all-sites {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1000;
  border: none;
  background: none;
}

.dialog-body-large {
  position: relative;
  background: white;
  border-radius: 12px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  max-width: 90vw;
  min-width: 1200px;
  max-height: 90vh;
  margin: 5vh auto;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.dialog-all-sites .dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
}

.dialog-all-sites .dialog-header h3 {
  margin: 0;
  color: #1e293b;
  font-size: 18px;
  font-weight: 600;
}

.sites-overview {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.overview-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  text-align: center;
}

.stat-number {
  display: block;
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 4px;
}

.stat-label {
  display: block;
  font-size: 12px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.sites-table-container {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow-x: auto;
  margin-bottom: 24px;
}

.sites-table {
  width: 100%;
  min-width: 1120px;
  border-collapse: collapse;
  font-size: 14px;
  table-layout: fixed;
}

.sites-table thead {
  background: #f8fafc;
}

.sites-table th {
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-bottom: 1px solid #e2e8f0;
}

.sites-table th:nth-child(1) { width: 60px; } /* # */
.sites-table th:nth-child(2) { width: 400px; min-width: 300px; max-width: 600px; } /* Site URL */
.sites-table th:nth-child(3) { width: 110px; } /* Status */
.sites-table th:nth-child(4) { width: 80px; } /* Pages */
.sites-table th:nth-child(5) { width: 100px; } /* Monitored */
.sites-table th:nth-child(6) { width: 80px; } /* Changed */
.sites-table th:nth-child(7) { width: 130px; } /* Last Crawled */
.sites-table th:nth-child(8) { width: 280px; } /* Actions */

.sites-table td:nth-child(1) { width: 60px; } /* # */
.sites-table td:nth-child(2) {
  width: 400px;
  min-width: 300px;
  max-width: 600px;
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: clip !important;
  word-break: break-word;
  line-height: 1.4;
} /* Site URL */
.sites-table td:nth-child(3) { width: 110px; } /* Status */
.sites-table td:nth-child(4) { width: 80px; } /* Pages */
.sites-table td:nth-child(5) { width: 100px; } /* Monitored */
.sites-table td:nth-child(6) { width: 80px; } /* Changed */
.sites-table td:nth-child(7) { width: 130px; } /* Last Crawled */
.sites-table td:nth-child(8) { width: 280px; } /* Actions */

.sites-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #f3f4f6;
}

.site-row:hover {
  background: #f9fafb;
}

.site-url-cell a {
  color: #3b82f6;
  text-decoration: none;
  font-weight: 500;
}

.site-url-cell a:hover {
  text-decoration: underline;
}

.status-cell .status-indicator {
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 12px;
  font-weight: 500;
}

.status-indicator.active {
  background: #dcfce7;
  color: #166534;
}

.status-indicator.inactive {
  background: #fee2e2;
  color: #991b1b;
}

.count-cell {
  text-align: center;
  font-weight: 500;
}

.count-cell.has-changes {
  color: #dc2626;
  font-weight: 600;
}

.time-cell {
  color: #64748b;
  font-size: 13px;
}

.actions-cell {
  white-space: nowrap;
}

.table-action-btn {
  background: none;
  border: 1px solid #e2e8f0;
  padding: 6px 12px;
  margin: 0 3px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 32px;
  white-space: nowrap;
}

.table-action-btn:hover {
  background: #f3f4f6;
}

.table-action-btn.details-btn:hover {
  background: #dbeafe;
  border-color: #3b82f6;
}

.table-action-btn.scan-btn:hover {
  background: #ecfdf5;
  border-color: #10b981;
}

.table-action-btn.delete-btn:hover {
  background: #fee2e2;
  border-color: #ef4444;
}

.table-action-btn.generate-btn {
  background: #f0f9ff;
  border-color: #0284c7;
  color: #0c4a6e;
}

.table-action-btn.generate-btn:hover:not(:disabled) {
  background: #e0f2fe;
  border-color: #0369a1;
  color: #0c4a6e;
}

.table-action-btn.generate-btn:disabled {
  background: #f1f5f9;
  border-color: #e2e8f0;
  color: #94a3b8;
  cursor: not-allowed;
}

.table-action-btn.documents-btn {
  background: #f0f9ff;
  border-color: #0284c7;
  color: #0c4a6e;
}

.table-action-btn.documents-btn:hover {
  background: #e0f2fe;
  border-color: #0369a1;
  color: #0c4a6e;
}

.table-action-btn.process-btn {
  background: #fef3c7;
  border-color: #f59e0b;
  color: #92400e;
}

.table-action-btn.process-btn:hover:not(:disabled) {
  background: #fde68a;
  border-color: #d97706;
  color: #78350f;
}

.table-action-btn.process-btn:disabled {
  background: #f9fafb;
  border-color: #e5e7eb;
  color: #9ca3af;
  cursor: not-allowed;
}

.table-action-btn.articles-btn {
  background: #ecfdf5;
  border-color: #10b981;
  color: #065f46;
}

.table-action-btn.articles-btn:hover {
  background: #d1fae5;
  border-color: #059669;
  color: #047857;
}

.modal-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
}

@media (max-width: 768px) {
  .dialog-body-large {
    max-width: 95vw;
    margin: 2.5vh auto;
  }
  
  .overview-stats {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .sites-table {
    font-size: 12px;
  }
  
  .sites-table th,
  .sites-table td {
    padding: 8px 12px;
  }
  
  .modal-actions {
    flex-direction: column;
  }
}

/* Loading Spinner Styles */
.spinner-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 20px;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #e2e8f0;
  border-top: 2px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-state {
  padding: 40px 20px;
  text-align: center;
  color: #64748b;
}

.loading-state .spinner-container {
  flex-direction: column;
  gap: 16px;
}

.loading-state .spinner {
  width: 32px;
  height: 32px;
  border-width: 3px;
}

.loading-row .loading-cell {
  text-align: center;
  padding: 24px;
  color: #64748b;
}

.loading-row .spinner-container {
  padding: 0;
}

.loading-row .spinner {
  width: 16px;
  height: 16px;
}

/* All Pages Table Styles - REMOVED: Not used in HTML, was causing conflicts */

/* Status badges for all pages table */
.status-badge-small {
  display: inline-block;
  text-align: center;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
  min-width: 28px;
}

/* Simple Table Styles */
.simple-table-container {
  max-height: 400px;
  overflow: auto;
  border: 1px solid #ddd;
  border-radius: 6px;
}

.simple-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.simple-table th {
  padding: 10px 8px;
  text-align: left;
  font-weight: 600;
  background: #f8f9fa;
  border-bottom: 2px solid #ddd;
  position: sticky;
  top: 0;
  z-index: 10;
  box-sizing: border-box;
}

.simple-table td {
  padding: 10px 8px;
  border-bottom: 1px solid #eee;
  vertical-align: middle;
  box-sizing: border-box;
}

.simple-table tr:hover {
  background: #f9f9f9;
}

/* Page action buttons */
.page-actions {
  display: flex;
  gap: 4px;
  justify-content: center;
}

.page-action-btn {
  background: none;
  border: 1px solid #e2e8f0;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 11px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 48px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
}

.page-action-btn:hover:not(:disabled) {
  background: #f3f4f6;
}

.page-action-btn.process-btn {
  background: #fef3c7;
  border-color: #f59e0b;
  color: #92400e;
}

.page-action-btn.process-btn:hover:not(:disabled) {
  background: #fde68a;
  border-color: #d97706;
}

.page-action-btn.articles-btn {
  background: #ecfdf5;
  border-color: #10b981;
  color: #065f46;
}

.page-action-btn.articles-btn:hover:not(:disabled) {
  background: #d1fae5;
  border-color: #059669;
}

.page-action-btn.view-btn {
  background: #dbeafe;
  border-color: #3b82f6;
  color: #1e40af;
}

.page-action-btn.view-btn:hover:not(:disabled) {
  background: #bfdbfe;
  border-color: #2563eb;
}

.page-action-btn.scan-btn {
  background: #f0fdf4;
  border-color: #22c55e;
  color: #15803d;
}

.page-action-btn.scan-btn:hover:not(:disabled) {
  background: #dcfce7;
  border-color: #16a34a;
}

.page-action-btn:disabled {
  background: #f9fafb;
  border-color: #e5e7eb;
  color: #9ca3af;
  cursor: not-allowed;
}

/* Bulk action buttons */
.bulk-action-btn {
  background: none;
  border: 1px solid #d1d5db;
  padding: 2px 4px;
  border-radius: 3px;
  font-size: 10px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 20px;
  height: 20px;
}

.bulk-action-btn:hover {
  background: #f3f4f6;
}

/* Larger bulk action buttons */
.bulk-action-btn-large {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;
}

.bulk-action-btn-large:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
  transform: translateY(-1px);
}

/* KB Refresh Button */
.kb-refresh-btn {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.kb-refresh-btn:hover:not(:disabled) {
  background: #f1f5f9;
  border-color: #cbd5e1;
  transform: translateY(-1px);
}

.kb-refresh-btn:disabled {
  background: #f9fafb;
  border-color: #e5e7eb;
  color: #9ca3af;
  cursor: not-allowed;
  transform: none;
}

.status-200, .status-201, .status-204 {
  background: #d4edda;
  color: #155724;
}

.status-301, .status-302, .status-304 {
  background: #fff3cd;
  color: #856404;
}

.status-400, .status-401, .status-403, .status-404, 
.status-500, .status-502, .status-503 {
  background: #f8d7da;
  color: #721c24;
}

.status-0 {
  background: #f1f3f4;
  color: #5f6368;
}

/* All pages table styling */
.page-row:hover {
  background-color: #f8f9fa !important;
}

.page-row.changed-page {
  background-color: rgba(220, 53, 69, 0.05);
}

.page-row.changed-page:hover {
  background-color: rgba(220, 53, 69, 0.1) !important;
}

.all-pages-section h4 {
  color: #495057;
  margin-bottom: 16px;
  font-size: 1.1rem;
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 8px;
}

/* Loading and error indicators */
.loading-indicator {
  animation: pulse 1.5s ease-in-out infinite;
}

.error-indicator {
  color: #dc3545;
  cursor: help;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* URL-grouped articles styles */
.articles-by-url {
  space-y: 8px;
}

.url-group {
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  margin-bottom: 8px;
  overflow: hidden;
}

.url-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #f8fafc;
  cursor: pointer;
  border-bottom: 1px solid #e2e8f0;
  transition: background-color 0.2s;
}

.url-header:hover {
  background: #e2e8f0;
}

.url-info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.url-icon {
  font-size: 12px;
}

.url-path {
  font-family: monospace;
  font-size: 12px;
  color: #4a5568;
  font-weight: 500;
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.article-count-badge {
  background: #667eea;
  color: white;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 10px;
  min-width: 18px;
  text-align: center;
}

.expand-icon {
  color: #a0aec0;
  font-size: 12px;
}

.url-articles {
  background: white;
}

.url-articles .article-row {
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid #f1f5f9;
  margin: 0;
  padding: 8px 12px;
}

.url-articles .article-row:last-child {
  border-bottom: none;
}

.row-number {
  text-align: center;
  font-weight: 600;
  color: #6c757d;
}

.no-articles-state {
  text-align: center;
  padding: 20px;
  color: #718096;
  font-style: italic;
}

.url-articles .article-info {
  flex: 1;
  min-width: 0;
}

.url-articles .article-actions {
  flex-shrink: 0;
  display: flex;
  gap: 4px;
}

/* Articles Button */
.articles-btn {
  background: #667eea;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 11px;
  cursor: pointer;
  transition: background-color 0.2s;
  margin-left: 8px;
}

.articles-btn:hover {
  background: #5a67d8;
}

.page-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  border-bottom: 1px solid #f1f5f9;
}

.page-row:last-child {
  border-bottom: none;
}

/* ==================== ARTICLES MODAL ==================== */
.articles-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.articles-modal-container {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 1000px;
  max-height: 90vh;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Modal Header */
.articles-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 24px 20px 24px;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
}

.modal-title-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.modal-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
}

.modal-title i {
  color: #3b82f6;
}

.articles-count {
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
}

.modal-header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.close-modal-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  background: #f3f4f6;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
}

.close-modal-btn:hover {
  background: #e5e7eb;
  color: #374151;
}

/* Modal Content */
.articles-modal-content {
  flex: 1;
  padding: 24px;
  overflow: auto;
  min-height: 0;
}

/* Loading State */
.modal-loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #6b7280;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f4f6;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Articles Table */
.articles-table-wrapper {
  width: 100%;
  overflow: auto;
}

.articles-table-container {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.articles-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  min-width: 700px;
}

.articles-table thead {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.articles-table th {
  padding: 16px 12px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-bottom: 2px solid #e2e8f0;
  font-size: 14px;
}

.articles-table tbody tr {
  transition: background-color 0.2s ease;
}

.articles-table tbody tr:hover {
  background: #f8fafc;
}

.articles-table tbody tr:not(:last-child) {
  border-bottom: 1px solid #f1f5f9;
}

.articles-table td {
  padding: 16px 12px;
  vertical-align: middle;
  font-size: 14px;
}

/* Column Widths */
.articles-table .col-title {
  width: 45%;
}

.articles-table .col-date {
  width: 18%;
}

.articles-table .col-status {
  width: 12%;
}

.articles-table .col-actions {
  width: 25%;
}

/* Article Title */
.article-title {
  font-weight: 500;
  color: #1f2937;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Article Actions */
.article-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

/* Empty State */
.modal-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  color: #6b7280;
}

.modal-empty-state .empty-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: #9ca3af;
  margin-bottom: 16px;
}

.modal-empty-state h4 {
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 600;
  color: #374151;
}

.modal-empty-state p {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
}

/* Modal Footer */
.articles-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px 24px 24px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
}

/* Responsive Design */
@media (max-width: 768px) {
  .articles-modal-overlay {
    padding: 10px;
  }
  
  .articles-modal-container {
    max-height: 95vh;
  }
  
  .articles-modal-header {
    padding: 20px 16px 16px 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .modal-header-actions {
    align-self: flex-end;
  }
  
  .articles-modal-content {
    padding: 16px;
  }
  
  .articles-modal-footer {
    padding: 16px;
  }
  
  .articles-table th,
  .articles-table td {
    padding: 12px 8px;
  }
  
  .article-actions {
    flex-direction: column;
    gap: 4px;
  }
  
  .action-btn.small {
    width: 100%;
    justify-content: center;
  }
}

/* Old modal styles removed - using new articles modal above */

/* Article Preview Modal - Modern Design */
.article-preview-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(15, 23, 42, 0.8);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
}

.article-preview-modal {
  background: #ffffff;
  border-radius: 20px;
  max-width: 900px;
  max-height: 90vh;
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(226, 232, 240, 0.8);
}

/* Header */
.article-preview-modal .modal-header {
  padding: 32px 32px 24px;
  border-bottom: 1px solid #f1f5f9;
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  position: relative;
}

.header-content {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.header-icon {
  font-size: 28px;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  border-radius: 12px;
  color: white;
  flex-shrink: 0;
}

.header-text h3 {
  margin: 0 0 8px 0;
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
}

.header-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.status-badge {
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.status-badge.published {
  background: #dcfce7;
  color: #166534;
  border: 1px solid #bbf7d0;
}

.status-badge.draft {
  background: #fef3c7;
  color: #92400e;
  border: 1px solid #fde68a;
}

.date-badge {
  background: #f1f5f9;
  color: #475569;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid #e2e8f0;
}

.article-preview-modal .close {
  position: absolute;
  top: 24px;
  right: 24px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  font-size: 16px;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.article-preview-modal .close:hover {
  background: #f1f5f9;
  color: #334155;
  transform: scale(1.05);
}

/* Content */
.article-preview-content {
  flex: 1;
  overflow-y: auto;
  padding: 32px;
}

.article-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
  padding: 24px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 16px;
  border: 1px solid #e2e8f0;
}

.article-info-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.article-info-item strong {
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.article-info-item span {
  font-size: 15px;
  color: #1e293b;
  font-weight: 500;
  word-break: break-word;
}

.source-url, .kb-id, .genesys-article-id {
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 13px !important;
  background: #ffffff;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.genesys-status {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.genesys-status.published {
  background: #dcfce7;
  color: #166534;
}

.genesys-status.draft {
  background: #fef3c7;
  color: #92400e;
}

/* Meta Information */
.article-meta-info {
  margin-bottom: 32px;
}

.article-meta-row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 20px;
  gap: 16px;
}

.article-meta-row strong {
  font-size: 14px;
  font-weight: 700;
  color: #475569;
  width: 120px;
  flex-shrink: 0;
  padding-top: 2px;
}

.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.category-tag {
  background: linear-gradient(135deg, #e0f2fe, #bae6fd);
  color: #0c4a6e;
  padding: 8px 16px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid #7dd3fc;
}

.label-tag {
  background: linear-gradient(135deg, #dbeafe, #bfdbfe);
  color: #1e40af;
  padding: 6px 12px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid #93c5fd;
}

.alternative-item {
  background: linear-gradient(135deg, #f0fdf4, #dcfce7);
  color: #166534;
  padding: 6px 12px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid #bbf7d0;
}

/* Content Section */
.content-preview-section {
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #f1f5f9;
  overflow: hidden;
}

.content-preview-section h4 {
  margin: 0;
  padding: 20px 24px;
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 1px solid #e2e8f0;
}

.content-text {
  padding: 24px;
  line-height: 1.7;
  color: #334155;
  font-size: 15px;
}

.content-text h3 {
  color: #1e293b;
  font-weight: 700;
  font-size: 18px;
  margin: 0 0 16px 0;
}

.content-text h4 {
  color: #334155;
  font-weight: 600;
  font-size: 16px;
  margin: 24px 0 12px 0;
}

.content-text p {
  margin: 0 0 16px 0;
}

.content-text p:last-child {
  margin-bottom: 0;
}

.content-text strong {
  color: #1e293b;
  font-weight: 600;
}

.content-text a {
  color: #3b82f6;
  text-decoration: none;
  font-weight: 500;
}

.content-text a:hover {
  text-decoration: underline;
}

.content-text ol, .content-text ul {
  margin: 16px 0;
  padding-left: 24px;
}

.content-text li {
  margin: 8px 0;
}

/* Footer */
.article-preview-modal .modal-footer {
  padding: 20px 32px;
  border-top: 1px solid #f1f5f9;
  background: #f8fafc;
  display: flex;
  justify-content: flex-end;
}

.article-preview-modal .btn-secondary {
  background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
  color: #475569;
  border: 1px solid #cbd5e1;
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.article-preview-modal .btn-secondary:hover {
  background: linear-gradient(135deg, #e2e8f0, #cbd5e1);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* Responsive Design */
@media (max-width: 768px) {
  .article-preview-modal-overlay {
    padding: 12px;
  }
  
  .article-preview-modal {
    max-height: 95vh;
    border-radius: 16px;
  }
  
  .article-preview-modal .modal-header {
    padding: 24px 20px 20px;
  }
  
  .header-content {
    gap: 12px;
  }
  
  .header-icon {
    width: 40px;
    height: 40px;
    font-size: 24px;
  }
  
  .header-text h3 {
    font-size: 20px;
  }
  
  .article-info-grid {
    grid-template-columns: 1fr;
    gap: 16px;
    padding: 20px;
  }
  
  .article-preview-content {
    padding: 20px;
  }
  
  .article-meta-row {
    flex-direction: column;
    gap: 8px;
  }
  
  .article-meta-row strong {
    width: auto;
  }
  
  .content-text {
    padding: 20px;
  }
  
  .article-preview-modal .modal-footer {
    padding: 16px 20px;
  }
}

/* Monitored Inventory Section Styling */
.monitored-inventory-section {
  margin-top: 40px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 24px;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 12px 12px 0 0;
}

.section-title h3 {
  margin: 0 0 16px 0;
  color: #1e293b;
  font-size: 20px;
  font-weight: 600;
}

.section-stats {
  display: flex;
  gap: 16px;
}

.stat-chip {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 80px;
}

.stat-chip.changed {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border-color: #fecaca;
}

.stat-chip .stat-label {
  font-size: 11px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
  margin-bottom: 4px;
}

.stat-chip .stat-value {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
}

.stat-chip.changed .stat-value {
  color: #dc2626;
}

.section-actions {
  display: flex;
  gap: 12px;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  color: #374151;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.action-btn:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.action-btn.monitoring-toggle.active {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: white;
  border-color: #dc2626;
}

.action-btn.monitoring-toggle.inactive {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border-color: #10b981;
}

.action-btn.process-btn {
  background: #f0f9ff;
  border-color: #0284c7;
  color: #0c4a6e;
}

.action-btn.process-btn:hover {
  background: #e0f2fe;
  border-color: #0369a1;
}

.action-btn.articles-btn {
  background: #f0fdf4;
  border-color: #16a34a;
  color: #15803d;
}

.action-btn.articles-btn:hover {
  background: #dcfce7;
  border-color: #15803d;
}

.action-btn.scan-btn {
  background: #fef3c7;
  border-color: #f59e0b;
  color: #92400e;
}

.action-btn.scan-btn:hover:not(:disabled) {
  background: #fde68a;
  border-color: #d97706;
}

.action-btn.history-btn {
  background: #f3f4f6;
  border-color: #9ca3af;
  color: #374151;
}

.action-btn.history-btn:hover {
  background: #e5e7eb;
  border-color: #6b7280;
}

.action-btn.primary {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border-color: #3b82f6;
}

.action-btn.secondary {
  background: linear-gradient(135deg, #64748b 0%, #475569 100%);
  color: white;
  border-color: #64748b;
}

.action-btn.success {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border-color: #10b981;
}

.inventory-content {
  padding: 24px;
}

.overview-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
}

.stat-card {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.3s ease;
}

.stat-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.stat-card.changed {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border-color: #fecaca;
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
}

.stat-card.changed .stat-icon {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
}

.stat-info {
  display: flex;
  flex-direction: column;
}

.stat-number {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1;
}

.stat-label {
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 4px;
}

.sites-table-container {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow-x: auto;
  margin-bottom: 24px;
  width: 100%;
  min-height: 200px;
  box-sizing: border-box;
}

/* Old pages-table-container styles removed - using new all-pages-table-container */

@media (max-width: 1200px) {
  .sites-table {
    min-width: 1000px;
  }
}

@media (max-width: 768px) {
  .sites-table {
    min-width: 800px;
    font-size: 13px;
  }
  
  .sites-table th,
  .sites-table td {
    padding: 10px 8px;
  }
  
  .pages-table {
    font-size: 13px;
  }
  
  .pages-table th,
  .pages-table td {
    padding: 10px 8px;
  }
}

.sites-table, .pages-table {
  width: 100% !important;
  border-collapse: collapse !important;
  table-layout: fixed !important;
  margin: 0 !important;
  padding: 0 !important;
  box-sizing: border-box !important;
}

/* Sites table specific column widths - with higher specificity */
.sites-table th.col-number,
.sites-table td.col-number {
  width: 5% !important;
  min-width: 40px !important;
}

.sites-table th.col-url,
.sites-table td.col-url {
  width: 22% !important;
  min-width: 160px !important;
}

.sites-table th.col-status,
.sites-table td.col-status {
  width: 10% !important;
  min-width: 80px !important;
}

.sites-table th.col-count,
.sites-table td.col-count {
  width: 7% !important;
  min-width: 50px !important;
}

.sites-table th.col-time,
.sites-table td.col-time {
  width: 14% !important;
  min-width: 100px !important;
}

.sites-table th.col-actions,
.sites-table td.col-actions {
  width: 35% !important;
  min-width: 320px !important;
}

/* ==================== NEW MONITORED SITES TABLE ==================== */

/* Container for the monitored sites table */
.monitored-sites-table-container {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 24px;
  width: 100%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
}

/* Main sites table styles */
.monitored-sites-data-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  margin: 0;
  padding: 0;
  font-size: 14px;
  box-sizing: border-box;
}

/* Sites table header styles */
.monitored-sites-data-table thead {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.monitored-sites-data-table th {
  padding: 16px 12px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-bottom: 2px solid #e2e8f0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Sites table column widths - totaling exactly 100% */
.monitored-sites-data-table .th-number,
.monitored-sites-data-table .td-number {
  width: 4%;
}

.monitored-sites-data-table .th-site-url,
.monitored-sites-data-table .td-site-url {
  width: 30%;
  min-width: 300px;
  max-width: 600px;
}

/* Allow URL column to wrap and show full URLs */
.monitored-sites-data-table .td-site-url {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: clip !important;
  word-break: break-word;
  line-height: 1.4;
}

/* Ensure links inside URL column also wrap */
.monitored-sites-data-table .td-site-url a {
  white-space: normal !important;
  word-break: break-word;
  display: inline;
}

.monitored-sites-data-table .th-status,
.monitored-sites-data-table .td-status {
  width: 9%;
}

.monitored-sites-data-table .th-pages,
.monitored-sites-data-table .td-pages {
  width: 7%;
}

.monitored-sites-data-table .th-monitored,
.monitored-sites-data-table .td-monitored {
  width: 7%;
}

.monitored-sites-data-table .th-changed,
.monitored-sites-data-table .td-changed {
  width: 7%;
}

.monitored-sites-data-table .th-crawled,
.monitored-sites-data-table .td-crawled {
  width: 12%;
}

.monitored-sites-data-table .th-scan-interval,
.monitored-sites-data-table .td-scan-interval {
  width: 10%;
}

.monitored-sites-data-table .th-knowledge-base,
.monitored-sites-data-table .td-knowledge-base {
  width: 12%;
}

.monitored-sites-data-table .th-site-actions,
.monitored-sites-data-table .td-site-actions {
  width: 15%;
}

/* Sites table row styles */
.monitored-sites-data-table tbody tr {
  border-bottom: 1px solid #f1f5f9;
  transition: background-color 0.2s ease;
}

.monitored-sites-data-table tbody tr:hover {
  background: #f8fafc;
}

.monitored-sites-data-table tbody tr:last-child {
  border-bottom: none;
}

/* Sites table cell styles */
.monitored-sites-data-table td {
  padding: 14px 12px;
  vertical-align: middle;
  border-right: none;
}

/* Apply text truncation to all cells EXCEPT URL column */
.monitored-sites-data-table td:not(.td-site-url) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Special styling for actions column */
.monitored-sites-data-table .td-site-actions {
  white-space: normal;
  overflow: visible;
}

/* Site actions row */
.site-actions-row {
  display: flex;
  gap: 6px;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  padding: 4px;
}

.site-action-btn {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  padding: 6px 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 12px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 70px;
  height: 30px;
  white-space: nowrap;
}

.site-action-btn:hover {
  background: #f1f5f9;
  border-color: #9ca3af;
}

.site-action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Site action button variants */
.site-action-btn.documents-btn {
  background: #f0f9ff;
  border-color: #0284c7;
  color: #0c4a6e;
}

.site-action-btn.documents-btn:hover {
  background: #e0f2fe;
  border-color: #0369a1;
}

.site-action-btn.generate-btn {
  background: #f0fdf4;
  border-color: #16a34a;
  color: #15803d;
}

.site-action-btn.generate-btn:hover:not(:disabled) {
  background: #dcfce7;
  border-color: #15803d;
}

.site-action-btn.generate-btn:disabled {
  background: #f9fafb;
  border-color: #e5e7eb;
  color: #9ca3af;
}

.site-action-btn.scan-btn {
  background: #fef3c7;
  border-color: #f59e0b;
  color: #92400e;
}

.site-action-btn.scan-btn:hover:not(:disabled) {
  background: #fde68a;
  border-color: #d97706;
}

.site-action-btn.delete-btn {
  background: #fee2e2;
  border-color: #ef4444;
  color: #dc2626;
}

.site-action-btn.delete-btn:hover {
  background: #fecaca;
  border-color: #dc2626;
}

/* Badge styles for sites table */
.count-badge {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background: #f3f4f6;
  color: #374151;
}

.count-badge.monitored {
  background: #dbeafe;
  color: #1d4ed8;
}

.count-badge.changed {
  background: #fef3c7;
  color: #d97706;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.status-badge.status-active {
  background: #dcfce7;
  color: #166534;
}

.status-badge.status-inactive {
  background: #fee2e2;
  color: #dc2626;
}

.time-badge {
  font-size: 12px;
  color: #6b7280;
}

/* Mobile responsive for sites table */
@media (max-width: 768px) {
  .monitored-sites-data-table {
    font-size: 13px;
  }
  
  .monitored-sites-data-table th,
  .monitored-sites-data-table td {
    padding: 10px 8px;
  }
  
  .site-actions-row {
    gap: 4px;
  }
  
  .site-action-btn {
    padding: 4px 8px;
    min-width: 60px;
    height: 28px;
    font-size: 11px;
  }
}

/* Old pages-table CSS removed - using new all-pages-data-table */

.sites-table th, .pages-table th {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  padding: 16px 12px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Force table layout and column alignment */
.sites-table th,
.sites-table td {
  box-sizing: border-box;
}

.pages-table th,
.pages-table td {
  box-sizing: border-box;
}

.sites-table td, .pages-table td {
  padding: 14px 12px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sites-table .col-actions {
  white-space: normal !important;
  overflow: visible !important;
}

/* ==================== NEW ALL PAGES DATA TABLE ==================== */

/* Container for the all pages table */
.all-pages-table-container {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 24px;
  width: 100%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* Main table styles */
.all-pages-data-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  margin: 0;
  padding: 0;
  font-size: 14px;
  box-sizing: border-box;
}

/* Header styles */
.all-pages-data-table thead {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.all-pages-data-table th {
  padding: 16px 12px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-bottom: 2px solid #e2e8f0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Column widths - totaling exactly 100% */
.all-pages-data-table .th-monitor,
.all-pages-data-table .td-monitor {
  width: 6%;
}

.all-pages-data-table .th-number,
.all-pages-data-table .td-number {
  width: 3%;
}

.all-pages-data-table .th-site,
.all-pages-data-table .td-site {
  width: 10%;
}

.all-pages-data-table .th-url,
.all-pages-data-table .td-url {
  width: 35%;
  min-width: 300px;
  max-width: 600px;
}

/* Allow URL column to wrap and show full URLs */
.all-pages-data-table .td-url {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: clip !important;
  word-break: break-word;
  line-height: 1.4;
}

/* Ensure links inside URL column also wrap */
.all-pages-data-table .td-url a {
  white-space: normal !important;
  word-break: break-word;
  display: inline;
}

.all-pages-data-table .th-status,
.all-pages-data-table .td-status {
  width: 7%;
}

.all-pages-data-table .th-changed,
.all-pages-data-table .td-changed {
  width: 11%;
}

.all-pages-data-table .th-processed,
.all-pages-data-table .td-processed {
  width: 11%;
}

.all-pages-data-table .th-kb,
.all-pages-data-table .td-kb {
  width: 9%;
}

.all-pages-data-table .th-on-process,
.all-pages-data-table .td-on-process {
  width: 9%;
}

.all-pages-data-table .th-actions,
.all-pages-data-table .td-actions {
  width: 17%;
}

/* Row styles */
.all-pages-data-table tbody tr {
  border-bottom: 1px solid #f1f5f9;
  transition: background-color 0.2s ease;
}

.all-pages-data-table tbody tr:hover {
  background: #f8fafc;
}

.all-pages-data-table tbody tr:last-child {
  border-bottom: none;
}

/* Cell styles */
.all-pages-data-table td {
  padding: 14px 12px;
  vertical-align: middle;
  border-right: none;
}

/* Apply text truncation to all cells EXCEPT URL column */
.all-pages-data-table td:not(.td-url) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Special styling for actions column */
.all-pages-data-table .td-actions {
  white-space: normal;
  overflow: visible;
}

/* Action buttons row */
.action-buttons-row {
  display: flex;
  gap: 6px;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  padding: 4px;
}

.action-btn {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  padding: 6px 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 12px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 70px;
  height: 30px;
  white-space: nowrap;
}

.action-btn:hover {
  background: #f1f5f9;
  border-color: #9ca3af;
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Loading and empty states */
.all-pages-data-table .loading-state,
.all-pages-data-table .empty-state {
  padding: 20px;
  text-align: center;
  color: #6b7280;
  font-style: italic;
}

/* Mobile responsive */
@media (max-width: 768px) {
  .all-pages-data-table {
    font-size: 13px;
  }
  
  .all-pages-data-table th,
  .all-pages-data-table td {
    padding: 10px 8px;
  }
  
  .action-buttons-row {
    gap: 2px;
  }
  
  .action-btn {
    padding: 5px 8px;
    min-width: 28px;
    height: 28px;
    font-size: 11px;
    gap: 2px;
  }
}

/* Pagination Controls */
.pagination-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  border-radius: 0 0 8px 8px;
  font-size: 14px;
  color: #374151;
}

.pagination-info {
  font-weight: 500;
}

.pagination-buttons {
  display: flex;
  align-items: center;
  gap: 4px;
}

.pagination-btn {
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 6px 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
}

.pagination-btn:hover:not(:disabled) {
  background: #f3f4f6;
  border-color: #9ca3af;
}

.pagination-btn:disabled {
  background: #f9fafb;
  border-color: #e5e7eb;
  color: #9ca3af;
  cursor: not-allowed;
}

.page-numbers {
  display: flex;
  gap: 2px;
  margin: 0 8px;
}

.page-number {
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 6px 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
}

.page-number:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
}

.page-number.active {
  background: #3b82f6;
  border-color: #3b82f6;
  color: white;
}

.per-page-selector label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
}

.per-page-selector select {
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 6px 8px;
  font-size: 14px;
  cursor: pointer;
}

.per-page-selector select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Knowledge Base Dropdown Styling */
.knowledge-base-select {
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 6px 8px;
  font-size: 13px;
  cursor: pointer;
  width: 100%;
  max-width: 100%;
  transition: border-color 0.2s ease;
}

.knowledge-base-select:hover:not(:disabled) {
  border-color: #9ca3af;
}

.knowledge-base-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.knowledge-base-select:disabled {
  background: #f9fafb;
  color: #9ca3af;
  cursor: not-allowed;
}

.kb-select-small {
  font-size: 12px;
  padding: 4px 6px;
}

.on-process-select {
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 4px 6px;
  font-size: 12px;
  cursor: pointer;
  width: 100%;
  max-width: 100%;
  transition: border-color 0.2s ease;
}

.on-process-select:hover {
  border-color: #9ca3af;
}

.on-process-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.kb-badge {
  display: inline-block;
  padding: 4px 8px;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 4px;
  font-size: 12px;
  color: #0369a1;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

/* Mobile responsive for pagination */
@media (max-width: 768px) {
  .pagination-controls {
    flex-direction: column;
    gap: 12px;
    padding: 12px 16px;
  }
  
  .pagination-buttons {
    order: 2;
  }
  
  .pagination-info {
    order: 1;
    text-align: center;
  }
  
  .per-page-selector {
    order: 3;
  }
}

.site-row:hover, .page-row:hover {
  background: #f8fafc;
}

/* ==================== NEW ADD SITES SECTION ==================== */

.add-sites-section {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.section-header {
  margin-bottom: 24px;
}

.section-header h3 {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 8px 0;
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
}

.section-header h3 i {
  color: #3b82f6;
}

.section-description {
  margin: 0;
  color: #6b7280;
  font-size: 14px;
  line-height: 1.5;
}

/* Source Tabs */
.source-tabs {
  display: flex;
  gap: 2px;
  margin-bottom: 24px;
  background: #f3f4f6;
  padding: 4px;
  border-radius: 8px;
}

.tab-button {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #6b7280;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.tab-button:hover {
  background: #e5e7eb;
  color: #374151;
}

.tab-button.active {
  background: white;
  color: #3b82f6;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.tab-button i {
  font-size: 16px;
}

/* Input Tab Content */
.input-tab-content {
  margin-bottom: 24px;
}

.input-header {
  margin-bottom: 16px;
}

.input-header h4 {
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
}

.input-header p {
  margin: 0;
  color: #6b7280;
  font-size: 14px;
}

/* URL Input */
.url-input-container {
  display: flex;
  gap: 12px;
  align-items: flex-end;
}

.input-container {
  position: relative;
  flex: 1;
}

.url-input-field {
  width: 100% !important;
  padding: 12px !important;
  border: 2px solid #e5e7eb !important;
  border-radius: 8px !important;
  font-size: 15px !important;
  transition: all 0.2s ease !important;
  box-sizing: border-box !important;
  background: white !important;
}

.url-input-field:focus {
  outline: none !important;
  border-color: #3b82f6 !important;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
}

.add-site-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  min-width: 120px;
  justify-content: center;
}

.add-site-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}

.add-site-btn:disabled {
  background: #d1d5db;
  color: #9ca3af;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* File Upload */
.file-upload-container {
  margin-top: 16px;
}

.file-drop-zone {
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  padding: 32px;
  text-align: center;
  transition: all 0.2s ease;
  cursor: pointer;
}

.file-drop-zone:hover {
  border-color: #3b82f6;
  background: #f8fafc;
}

.file-input-hidden {
  display: none;
}

.file-drop-content i {
  font-size: 48px;
  color: #9ca3af;
  margin-bottom: 16px;
}

.drop-text {
  margin: 0 0 8px 0;
  color: #374151;
  font-size: 16px;
  font-weight: 500;
}

.browse-link {
  background: none;
  border: none;
  color: #3b82f6;
  text-decoration: underline;
  cursor: pointer;
  font-size: inherit;
}

.browse-link:hover {
  color: #2563eb;
}

.file-formats {
  margin: 0;
  color: #9ca3af;
  font-size: 14px;
}

/* Crawl Options */
.crawl-options-section {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
}

.options-header {
  margin-bottom: 16px;
}

.options-header h4 {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #374151;
}

.options-header i {
  color: #6b7280;
}

.options-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 20px;
  align-items: start;
}

.option-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.option-label {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin: 0;
}

.option-input {
  padding: 10px 12px !important;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s ease;
  box-sizing: border-box;
}

.option-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.checkbox-group {
  padding: 12px !important;
  background: #f9fafb !important;
  border-radius: 6px !important;
  border: 1px solid #e5e7eb !important;
  margin-bottom: 8px !important;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.checkbox-label {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  margin: 0;
  padding: 4px 0;
}

.option-checkbox {
  display: none;
}

.checkmark-custom {
  width: 18px;
  height: 18px;
  border: 2px solid #d1d5db;
  border-radius: 4px;
  position: relative;
  transition: all 0.2s ease;
  flex-shrink: 0;
  margin-top: 2px;
}

.option-checkbox:checked + .checkmark-custom {
  background: #3b82f6;
  border-color: #3b82f6;
}

.option-checkbox:checked + .checkmark-custom::after {
  content: '';
  position: absolute;
  left: 5px;
  top: 2px;
  width: 4px;
  height: 8px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.option-help {
  margin: 0 0 0 28px;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .add-sites-section {
    padding: 20px;
  }
  
  .source-tabs {
    flex-direction: column;
    gap: 2px;
  }
  
  .url-input-container {
    flex-direction: column;
    gap: 12px;
  }
  
  .add-site-btn {
    width: 100%;
  }
  
  .options-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .file-drop-zone {
    padding: 24px 16px;
  }
}

.col-monitor {
  text-align: center;
}

.col-number {
  text-align: center;
  font-weight: 600;
  color: #64748b;
}

.col-url {
  /* Width set by table-specific rules */
}

.site-link, .page-link {
  color: #3b82f6;
  text-decoration: none;
  font-weight: 500;
}

.site-link:hover, .page-link:hover {
  text-decoration: underline;
}

.col-site {
  color: #3b82f6;
  font-weight: 500;
}

.col-status {
  text-align: center;
}

.status-indicator {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
}

.status-indicator.active {
  color: #059669;
}

.status-indicator.inactive {
  color: #dc2626;
}

.status-indicator i {
  font-size: 8px;
}

.col-count {
  text-align: center;
  font-weight: 600;
}

.col-count.has-changes {
  color: #dc2626;
  font-weight: 700;
}

.loading-indicator {
  color: #3b82f6;
}

.error-indicator {
  color: #dc2626;
}

.col-time {
  font-size: 13px;
  color: #64748b;
  text-align: center;
}

.col-actions {
  text-align: center;
}

.action-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  padding: 4px;
}

.table-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 8px 14px;
  border: 1px solid #d1d5db;
  border-radius: 5px;
  background: white;
  color: #374151;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  min-height: 36px;
  white-space: nowrap;
}

.table-action-btn:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}

.table-action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.table-action-btn.generate-btn {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border-color: #10b981;
}

.table-action-btn.details-btn {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border-color: #3b82f6;
}

.table-action-btn.scan-btn {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  border-color: #f59e0b;
}

.table-action-btn.delete-btn {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  border-color: #ef4444;
}

.all-pages-section {
  margin-top: 32px;
  border-top: 1px solid #e2e8f0;
  padding-top: 32px;
}

.section-subheader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.section-subheader h4 {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0;
  color: #1e293b;
  font-size: 18px;
  font-weight: 600;
}

.section-subheader .header-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

.pages-filters {
  display: flex;
  gap: 16px;
  align-items: center;
}

.search-container {
  position: relative;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
}

.search-input {
  padding: 8px 12px 8px 36px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  min-width: 250px;
}

.filter-checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
}

.filter-checkbox input {
  margin: 0;
}

.page-bulk-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 32px;
  padding: 18px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.bulk-actions-label {
  font-weight: 500;
  color: #374151;
}

.bulk-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: white;
  color: #374151;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.bulk-action-btn:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}


.monitor-checkbox {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
}

.monitor-checkbox input {
  margin: 0;
  margin-right: 4px;
}

.col-size {
  text-align: center;
  color: #64748b;
  font-size: 13px;
}

.col-changed {
  font-size: 13px;
  text-align: center;
}

.changed-time {
  color: #dc2626;
  font-weight: 600;
}

.no-changes {
  color: #9ca3af;
}

.processed-time {
  color: #059669;
  font-weight: 600;
}

.not-processed {
  color: #9ca3af;
}

.changed-indicator {
  color: #dc2626;
  margin-right: 4px;
}

.redirect-indicator {
  color: #f59e0b;
  margin-right: 4px;
}

.page-actions {
  display: flex;
  gap: 4px;
  justify-content: center;
  align-items: center;
}

.page-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: white;
  color: #374151;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.page-action-btn:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}

.page-action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-action-btn.process-btn {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  color: white;
  border-color: #8b5cf6;
}

.page-action-btn.articles-btn {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border-color: #10b981;
}

.page-action-btn.view-btn {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border-color: #3b82f6;
}

.page-action-btn.scan-btn {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  border-color: #f59e0b;
}

.status-badge {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-align: center;
  min-width: 40px;
}

.status-badge.status-200 {
  background: #dcfce7;
  color: #166534;
}

.status-badge.status-404 {
  background: #fee2e2;
  color: #991b1b;
}

.status-badge.status-500 {
  background: #fef2f2;
  color: #991b1b;
}

.pages-pagination {
  padding: 12px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  font-size: 12px;
  color: #64748b;
  text-align: center;
}

.inventory-actions {
  display: flex;
  gap: 20px;
  justify-content: center;
  align-items: center;
  margin-top: 36px;
  padding: 28px 24px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
  border-radius: 8px;
  margin-bottom: 20px;
}

.loading-cell, .empty-cell {
  padding: 32px;
  text-align: center;
  color: #64748b;
}

.empty-state {
  text-align: center;
  padding: 64px 32px;
  color: #64748b;
}

.empty-icon {
  font-size: 48px;
  color: #cbd5e1;
  margin-bottom: 16px;
}

.empty-state h4 {
  margin: 0 0 8px 0;
  color: #374151;
  font-size: 18px;
  font-weight: 600;
}

.empty-state p {
  margin: 0;
  color: #64748b;
}

.loading-state {
  padding: 64px 32px;
  text-align: center;
}

.loading-state .spinner-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.loading-state .spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e2e8f0;
  border-top: 3px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-state p {
  margin: 0;
  color: #64748b;
  font-weight: 500;
}

/* Update the upper summary section to be more compact */
.inventory-summary {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;
}

.summary-stats {
  display: flex;
  gap: 20px;
  justify-content: space-around;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.stat-item .stat-label {
  font-size: 11px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
}

.stat-item .stat-value {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
}

.stat-item .stat-value.changed {
  color: #dc2626;
}

/* Initial Loading Spinner */
.initial-loading-overlay {
  position: relative;
  min-height: 60vh;
  background-color: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 12px;
  margin: 20px;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 5px solid #f3f4f6;
  border-top: 5px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-message {
  color: #64748b;
  font-size: 16px;
  font-weight: 500;
  margin: 0;
}

/* Progress Log Sidebar */
.progress-log-sidebar {
  position: fixed;
  top: 120px;
  right: 20px;
  width: 350px;
  max-height: 400px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  overflow: hidden;
}

.log-sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
}

.log-sidebar-header h5 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 8px;
}

.clear-log-btn {
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  transition: all 0.2s;
}

.clear-log-btn:hover {
  background: #e5e7eb;
  color: #374151;
}

.log-actions {
  padding: 12px 16px;
  border-top: 1px solid #e5e7eb;
  background: #f8fafc;
}

.view-articles-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.view-articles-btn:hover {
  background: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
}

.articles-action-sidebar {
  position: fixed;
  top: 120px;
  right: 20px;
  width: 350px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  z-index: 999;
  padding: 16px;
}

/* Stack below progress log when both are visible */
.progress-log-sidebar + .articles-action-sidebar {
  top: 540px;
}

.sidebar-log {
  max-height: 350px;
  overflow-y: auto;
  background: white;
}

.sidebar-log .log-entry {
  padding: 8px 16px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 12px;
}

.sidebar-log .log-entry:last-child {
  border-bottom: none;
}

/* Adjust main content when sidebar is visible */
#centerdiv.with-log-sidebar {
  padding-right: 390px;
}

/* Improved section organization */
.source-section {
  margin-bottom: 30px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.section-header h3 {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #1e293b;
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 8px;
}

.section-description {
  color: #64748b;
  font-size: 14px;
  margin-bottom: 24px;
}

.action-btn.primary {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  color: white;
  font-weight: 500;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3);
}

.action-btn.primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}

.action-btn.pulse {
  animation: subtle-pulse 2s infinite;
}

@keyframes subtle-pulse {
  0% {
    box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3);
  }
  50% {
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.6);
  }
  100% {
    box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3);
  }
}

.action-btn.success {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  color: white;
  font-weight: 500;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(16, 185, 129, 0.3);
}

.action-btn.success:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
}

.action-btn.secondary {
  background: white;
  border: 2px solid #e5e7eb;
  padding: 8px 18px;
  border-radius: 8px;
  color: #374151;
  font-weight: 500;
  transition: all 0.3s ease;
}

.action-btn.secondary:hover {
  border-color: #3b82f6;
  color: #3b82f6;
  background: #f0f9ff;
}

/* Better spacing for main sections */
.knowledge-inventory-section {
  /* Removed the blue border */
}

.add-sites-section {
  margin-top: 20px;
}

.monitored-sites-section {
  margin-top: 30px;
}

/* Improved responsive design */
@media (max-width: 768px) {
  .header-actions {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }
  
  .action-btn {
    width: 100%;
    justify-content: center;
  }
  
  .progress-log-sidebar {
    position: static;
    width: 100%;
    margin: 20px 0;
    max-height: 250px;
  }
  
  #centerdiv.with-log-sidebar {
    padding-right: 0;
  }
}

@media (max-width: 1400px) {
  .progress-log-sidebar {
    width: 300px;
  }
  
  #centerdiv.with-log-sidebar {
    padding-right: 320px;
  }
}

@media (max-width: 1200px) {
  .progress-log-sidebar {
    position: relative;
    top: auto;
    right: auto;
    width: 100%;
    margin: 20px 0;
    max-height: 200px;
  }
  
  #centerdiv.with-log-sidebar {
    padding-right: 0;
  }
}

/* File name styling */
.file-name {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #2563eb;
  font-weight: 500;
}

.file-name i {
  color: #64748b;
  font-size: 14px;
}

/* File type icons */
.file-type-pdf { color: #dc2626 !important; }
.file-type-docx, .file-type-doc { color: #2563eb !important; }
.file-type-json { color: #059669 !important; }
.file-type-html { color: #ea580c !important; }
.file-type-plain { color: #64748b !important; }
.file-type-markdown { color: #374151 !important; }
.file-type-generic { color: #64748b !important; }

/* Directory path styling */
.directory-path {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #7c3aed;
  font-weight: 500;
}

.directory-path i {
  color: #a855f7;
}
</style>